import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import moment from 'moment'; // Import moment
import { Table, Spin, Alert, Button, Card, Modal, Form, Input, Select, message, Row, Col, InputNumber, Typography, Space, Collapse, Popconfirm, Descriptions, Tag, Avatar, Tooltip, Progress, Radio } from 'antd'; // Removed DatePicker, Added Space, Collapse, Popconfirm, Descriptions, Input needed for Password, Tag, Avatar, Tooltip, Progress, Radio
import MomentDatePicker from './MomentDatePicker.tsx'; // Import the custom MomentDatePicker
import PhysicalExamModal from './PhysicalExamModal'; // Existing simple exam modal
import StructuredPhysicalExamFormFull from './StructuredPhysicalExamFormFull'; // Import the new comprehensive form
import { FileAddOutlined, EditOutlined, SolutionOutlined, DeleteOutlined, EyeOutlined, CopyOutlined, AudioOutlined, MedicineBoxOutlined, TeamOutlined, FileTextOutlined, TableOutlined, FolderOutlined, UpOutlined, DownOutlined } from '@ant-design/icons'; // Added SolutionOutlined for Intake, DeleteOutlined, EyeOutlined, CopyOutlined, AudioOutlined, MedicineBoxOutlined, TeamOutlined, FileTextOutlined, TableOutlined, FolderOutlined, UpOutlined, DownOutlined
import { useAuth } from '../context/AuthContext';
import { usePatientChart } from '../context/PatientChartContext';
import { useTranslation } from 'react-i18next';
// import PatientSummary from './PatientSummary'; // No longer needed here
import ReadOnlyIntakeSummary from './ReadOnlyIntakeSummary'; // Import the new summary component

import ClickableVitalsDisplay from './ClickableVitalsDisplay'; // Import clickable vitals display component
import VitalsSnapshotDisplay from './VitalsSnapshotDisplay'; // Import vitals snapshot display component
import AbnormalLabsDisplay from './AbnormalLabsDisplay'; // Import abnormal labs display component
import RichTextEditor from './RichTextEditor'; // Import the new editor
import RichTextDisplay from './RichTextDisplay'; // Import the rich text display component
import CollaborativeIdtField from './CollaborativeIdtField'; // Import collaborative field component
import useIdtCollaboration from '../hooks/useIdtCollaboration'; // Import collaboration hook
import PatientIntakeForm from './PatientIntakeForm'; // Import Intake Form
import UpdateVitalsModal from './UpdateVitalsModal'; // Import new update modal
import ProblemListManagementModal from './ProblemListManagementModal'; // Import Problem List Management Modal
import MedicationManagementModal from './MedicationManagementModal'; // Import Medication Management Modal
import CareTeamManagementModal from './CareTeamManagementModal'; // Import Care Team Management Modal
import OrderManagementModal from './OrderManagementModal'; // Import Order Management Modal
import PatientFlowsheet from './PatientFlowsheet'; // Import Patient Flowsheet component
import PatientDocumentsAndHealthLibrary from './PatientDocumentsAndHealthLibrary'; // Import Patient Documents component
import AIScribeModal from './AIScribeModal'; // Import AI Scribe Modal
import IdtConflictResolutionModal from './IdtConflictResolutionModal'; // Import IDT Conflict Resolution Modal
import { transformLabsForAbnormalDisplay } from '../utils/abnormalLabsUtils'; // Import abnormal labs utilities
import { getLabReferenceRange, getLabUnits } from '../utils/labReferenceRanges'; // Import reference ranges
import { getOrganSystemInfo, ORGAN_SYSTEM_COLORS } from '../utils/organSystemConstants'; // Import organ system utilities
import EnhancedSmartGoalDetailsModal from './EnhancedSmartGoalDetailsModal'; // Import SMART Goals details modal
import '../styles/NutritionStatusRadio.css'; // Import nutrition status radio button styles

const { Option } = Select;
const { Text } = Typography;
// const { TextArea } = Input; // No longer needed for notes

// Status Classification Options (used for Nutrition, Physical Activity, and Recovery)
const STATUS_CLASSIFICATION_OPTIONS = [
  { value: 'optimal', label: 'Optimal', color: '#52c41a' },
  { value: 'adequate', label: 'Adequate', color: '#1890ff' },
  { value: 'at_risk', label: 'At Risk', color: '#faad14' },
  { value: 'compromised', label: 'Compromised', color: '#f5222d' }
];

// Legacy alias for backward compatibility
const NUTRITION_STATUS_OPTIONS = STATUS_CLASSIFICATION_OPTIONS;

// Helper function to get proper order name based on order type and available fields
const getOrderDisplayName = (order) => {
  if (!order) return 'Unknown Order';

  const details = order.orderDetails || {};

  // Try different fields based on order type
  switch (order.orderType) {
    case 'Laboratory':
      return details.testName || 'Lab Order';
    case 'Radiology':
      return details.studyDescription || 'Imaging Study';
    case 'Lifestyle':
      return details.title || details.description || 'Lifestyle Order';
    case 'Referral':
      return `Referral to ${details.specialty || 'Specialist'}`;
    case 'Medication':
      return details.medicationName || 'Medication Order';
    case 'Procedure':
      return details.description || 'Procedure';
    default:
      return details.description || details.testName || details.title || `${order.orderType} Order`;
  }
};

// Helper function to format medications for IDT display
const formatMedicationsForIDT = (medications, isSnapshot = false) => {
  if (!medications || medications.length === 0) {
    return <Text type="secondary">None Recorded</Text>;
  }

  // Handle snapshot data structure (object with keys like medication_1, medication_2)
  const medicationsList = Array.isArray(medications)
    ? medications
    : Object.values(medications);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
      {isSnapshot && (
        <div style={{ marginBottom: '8px' }}>
          <Tag color="purple" size="small">
            📸 Snapshot Data (Captured at Signing)
          </Tag>
        </div>
      )}
      {medicationsList.map((med, index) => (
        <div key={index} style={{
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '6px',
          padding: '6px 0',
          borderBottom: index < medicationsList.length - 1 ? '1px solid #f0f0f0' : 'none'
        }}>
          <Text strong style={{ color: '#1890ff', fontSize: '14px' }}>
            {med.medicationName}
          </Text>
          {med.dosage && (
            <Tag color="blue" style={{ margin: 0, fontSize: '12px' }}>
              {typeof med.dosage === 'object' && med.dosage !== null
                ? `${med.dosage.value || ''} ${med.dosage.unit || ''}`
                : med.dosage}
            </Tag>
          )}
          {med.frequency && (
            <Tag color="green" style={{ margin: 0, fontSize: '12px' }}>
              {med.frequency}
            </Tag>
          )}
          {med.route && (
            <Tag color="orange" style={{ margin: 0, fontSize: '12px' }}>
              {med.route}
            </Tag>
          )}
          {med.isPrn && (
            <Tag color="red" style={{ margin: 0, fontSize: '12px' }}>
              PRN
            </Tag>
          )}
        </div>
      ))}
    </div>
  );
};

// Helper function to format problems for IDT display
const formatProblemsForIDT = (problems, isSnapshot = false) => {
  if (!problems || problems.length === 0) {
    return <Text type="secondary">None Recorded</Text>;
  }

  // Handle snapshot data structure (object with keys like problem_1, problem_2)
  const problemsList = Array.isArray(problems)
    ? problems
    : Object.values(problems);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
      {isSnapshot && (
        <div style={{ marginBottom: '8px' }}>
          <Tag color="purple" size="small">
            📸 Snapshot Data (Captured at Signing)
          </Tag>
        </div>
      )}
      {problemsList.map((problem, index) => (
        <div key={index} style={{
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '6px',
          padding: '6px 0',
          borderBottom: index < problemsList.length - 1 ? '1px solid #f0f0f0' : 'none'
        }}>
          <Text strong style={{ color: '#1890ff', fontSize: '14px' }}>
            {problem.problemName}
          </Text>
          <Tag color={problem.status === 'Active' ? 'green' : problem.status === 'Resolved' ? 'blue' : 'default'}
               style={{ margin: 0, fontSize: '12px' }}>
            {problem.status}
          </Tag>
          {problem.priority && (
            <Tag color={
              problem.priority === 'Critical' ? 'red' :
              problem.priority === 'High' ? 'orange' :
              problem.priority === 'Medium' ? 'gold' : 'default'
            } style={{ margin: 0, fontSize: '12px' }}>
              {problem.priority}
            </Tag>
          )}
          {problem.organSystems && problem.organSystems.length > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              {problem.organSystems.map(systemKey => {
                const systemInfo = getOrganSystemInfo(systemKey);
                if (!systemInfo) return null;

                return (
                  <Tooltip key={systemKey} title={`${systemInfo.name} - ${systemInfo.description}`}>
                    <Avatar
                      src={systemInfo.iconPath}
                      size={20}
                      style={{
                        backgroundColor: ORGAN_SYSTEM_COLORS[systemKey],
                        width: '20px',
                        height: '20px',
                        minWidth: '20px',
                        cursor: 'help',
                        border: '1px solid #f0f0f0'
                      }}
                    />
                  </Tooltip>
                );
              })}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

// Helper function to format abnormal labs snapshot for IDT display
const formatAbnormalLabsSnapshotForIDT = (abnormalLabsSnapshot) => {
  if (!abnormalLabsSnapshot || Object.keys(abnormalLabsSnapshot).length === 0) {
    return <Text type="secondary">No abnormal lab values in snapshot</Text>;
  }

  // Convert snapshot object to array
  const abnormalLabs = Object.values(abnormalLabsSnapshot);

  // Helper function to get lab display name
  const getLabDisplayName = (name) => {
    if (!name) return 'Unknown Lab';
    const cleanName = name.replace(/^labs_/, '');
    return cleanName
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  // Helper function to get flag color
  const getFlagColor = (flag) => {
    if (!flag) return 'default';
    const upperFlag = flag.toString().toUpperCase();
    switch (upperFlag) {
      case 'H':
      case 'HIGH':
        return 'red';
      case 'L':
      case 'LOW':
        return 'blue';
      case 'C':
      case 'CRITICAL':
        return 'magenta';
      case 'A':
      case 'ABNORMAL':
        return 'orange';
      default:
        return 'default';
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
      <div style={{ marginBottom: '8px' }}>
        <Tag color="purple" size="small">
          📸 Snapshot Data (Captured at Signing)
        </Tag>
      </div>
      {abnormalLabs.map((lab, index) => (
        <div key={index} style={{
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '6px',
          padding: '6px 0',
          borderBottom: index < abnormalLabs.length - 1 ? '1px solid #f0f0f0' : 'none'
        }}>
          <Text strong style={{ color: '#d4380d', fontSize: '14px' }}>
            {getLabDisplayName(lab.name)}
          </Text>
          <Tag color="volcano" style={{ margin: 0, fontSize: '12px' }}>
            {typeof lab.value === 'object' && lab.value !== null
              ? `${lab.value.value || ''} ${lab.value.unit || lab.units || ''}`
              : `${lab.value} ${lab.units || ''}`}
          </Tag>
          {lab.flag && (
            <Tag color={getFlagColor(lab.flag)} style={{ margin: 0, fontSize: '12px' }}>
              {lab.flag.toString().toUpperCase()}
            </Tag>
          )}
          {lab.referenceRange && (
            <Text type="secondary" style={{ fontSize: '12px' }}>
              (Normal: {lab.referenceRange})
            </Text>
          )}
          {/* Display lab collection date */}
          <div style={{ marginTop: '4px' }}>
            <Text type="secondary" style={{ fontSize: '11px' }}>
              Most Recent: {lab.entryDate ? moment(lab.entryDate).format('M/D/YYYY') : 'No date'}
            </Text>
          </div>
        </div>
      ))}
    </div>
  );
};

// Helper function to format medical history for IDT display
const formatMedicalHistoryForIDT = (patientData) => {
  if (!patientData) {
    return 'None Recorded';
  }

  const freeTextHistory = patientData.medicalHistory?.length > 0
    ? patientData.medicalHistory.map(h => h.condition)
    : [];

  const structured = patientData.structuredMedicalHistory;
  let structuredConditions = [];

  if (structured) {
    structuredConditions = Object.entries(structured)
      .filter(([key, value]) =>
        key !== '_id' &&
        key !== 'lastUpdatedAt' &&
        key !== 'lastUpdatedBy' &&
        Array.isArray(value) &&
        value.length > 0
      )
      .flatMap(([key, value]) => value);
  }

  // Combine all conditions into a single clean list
  const allConditions = [];

  if (freeTextHistory.length > 0) {
    allConditions.push(...freeTextHistory);
  }

  if (structuredConditions.length > 0) {
    allConditions.push(...structuredConditions);
  }

  // Smart duplicate removal: keep more specific entries (with years) over generic ones
  const processedConditions = allConditions
    .filter(c => c && c.trim())
    .map(c => c.trim());

  // Create a map to track conditions and their specificity
  const conditionMap = new Map();

  processedConditions.forEach(condition => {
    // Extract base condition name (remove year/date info for comparison)
    const baseCondition = condition.replace(/\s*\(\d{4}\).*$/, '').trim().toLowerCase();

    // If we haven't seen this base condition, or if current one has more detail (year), keep it
    if (!conditionMap.has(baseCondition) ||
        (condition.includes('(') && !conditionMap.get(baseCondition).includes('('))) {
      conditionMap.set(baseCondition, condition);
    }
  });

  // Get the final unique conditions, preserving the more detailed versions
  const uniqueConditions = Array.from(conditionMap.values());

  // Display each condition on a separate line instead of comma-separated
  if (uniqueConditions.length > 0) {
    return (
      <div style={{ lineHeight: '1.6' }}>
        {uniqueConditions.map((condition, index) => (
          <div key={index} style={{ marginBottom: '4px' }}>
            • {condition}
          </div>
        ))}
      </div>
    );
  }

  return 'None Recorded';
};

// Helper function to format status classification (nutrition, physical activity, recovery)
const formatStatusClassification = (classification, fieldName = 'Status') => {
  if (!classification) return null;

  const option = STATUS_CLASSIFICATION_OPTIONS.find(opt => opt.value === classification);
  if (!option) return null;

  return (
    <Tag color={option.color} style={{ fontSize: '14px', fontWeight: 'bold', marginBottom: '8px' }}>
      {option.label}
    </Tag>
  );
};

// Legacy helper functions for backward compatibility
const formatNutritionStatusClassification = (classification) => formatStatusClassification(classification, 'Nutrition');
const formatPhysicalActivityStatusClassification = (classification) => formatStatusClassification(classification, 'Physical Activity');
const formatRecoveryStatusClassification = (classification) => formatStatusClassification(classification, 'Recovery');
const formatEmotionStatusClassification = (classification) => formatStatusClassification(classification, 'Emotion');
const formatEnvironmentalStatusClassification = (classification) => formatStatusClassification(classification, 'Environmental');

// Helper function to format SMART Goals for IDT display
const formatSmartGoalsForIDT = (smartGoals, onGoalClick) => {
  if (!smartGoals || smartGoals.length === 0) {
    return <Text type="secondary">No SMART Goals recorded</Text>;
  }

  // Utility function to decode HTML entities
  const decodeHtmlEntities = (text) => {
    if (typeof text !== 'string') return text;

    const textarea = document.createElement('textarea');
    textarea.innerHTML = text;
    return textarea.value;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Completed': return 'green';
      case 'In Progress': return 'blue';
      case 'On Hold': return 'orange';
      case 'Cancelled': return 'red';
      case 'Not Started': return 'default';
      default: return 'default';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'Critical': return 'red';
      case 'High': return 'orange';
      case 'Medium': return 'gold';
      case 'Low': return 'green';
      default: return 'default';
    }
  };

  // Helper function to extract goal title from various possible fields
  const getGoalTitle = (goal) => {
    // Try multiple possible field names based on the data model evolution
    // Based on existing components, mdSmartGoalOrder is the primary field
    const rawTitle = goal.mdSmartGoalOrder ||
                     goal.mdGoal ||
                     goal.specific?.objective ||
                     goal.specific?.description ||
                     goal.smartGoal ||
                     goal.objective ||
                     goal.title ||
                     (goal.problem?.problemName ? `Goal for ${goal.problem.problemName}` : 'Untitled Goal');

    // Decode HTML entities to display characters properly
    return decodeHtmlEntities(rawTitle);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
      {smartGoals.map((goal, index) => (
        <div key={goal._id || index} style={{
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '6px',
          padding: '8px',
          backgroundColor: '#fafafa',
          border: '1px solid #f0f0f0',
          borderRadius: '4px'
        }}>
          <Text
            strong
            style={{
              color: '#1890ff',
              fontSize: '14px',
              flex: 1,
              minWidth: '200px',
              cursor: onGoalClick ? 'pointer' : 'default'
            }}
            onClick={() => onGoalClick && onGoalClick(goal)}
          >
            {getGoalTitle(goal)}
          </Text>
          <Tag color={getStatusColor(goal.status)} style={{ margin: 0, fontSize: '12px' }}>
            {goal.status || 'Not Started'}
          </Tag>
          {goal.overallCompletionRate !== undefined && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px', minWidth: '80px' }}>
              <Progress
                percent={Math.round(goal.overallCompletionRate)}
                size="small"
                style={{ width: '60px' }}
                strokeColor={goal.overallCompletionRate === 100 ? '#52c41a' : '#1890ff'}
              />
              <Text style={{ fontSize: '12px', color: '#666' }}>
                {Math.round(goal.overallCompletionRate)}%
              </Text>
            </div>
          )}
          {goal.relevant?.clinicalPriority && (
            <Tag color={getPriorityColor(goal.relevant.clinicalPriority)} style={{ margin: 0, fontSize: '12px' }}>
              {goal.relevant.clinicalPriority}
            </Tag>
          )}
          {(goal.timeBound?.targetDate || goal.targetDate) && (
            <Text style={{ fontSize: '12px', color: '#666' }}>
              Due: {moment(goal.timeBound?.targetDate || goal.targetDate).format('MM/DD/YYYY')}
            </Text>
          )}
        </div>
      ))}
    </div>
  );
};

// Helper function to clean vitals object and extract nested values
const cleanVitalsObject = (vitals) => {
  if (!vitals) return {};

  const cleaned = {};
  Object.keys(vitals).forEach(key => {
    const value = vitals[key];
    if (typeof value === 'object' && value !== null && 'value' in value) {
      // Extract the actual value from nested object
      cleaned[key] = value.value;
    } else {
      cleaned[key] = value;
    }
  });
  return cleaned;
};

// Helper function to transform API vitals data to ClickableVitalsDisplay format
const transformVitalsData = (apiVitalsData) => {
  if (!apiVitalsData || !apiVitalsData.vitals) {
    return null;
  }

  const vitals = apiVitalsData.vitals;
  const transformedVitals = {};
  let mostRecentTimestamp = null;

  // Mapping from API keys to display keys
  const vitalKeyMapping = {
    'vitals_sbp': 'bloodPressureSystolic',
    'vitals_dbp': 'bloodPressureDiastolic',
    'vitals_rhr': 'heartRate',
    'vitals_rr': 'respiratoryRate',
    'vitals_temp_c': 'temperature',
    'vitals_spo2': 'oxygenSaturation',
    'vitals_pain': 'pain',
    'vitals_weight': 'weight',
    'vitals_height': 'height'
  };

  // Transform each vital sign
  for (const [apiKey, displayKey] of Object.entries(vitalKeyMapping)) {
    if (vitals[apiKey] && vitals[apiKey].mostRecent) {
      const mostRecent = vitals[apiKey].mostRecent;
      // Handle case where value might be an object with {value, unit} structure
      let actualValue = mostRecent.value;
      if (typeof actualValue === 'object' && actualValue !== null && 'value' in actualValue) {
        actualValue = actualValue.value;
      }
      transformedVitals[displayKey] = actualValue;

      // Track the most recent timestamp
      if (!mostRecentTimestamp || new Date(mostRecent.timestamp) > new Date(mostRecentTimestamp)) {
        mostRecentTimestamp = mostRecent.timestamp;
      }
    }
  }

  // Only return data if we have at least one vital sign
  if (Object.keys(transformedVitals).length === 0) {
    return null;
  }

  return {
    source: 'Latest Vitals',
    date: mostRecentTimestamp,
    vitals: transformedVitals
  };
};

const PatientEncounters = ({ patientId }) => {
  const [encounters, setEncounters] = useState([]);
  const [loading, setLoading] = useState(true); // For table loading
  const [addSubmitLoading, setAddSubmitLoading] = useState(false); // Loading for add submit
  const [editSubmitLoading, setEditSubmitLoading] = useState(false); // Loading for edit submit
  const [error, setError] = useState(null); // General error for fetch
  const [addModalVisible, setAddModalVisible] = useState(false); // State for add modal
  const [editModalVisible, setEditModalVisible] = useState(false); // State for edit modal
  const [editingEncounter, setEditingEncounter] = useState(null); // State to hold encounter being edited
  const [selectedEncounterType, setSelectedEncounterType] = useState(null); // State for selected type in Add modal
  const [addForm] = Form.useForm(); // Form instance for adding
  const [editForm] = Form.useForm(); // Form instance for editing
  const [isIntakeModalVisible, setIsIntakeModalVisible] = useState(false); // State for Intake modal
  const [showPhysicalExamModal, setShowPhysicalExamModal] = useState(false); // State for SIMPLE Physical Exam modal
  const [isFullExamModalVisible, setIsFullExamModalVisible] = useState(false); // State for FULL Physical Exam modal
  const [fullExamSubmitLoading, setFullExamSubmitLoading] = useState(false); // Loading state for full exam save
  const [latestVitalsData, setLatestVitalsData] = useState(null); // State for latest vitals display
  const [isUpdateVitalsModalVisible, setIsUpdateVitalsModalVisible] = useState(false); // State for update vitals modal
  const [stagedVitals, setStagedVitals] = useState(null); // State to hold vitals updated via modal before main save
  const { user } = useAuth(); // Get user info for provider field
  const { t } = useTranslation();
  const { chartAction, clearChartAction } = usePatientChart(); // Get chart action from context

  // --- State for IDT Note Data ---
  const [idtPatientData, setIdtPatientData] = useState(null);
  const [idtAllergies, setIdtAllergies] = useState([]);
  const [idtMedications, setIdtMedications] = useState([]);
  const [idtProblems, setIdtProblems] = useState([]); // For Problem List
  const [idtOrders, setIdtOrders] = useState([]); // For Upcoming Diagnostics
  const [idtAbnormalLabs, setIdtAbnormalLabs] = useState([]); // For Abnormal Labs
  const [idtAllLabEntries, setIdtAllLabEntries] = useState([]); // State for all lab entries for history modal
  const [idtAllVitalsEntries, setIdtAllVitalsEntries] = useState([]); // State for all vitals entries for history modal
  const [idtLoading, setIdtLoading] = useState(false); // Loading state for IDT data
  const [idtSmartGoals, setIdtSmartGoals] = useState([]); // State for SMART Goals display in IDT

  // --- State for IDT Status Classifications ---
  const [idtNutritionStatusClassification, setIdtNutritionStatusClassification] = useState('');
  const [idtPhysicalActivityStatusClassification, setIdtPhysicalActivityStatusClassification] = useState('');
  const [idtRecoveryStatusClassification, setIdtRecoveryStatusClassification] = useState('');
  const [idtEmotionStatusClassification, setIdtEmotionStatusClassification] = useState('');
  const [idtEnvironmentalStatusClassification, setIdtEnvironmentalStatusClassification] = useState('');

  // --- State for SMART Goals Details Modal ---
  const [isSmartGoalDetailsModalVisible, setIsSmartGoalDetailsModalVisible] = useState(false);
  const [selectedSmartGoal, setSelectedSmartGoal] = useState(null);

  // --- State for Strike Out Modal ---
  const [isStrikeModalVisible, setIsStrikeModalVisible] = useState(false);
  const [strikingEncounterId, setStrikingEncounterId] = useState(null);
  const [strikeForm] = Form.useForm();
  const [strikeSubmitLoading, setStrikeSubmitLoading] = useState(false);

  // --- State for Password Confirmation Modal ---
  const [isPasswordModalVisible, setIsPasswordModalVisible] = useState(false);
  const [passwordModalLoading, setPasswordModalLoading] = useState(false);
  const [signingEncounterId, setSigningEncounterId] = useState(null); // Store ID of encounter being signed
  const [passwordForm] = Form.useForm(); // Form instance for password modal
  const [passwordError, setPasswordError] = useState(''); // State for password error message

  // --- Addendum Modal Logic ---
  const [isAddendumModalVisible, setIsAddendumModalVisible] = useState(false);
  const [addendumEncounter, setAddendumEncounter] = useState(null);
  const [addendumForm] = Form.useForm();
  const [addendumSubmitLoading, setAddendumSubmitLoading] = useState(false);
  const [isAddendumPasswordModalVisible, setIsAddendumPasswordModalVisible] = useState(false); // State for addendum password modal
  const [tempAddendumContent, setTempAddendumContent] = useState(''); // State to hold temporary addendum text
  const [addendumPasswordForm] = Form.useForm(); // Form instance for addendum password modal
  const [addendumPasswordError, setAddendumPasswordError] = useState(''); // State for password error message

  // --- State for Problem List Management Modal ---
  const [isProblemListModalVisible, setIsProblemListModalVisible] = useState(false);

  // --- State for Medication Management Modal ---
  const [isMedicationModalVisible, setIsMedicationModalVisible] = useState(false);

  // --- State for Care Team Management Modal ---
  const [isCareTeamModalVisible, setIsCareTeamModalVisible] = useState(false);

  // --- State for Order Management Modal ---
  const [isOrderModalVisible, setIsOrderModalVisible] = useState(false);

  // --- State for Flowsheet Modal ---
  const [isFlowsheetModalVisible, setIsFlowsheetModalVisible] = useState(false);

  // --- State for Documents Modal ---
  const [isDocumentsModalVisible, setIsDocumentsModalVisible] = useState(false);

  // --- State for Encounters Modal ---
  const [isEncountersModalVisible, setIsEncountersModalVisible] = useState(false);

  // --- State for AI Scribe Modal ---
  const [isAIScribeModalVisible, setIsAIScribeModalVisible] = useState(false);

  // --- IDT Notes state ---
  const [currentIdtEncounterId, setCurrentIdtEncounterId] = useState(null); // Store current IDT encounter ID

  // --- Conflict resolution state for IDT Notes ---
  const [conflictModalVisible, setConflictModalVisible] = useState(false);
  const [conflictData, setConflictData] = useState(null);
  const [currentEncounterVersion, setCurrentEncounterVersion] = useState(1);
  const [pendingSaveData, setPendingSaveData] = useState(null);

  // --- Real-time collaboration for IDT Notes ---
  const handleCollaborativeFieldUpdate = useCallback((fieldName, value, updatedBy, timestamp) => {
    console.log('🔄 Received collaborative update:', { fieldName, updatedBy: updatedBy.name, timestamp });

    // Update the appropriate form based on which modal is open
    if (addModalVisible && selectedEncounterType === 'IDT Note') {
      addForm.setFieldsValue({ [fieldName]: value });
    } else if (editModalVisible && editingEncounter?.encounterType === 'IDT Note') {
      editForm.setFieldsValue({ [fieldName]: value });
    }

    // Add visual feedback for the update
    const fieldElement = document.querySelector(`[data-field-name="${fieldName}"]`);
    if (fieldElement) {
      fieldElement.classList.add('collaborative-field-updated');
      setTimeout(() => {
        fieldElement.classList.remove('collaborative-field-updated');
      }, 2000);
    }

    // Refresh encounters list to show updated content in the background
    // This ensures the encounter list reflects real-time changes from other users
    setTimeout(() => {
      console.log('🔄 Refreshing encounters list due to collaborative update');
      fetchEncounters();
    }, 2000); // Wait 2 seconds after collaborative update
  }, [addModalVisible, selectedEncounterType, editModalVisible, editingEncounter, addForm, editForm]);

  // Initialize collaboration for the current IDT encounter
  const collaboration = useIdtCollaboration(
    (selectedEncounterType === 'IDT Note' || editingEncounter?.encounterType === 'IDT Note') ? currentIdtEncounterId : null,
    handleCollaborativeFieldUpdate
  );
  const [aiScribeLoading, setAIScribeLoading] = useState(false);

  // --- State for Snapshots Display ---
  const [medicationsSnapshotData, setMedicationsSnapshotData] = useState(null);
  const [problemListSnapshotData, setProblemListSnapshotData] = useState(null);
  const [abnormalLabsSnapshotData, setAbnormalLabsSnapshotData] = useState(null);

  // --- Permissions ---
  const intakeAllowedRoles = ['Customer Service', 'Nursing', 'MD', 'Admin'];
  const canShowIntakeButton = user && intakeAllowedRoles.includes(user.role);

  // Helper function to combine assessment and plan for MD Encounters
  const combineAssessmentAndPlan = (assessment, plan) => {
    if (!assessment && !plan) return '';
    if (!assessment) return plan;
    if (!plan) return assessment;

    // If both exist, combine them with a separator
    return `${assessment}<br/><br/><strong>Plan:</strong><br/>${plan}`;
  };

  // Use useCallback to memoize fetchEncounters function
  const fetchEncounters = useCallback(async () => {
    if (!patientId) {
        setLoading(false); // Ensure loading stops if no ID
        return;
    }
    setLoading(true);
    setError(null);
      try {
        // Use the nested route to get encounters for this specific patient
        const response = await axios.get(`/patients/${patientId}/encounters`); // Removed prefix
        // Backend now populates provider, signedBy, and strickenBy details
        setEncounters(response.data.data.encounters);
      } catch (err) {
        console.error(`Error fetching encounters for patient ${patientId}:`, err);
        setError(err.response?.data?.message || 'Failed to fetch encounters.');
      } finally {
        setLoading(false);
      }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId]); // Dependency is patientId

  // Function to fetch SMART Goals for IDT display
  const fetchSmartGoalsForIDT = useCallback(async () => {
    if (!patientId) return;

    try {
      const response = await axios.get(`/patients/${patientId}/smart-goals`);
      const smartGoals = response.data.data.smartGoals || [];

      // Filter for active goals and sort by creation date
      const activeGoals = smartGoals
        .filter(goal => goal.status !== 'Cancelled')
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

      setIdtSmartGoals(activeGoals);
    } catch (err) {
      console.error('Error fetching SMART Goals for IDT:', err);
      setIdtSmartGoals([]); // Set empty array on error
    }
  }, [patientId]);

  // Handler for when a SMART Goal is clicked in IDT display
  const handleSmartGoalClick = useCallback((goal) => {
    console.log('SMART Goal clicked:', goal);
    setSelectedSmartGoal(goal);
    setIsSmartGoalDetailsModalVisible(true);
  }, []);

  // Handler for updating SMART Goals after modal changes
  const handleSmartGoalUpdate = useCallback(() => {
    fetchSmartGoalsForIDT(); // Refresh the SMART Goals data
  }, [fetchSmartGoalsForIDT]);

  // useEffect to call fetchEncounters on mount and when patientId changes
  useEffect(() => {
    fetchEncounters();
  }, [fetchEncounters]); // fetchEncounters is memoized by useCallback

  // useEffect to handle chart actions (e.g., from Weekly IDT List)
  useEffect(() => {
    if (chartAction && chartAction.patientId === patientId) {
      console.log('🎯 PatientEncounters: Handling chart action:', chartAction);
      const handleChartAction = async () => {
        try {
          if (chartAction.type === 'EDIT_IDT_NOTE') {
            // Open existing IDT Note for editing
            const existingEncounter = chartAction.encounter;
            if (existingEncounter) {
              showEditModal(existingEncounter);
              message.success('Opening existing IDT Note for editing.');
            }
            clearChartAction();
          } else if (chartAction.type === 'VIEW_IDT_NOTE') {
            // Open existing IDT Note for viewing
            const existingEncounter = chartAction.encounter;
            if (existingEncounter) {
              showViewModal(existingEncounter);
              message.success('Opening existing IDT Note for viewing.');
            }
            clearChartAction();
          }
        } catch (error) {
          console.error('Error handling chart action:', error);
          message.error('Failed to perform the requested action.');
          clearChartAction();
        }
      };

      handleChartAction();
    }
  }, [chartAction, patientId, clearChartAction, addForm]);



  // --- Add Modal Logic ---
  // VITALS ASSOCIATION BEHAVIOR:
  // - New encounters: Pull from intake vitals first, fallback to most recent vitals
  // - Editing unsigned encounters: Use encounter's saved vitals if available, otherwise intake/latest vitals
  // - Editing signed encounters: ALWAYS use encounter's locked vitals, no updates allowed
  // - Once signed: Vitals become permanently associated with the encounter
  const showAddModal = async () => { // Make async
    addForm.resetFields();
    // Set default encounter date to current time after reset
    addForm.setFieldsValue({
      encounterDate: moment()
    });
    setSelectedEncounterType(null);
    setError(null); // Clear general error if shown
    setAddModalVisible(true);
    setLatestVitalsData(null); // Reset latest vitals on modal open
    setStagedVitals(null); // Reset staged vitals

    // Fetch vitals for display - prioritize intake vitals, fallback to latest vitals
    try {
      setAddSubmitLoading(true); // Use loading state

      // First, fetch recent intake to check for intake vitals
      const intakeResponse = await axios.get(`/patients/${patientId}/intakes`);
      const recentIntake = intakeResponse.data.data.intakeRecord;

      let vitalsToDisplay = null;

      // Check if intake has vitals data
      if (recentIntake && recentIntake.vitals && Object.keys(recentIntake.vitals).length > 0) {
        console.log("Using vitals from intake:", recentIntake.vitals);
        vitalsToDisplay = {
          source: 'Recent Intake',
          date: recentIntake.createdAt || recentIntake.updatedAt,
          vitals: recentIntake.vitals
        };
      } else {
        // Fallback to latest vitals trend if no intake vitals
        console.log("No intake vitals found, fetching latest vitals trend");
        console.log("Recent intake data:", recentIntake);
        const vitalsResponse = await axios.get(`/patients/${patientId}/latest-vitals-trend`);
        console.log("Latest vitals response:", vitalsResponse.data);

        // Transform the API response to the expected format
        vitalsToDisplay = transformVitalsData(vitalsResponse.data.data);
        console.log("Transformed vitals to display:", vitalsToDisplay);

        if (!vitalsToDisplay) {
          console.log("No vitals data available from any source");
        }
      }

      setLatestVitalsData(vitalsToDisplay);

      // Set form values from intake
      if (recentIntake) {
         addForm.setFieldsValue({
           reasonForVisit: recentIntake.reasonForVisit,
           intakeHistoryUpdates: recentIntake.historyUpdates,
         });
      }

    } catch (err) {
      console.error("Error fetching initial data for encounter modal:", err);
      message.error('Failed to load vitals or intake data.');
    } finally {
       setAddSubmitLoading(false);
    }
  };

   const handleEncounterTypeChange = async (value) => { // Make async
    setSelectedEncounterType(value);
    // Reset note fields when type changes, but keep pre-filled intake updates
    const currentIntakeUpdates = addForm.getFieldValue('intakeHistoryUpdates');
    let initialObjective = ''; // Always start objective empty
    // Removed template insertion for Initial Visit
    addForm.setFieldsValue({
        subjective: '',
        objective: initialObjective, // Objective starts empty
        assessment: '', plan: '',
        hpiRosCombined: '', // Use new combined field name, clear reviewOfSystems
        intakeHistoryUpdates: currentIntakeUpdates // Preserve pre-filled value
    });

    // If IDT Note is selected, fetch necessary data
    if (value === 'IDT Note') {
      setIdtLoading(true);
      try {
        // Fetch patient, allergies, meds, problems, orders, vitals, flowsheet data, encounters for vitals history, and SMART Goals
        const [patientRes, allergiesRes, medsRes, problemsRes, ordersRes, vitalsRes, flowsheetRes, encountersRes, smartGoalsRes] = await Promise.all([
          axios.get(`/patients/${patientId}?populate=careTeam`),
          axios.get(`/patients/${patientId}/allergies`),
          axios.get(`/patients/${patientId}/medications?status=Active`),
          axios.get(`/patients/${patientId}/problems?status=Active`),
          axios.get(`/patients/${patientId}/orders?status=Active&status=Pending`),
          axios.get(`/patients/${patientId}/latest-vitals-trend`),
          axios.get(`/patients/${patientId}/flowsheet?limit=500`),
          axios.get(`/patients/${patientId}/encounters?limit=100`), // Get encounters for vitals history
          axios.get(`/patients/${patientId}/smart-goals`) // Get SMART Goals
        ]);
        setIdtPatientData(patientRes.data.data.patient);
        setIdtAllergies(allergiesRes.data.data.allergies || []);
        setIdtMedications(medsRes.data.data.medications || []);
        setIdtProblems(problemsRes.data.data.problems || []);
        // Filter orders to show only Active/Pending Laboratory and Radiology orders for diagnostics
        const filteredOrders = (ordersRes.data.data.orders || []).filter(order =>
          (order.status === 'Active' || order.status === 'Pending') &&
          (order.orderType === 'Laboratory' || order.orderType === 'Radiology')
        );
        setIdtOrders(filteredOrders);

        // Process SMART Goals data
        const smartGoals = smartGoalsRes.data.data.smartGoals || [];
        const activeGoals = smartGoals
          .filter(goal => goal.status !== 'Cancelled')
          .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        setIdtSmartGoals(activeGoals);

        // Transform and set vitals data for IDT Note display
        const vitalsToDisplay = transformVitalsData(vitalsRes.data.data);
        if (vitalsToDisplay) {
          setLatestVitalsData({
            ...vitalsToDisplay,
            source: 'Most Recent Vitals'
          });
        } else {
          setLatestVitalsData(null);
        }

        // Transform and set abnormal labs data for IDT Note display
        // Use the labs object but get actual entry dates from raw flowsheet entries
        const labsData = flowsheetRes.data.data.labs || {};

        // Create a map of lab names to their most recent entry dates from raw flowsheet entries
        const labDateMap = {};
        if (flowsheetRes.data.data.flowsheetEntries) {
          flowsheetRes.data.data.flowsheetEntries
            .filter(entry => entry.type === 'Lab' || entry.type === 'LabResult')
            .forEach(entry => {
              if (entry.name && entry.entryDate) {
                // Keep the most recent date for each lab parameter
                if (!labDateMap[entry.name] || new Date(entry.entryDate) > new Date(labDateMap[entry.name])) {
                  labDateMap[entry.name] = entry.entryDate;
                }
              }
            });
        }

        const flowsheetEntries = Object.entries(labsData).map(([paramName, value]) => ({
          name: paramName,
          value: value,
          units: getLabUnits(paramName),
          referenceRange: getLabReferenceRange(paramName),
          type: 'Lab',
          category: 'Lab Results',
          entryDate: labDateMap[paramName] || new Date(), // Use actual entry date or fallback to current date
          source: 'Most Recent Flowsheet'
        }));

        const abnormalLabsData = transformLabsForAbnormalDisplay(flowsheetEntries, 30);
        setIdtAbnormalLabs(abnormalLabsData);

        // Store all lab entries for history modal
        const allLabEntries = flowsheetEntries.filter(entry =>
          entry.type === 'Lab' ||
          (entry.name && entry.name.startsWith('labs_')) ||
          entry.category === 'Lab Results'
        );
        setIdtAllLabEntries(allLabEntries);

        // Store all vitals entries for history modal (from encounters)
        const allVitalsEntries = encountersRes.data.data.encounters
          .filter(encounter => encounter.vitals && Object.keys(encounter.vitals).length > 0)
          .map(encounter => ({
            _id: encounter._id,
            entryDate: encounter.encounterDate,
            vitals: encounter.vitals,
            source: encounter.encounterType || 'Encounter'
          }));
        setIdtAllVitalsEntries(allVitalsEntries);

        console.log("IDT Data Fetched:", { // Updated log message
            patient: patientRes.data.data.patient,
            allergies: allergiesRes.data.data.allergies,
            medications: medsRes.data.data.medications,
            problems: problemsRes.data.data.problems,
            orders: ordersRes.data.data.orders,
            vitals: vitalsToDisplay,
            abnormalLabs: abnormalLabsData
        });
        console.log("Care team data:", patientRes.data.data.patient.careTeam);
      } catch (err) {
        console.error("Error fetching data for IDT Note:", err);
        message.error('Failed to load required data for IDT Note.');
        // Optionally reset type or show error state
      } finally {
        setIdtLoading(false);
      }
    } else {
      // Clear IDT data if another type is selected
      setIdtPatientData(null);
      setIdtAllergies([]);
      setIdtMedications([]);
      setIdtOrders([]);
      setIdtAbnormalLabs([]);
      setIdtAllLabEntries([]);
    }
  };

  const handleAddCancel = () => {
    // Check if we're closing an IDT Note that may have been auto-saved
    const wasIdtNote = selectedEncounterType === 'IDT Note';
    const hadEncounterId = currentIdtEncounterId !== null;

    setAddModalVisible(false);
    setStagedVitals(null); // Reset staged vitals on cancel

    // Reset IDT state
    setCurrentIdtEncounterId(null);

    // Refresh encounters list if IDT Note was auto-saved
    if (wasIdtNote && hadEncounterId) {
      console.log('🔄 Refreshing encounters list after IDT Note auto-save');
      fetchEncounters();
    }
  };

  // Function to handle Save for Later without validation
  const handleSaveForLater = async () => {
    if (!user || !user._id) {
      message.error('User not logged in or user ID missing.');
      return;
    }

    try {
      setAddSubmitLoading(true);

      // Get current form values without validation
      const formValues = addForm.getFieldsValue();
      
      // Build minimum required payload for draft
      const draftPayload = {
        encounterType: formValues.encounterType, // Keep empty if not selected
        reasonForVisit: formValues.reasonForVisit || '',
        encounterDate: formValues.encounterDate ? formValues.encounterDate.toISOString() : new Date().toISOString(),
        vitals: (() => {
          const formVitals = formValues.vitals || {};
          const finalVitals = { ...formVitals };
          if (stagedVitals) {
            // Merge staged vitals
            for (const key in stagedVitals) {
              if (Object.hasOwnProperty.call(stagedVitals, key)) {
                let stagedValue = stagedVitals[key];
                if (['temperature', 'weight', 'height'].includes(key) && typeof stagedValue === 'object' && stagedValue !== null && stagedValue.hasOwnProperty('value')) {
                  stagedValue = stagedValue.value;
                }
                if (typeof stagedValue === 'number' || stagedValue === null || stagedValue === undefined) {
                  finalVitals[key] = stagedValue;
                }
              }
            }
          }
          // Clean the vitals object
          for (const key in finalVitals) {
            if (Object.hasOwnProperty.call(finalVitals, key)) {
              const value = finalVitals[key];
              if ((typeof value === 'object' && value !== null) || value === '') {
                finalVitals[key] = null;
              }
            }
          }
          return finalVitals;
        })(),
        patient: patientId,
        provider: user._id,
        notes: {
          subjective: formValues.subjective || '',
          objective: formValues.objective || '', 
          assessment: formValues.assessment || '',
          plan: formValues.plan || '',
          intakeHistoryUpdates: formValues.intakeHistoryUpdates || '',
          hpiRosCombined: formValues.hpiRosCombined || ''
        },
        // Add SBAR fields if needed
        ...(formValues.encounterType === 'Nurse SBAR Summary' && {
          sbarSituation: formValues.sbarSituation || '',
          sbarAssessmentOutcome: formValues.sbarAssessmentOutcome || '',
          sbarPcpFeedback: formValues.sbarPcpFeedback || '',
          sbarComment: formValues.sbarComment || '',
        }),
        // Add IDT fields if needed
        ...(formValues.encounterType === 'IDT Note' && {
          idtActiveConcerns: formValues.idtActiveConcerns || '',
          idtCurrentStatus: formValues.idtCurrentStatus || '',
          idtActiveNotes: formValues.idtActiveNotes || '',
          idtSmartGoalsProgress: formValues.idtSmartGoalsProgress || '',
          idtNutritionStatusClassification: formValues.idtNutritionStatusClassification || '',
          idtNutritionStatus: formValues.idtNutritionStatus || '',
          idtPhysicalActivityStatusClassification: formValues.idtPhysicalActivityStatusClassification || '',
          idtPhysicalActivityStatus: formValues.idtPhysicalActivityStatus || '',
          idtRecoveryStatusClassification: formValues.idtRecoveryStatusClassification || '',
          idtRecoveryStatus: formValues.idtRecoveryStatus || '',
          idtEmotionStatusClassification: formValues.idtEmotionStatusClassification || '',
          idtEmotionStatus: formValues.idtEmotionStatus || '',
          idtEnvironmentalStatusClassification: formValues.idtEnvironmentalStatusClassification || '',
          idtEnvironmentalStatus: formValues.idtEnvironmentalStatus || '',
          idtPharmacyRecommendations: formValues.idtPharmacyRecommendations || '',
          idtMdRecommendations: formValues.idtMdRecommendations || '',
          idtAdditionalNotes: formValues.idtAdditionalNotes || '',
        }),
        isSigned: false // Explicitly set as draft/unsigned
      };

      console.log('handleSaveForLater: Saving draft encounter:', draftPayload);

      const response = await axios.post(`/patients/${patientId}/encounters`, draftPayload);
      const newEncounter = response.data.data.encounter;

      message.success('Encounter saved as draft! You can complete and sign it later from your Work Basket.');
      
      // Close modal and refresh list
      setAddModalVisible(false);
      setStagedVitals(null);
      fetchEncounters();
      
      console.log('handleSaveForLater: Draft encounter saved successfully:', newEncounter);
      
    } catch (error) {
      console.error('handleSaveForLater: Error saving draft encounter:', error);
      const errorMsg = error.response?.data?.message || 'Failed to save encounter as draft.';
      message.error(errorMsg);
    } finally {
      setAddSubmitLoading(false);
    }
  };

  // Function to handle adding (saving) an encounter - Called by onFinish
  const handleAddEncounter = async (values, saveForLater = false) => {
    if (!user || !user._id) {
        message.error('User not logged in or user ID missing.');
        throw new Error('User not authenticated'); // Throw to stop further execution
    }
    console.log('handleAddEncounter: Submitting new encounter with form values:', values); // Log initial form values
    console.log('handleAddEncounter: Staged vitals before submission:', stagedVitals); // Log staged vitals
    console.log('handleAddEncounter: Save for later mode:', saveForLater); // Log save mode
    setAddSubmitLoading(true); // Use addSubmitLoading for save process
    try {
      // Start with base payload excluding notes fields
      const basePayload = {
        encounterType: values.encounterType,
        reasonForVisit: values.reasonForVisit,
        encounterDate: values.encounterDate ? values.encounterDate.toISOString() : new Date().toISOString(),
        // --- Cline: Refined Vitals Merging Logic ---
        // Get vitals from the main form (might contain objects if pre-filled)
        // Note: values.vitals might be undefined if no vitals fields were rendered/touched in the main form
        vitals: (() => {
            const formVitals = values.vitals || {};
            const finalVitals = { ...formVitals }; // Start with form values

            if (stagedVitals) {
              // Overwrite with staged values (which should be numbers from UpdateVitalsModal)
              for (const key in stagedVitals) {
                if (Object.hasOwnProperty.call(stagedVitals, key)) {
                  // Ensure we only stage valid numbers or null/undefined
                  let stagedValue = stagedVitals[key];

                  // --- Cline: Extract numeric value for specific keys if object ---
                  if (['temperature', 'weight', 'height'].includes(key) && typeof stagedValue === 'object' && stagedValue !== null && stagedValue.hasOwnProperty('value')) {
                    stagedValue = stagedValue.value; // Extract the numeric value
                  }
                  // --- End Cline ---

                  if (typeof stagedValue === 'number' || stagedValue === null || stagedValue === undefined) {
                     finalVitals[key] = stagedValue;
                  } else {
                     console.warn(`handleAddEncounter: Ignoring non-numeric/invalid staged vital: ${key}=${JSON.stringify(stagedValue)}`); // Log full value
                     // Optionally set to null if the key existed in formVitals but was invalid in stagedVitals
                     if (finalVitals.hasOwnProperty(key)) {
                        finalVitals[key] = null;
                     }
                  }
                }
              }
            }

            // Clean the final object: Ensure all values sent are numbers or null/undefined
            for (const key in finalVitals) {
              if (Object.hasOwnProperty.call(finalVitals, key)) {
                const value = finalVitals[key];
                // Remove properties that are objects (like { unit: 'C' }) or empty strings
                if ((typeof value === 'object' && value !== null) || value === '') {
                  console.warn(`handleAddEncounter: Removing invalid/object vital before sending: ${key}=${JSON.stringify(value)}`);
                  finalVitals[key] = null; // Set to null instead of deleting to potentially clear existing values if needed
                }
              }
            }
            return finalVitals;
        })(),
        // --- End Cline: Refined Vitals Merging Logic ---
        patient: patientId,
        provider: user._id,
        isSigned: true, // Regular encounters are signed/completed by default
      };

      // Build the notes object based on the form values that are present
      const notesPayload = {};
      // Define fields relevant to encounters (excluding removed history)
      // Use new combined field name, remove reviewOfSystems
      const noteFields = [
        'subjective', 'objective', 'assessment', 'plan',
        'intakeHistoryUpdates', 'hpiRosCombined'
      ];

      noteFields.forEach(field => {
        // Include the field in notesPayload if it has a non-empty value in the form
        const fieldValue = addForm.getFieldValue(field);
        if (fieldValue !== undefined && fieldValue !== null && fieldValue !== '' && fieldValue !== '<p></p>') { // Check for empty paragraph from editor
          notesPayload[field] = fieldValue;
        }
      });

      // Combine base payload and notes
      const finalPayload = {
        ...basePayload,
        notes: notesPayload,
        // Add SBAR fields directly if the type is Nurse SBAR Summary
        ...(values.encounterType === 'Nurse SBAR Summary' && {
          sbarSituation: values.sbarSituation,
          sbarAssessmentOutcome: values.sbarAssessmentOutcome,
          sbarPcpFeedback: values.sbarPcpFeedback,
          sbarComment: values.sbarComment,
        }),
        // Add IDT fields directly if the type is IDT Note
        ...(values.encounterType === 'IDT Note' && {
          idtActiveConcerns: values.idtActiveConcerns,
          idtCurrentStatus: values.idtCurrentStatus,
          idtActiveNotes: values.idtActiveNotes,
          idtSmartGoalsProgress: values.idtSmartGoalsProgress,
          idtNutritionStatusClassification: values.idtNutritionStatusClassification,
          idtNutritionStatus: values.idtNutritionStatus,
          idtPhysicalActivityStatusClassification: values.idtPhysicalActivityStatusClassification,
          idtPhysicalActivityStatus: values.idtPhysicalActivityStatus,
          idtRecoveryStatusClassification: values.idtRecoveryStatusClassification,
          idtRecoveryStatus: values.idtRecoveryStatus,
          idtEmotionStatusClassification: values.idtEmotionStatusClassification,
          idtEmotionStatus: values.idtEmotionStatus,
          idtEnvironmentalStatusClassification: values.idtEnvironmentalStatusClassification,
          idtEnvironmentalStatus: values.idtEnvironmentalStatus,
          idtPharmacyRecommendations: values.idtPharmacyRecommendations,
          idtMdRecommendations: values.idtMdRecommendations,
          idtAdditionalNotes: values.idtAdditionalNotes,
        }),
      };

      // console.log('Final Payload:', JSON.stringify(finalPayload, null, 2)); // Cline: Removed debug log

      const response = await axios.post(`/patients/${patientId}/encounters`, finalPayload); // Removed prefix
      const newEncounter = response.data.data.encounter; // Get the newly created encounter object
      console.log('handleAddEncounter: New encounter created:', newEncounter); // Updated log

      const successMessage = saveForLater
        ? 'Encounter saved as draft! You can sign it later from your Work Basket.'
        : 'Encounter added successfully!';
      message.success(successMessage);

      // Close modal and refresh list after successful save
      setAddModalVisible(false);
      setStagedVitals(null); // Clear staged vitals after successful save
      fetchEncounters(); // Refresh the list

      return newEncounter; // Return the created encounter object
    } catch (err) {
      console.error("handleAddEncounter: Error adding encounter:", err); // Updated log
      const errorMsg = err.response?.data?.message || 'Failed to add encounter.';
      message.error(errorMsg);
      throw err; // Re-throw the error so the caller can catch it
    } finally {
      setAddSubmitLoading(false); // Ensure loading is turned off
    }
  };

  // --- Password Confirmation and Signing Logic --- (Moved up)
  const promptForPasswordAndSign = (encounterId) => {
    console.log('promptForPasswordAndSign: Prompting for password for encounter ID:', encounterId);
    setSigningEncounterId(encounterId); // Store the ID of the encounter we intend to sign
    passwordForm.resetFields(); // Clear any previous password input
    setPasswordError(''); // Clear any previous error messages
    setIsPasswordModalVisible(true); // Show the password modal
  };

  const handlePasswordConfirmSubmit = async (values) => {
    if (!signingEncounterId) {
      message.error('Error: No encounter ID specified for signing.');
      return;
    }
    console.log('handlePasswordConfirmSubmit: Attempting to sign encounter ID:', signingEncounterId);
    setPasswordModalLoading(true); // Start loading for the password modal
    setPasswordError(''); // Clear any previous errors
    try {
      const payload = {
        password: values.password, // Get password from the modal form
      };
      // Call the backend SIGN endpoint with the password
      // Add a flag to bypass the global 401 interceptor for password verification
      await axios.patch(`/encounters/${signingEncounterId}/sign`, payload, {
        bypassGlobalInterceptor: true
      });
      console.log('handlePasswordConfirmSubmit: PATCH /sign successful for ID:', signingEncounterId);
      message.success('Encounter signed successfully!');

      // Clear error state on success
      setPasswordError('');
      
      // Close the password modal
      setIsPasswordModalVisible(false);
      setSigningEncounterId(null); // Clear the stored ID

      // Fetch the updated encounter to refresh the view modal if it's open
      if (isViewModalVisible && viewingEncounter?._id === signingEncounterId) {
        const updatedEncounterResponse = await axios.get(`/encounters/${signingEncounterId}`);
        setViewingEncounter(updatedEncounterResponse.data.data.encounter);
      }

      // Refresh the main encounter list
      fetchEncounters();

      // Close the edit modal if it was open when signing was initiated
      if (editModalVisible && editingEncounter?._id === signingEncounterId) {
        setEditModalVisible(false);
        setEditingEncounter(null);
      }
      // Note: Add modal is closed earlier in handleSaveAndSignClick

    } catch (err) {
      console.error("handlePasswordConfirmSubmit: Error signing encounter:", signingEncounterId, err);
      console.error("handlePasswordConfirmSubmit: Full error response:", err.response);
      
      let errorMessage = 'Failed to sign encounter. Please try again.';
      
      // Handle specific password-related errors without letting them trigger global logout
      if (err.response?.status === 401) {
        errorMessage = err.response?.data?.message || 'Incorrect password. Please try again.';
        console.warn("handlePasswordConfirmSubmit: 401 Password error:", errorMessage);
        
        // Display error in multiple ways to ensure visibility
        message.error(errorMessage);
        setPasswordError(errorMessage);
        
        // Clear password field for retry
        passwordForm.setFieldsValue({ password: '' });
        
      } else if (err.response?.status === 403) {
        errorMessage = 'You do not have permission to sign this type of encounter.';
        message.error(errorMessage);
        setPasswordError(errorMessage);
        
        // Close modal for permission errors after a delay
        setTimeout(() => {
          setIsPasswordModalVisible(false);
          setSigningEncounterId(null);
          setPasswordError('');
        }, 3000);
        
      } else {
        // Handle other errors
        errorMessage = err.response?.data?.message || 'Failed to sign encounter. Please try again.';
        console.error("handlePasswordConfirmSubmit: Other error:", errorMessage);
        message.error(errorMessage);
        setPasswordError(errorMessage);
      }
      
      // Always set error state for form display
      if (err.response?.status !== 403) {
        // Keep the password modal open for retry (except for permission errors)
        console.log("handlePasswordConfirmSubmit: Modal staying open for retry");
      }
    } finally {
      setPasswordModalLoading(false); // Stop loading for the password modal
    }
  };

  // Function to handle the "Sign and Save" button click (in Add Modal)
  const handleSaveAndSignClick = async () => {
    try {
      // 1. Validate the form fields
      const values = await addForm.validateFields();
      console.log('handleSaveAndSignClick: Form validation successful, values:', values); // Added log

      // 2. Save the encounter (re-use logic, but don't close modal/refresh yet)
      if (!user || !user._id) {
          message.error('User not logged in or user ID missing.');
          throw new Error('User not authenticated');
      }
      console.log('handleSaveAndSignClick: Saving encounter:', values);
      setAddSubmitLoading(true); // Start loading

      // Build payload (same as in handleAddEncounter, including stagedVitals logic)
      const basePayload = {
        encounterType: values.encounterType,
        reasonForVisit: values.reasonForVisit,
        encounterDate: values.encounterDate ? values.encounterDate.toISOString() : new Date().toISOString(),
        // --- Cline: Apply Refined Vitals Merging Logic Here Too ---
        vitals: (() => {
            const formVitals = values.vitals || {};
            const finalVitals = { ...formVitals }; // Start with form values

            if (stagedVitals) {
              // Overwrite with staged values
              for (const key in stagedVitals) {
                if (Object.hasOwnProperty.call(stagedVitals, key)) {
                  // Ensure we only stage valid numbers or null/undefined
                  let stagedValue = stagedVitals[key];

                  // Extract numeric value for specific keys if object
                  if (['temperature', 'weight', 'height'].includes(key) && typeof stagedValue === 'object' && stagedValue !== null && stagedValue.hasOwnProperty('value')) {
                    stagedValue = stagedValue.value; // Extract the numeric value
                  }

                  if (typeof stagedValue === 'number' || stagedValue === null || stagedValue === undefined) {
                     finalVitals[key] = stagedValue;
                  } else {
                     console.warn(`handleSaveAndSignClick: Ignoring non-numeric/invalid staged vital: ${key}=${JSON.stringify(stagedValue)}`);
                     if (finalVitals.hasOwnProperty(key)) {
                         finalVitals[key] = null;
                      }
                   }
                }
              }
            }

            // Clean the final object: Ensure all values sent are numbers or null/undefined
            for (const key in finalVitals) {
              if (Object.hasOwnProperty.call(finalVitals, key)) {
                const value = finalVitals[key];
                if ((typeof value === 'object' && value !== null) || value === '') {
                   console.warn(`handleSaveAndSignClick: Removing invalid/object vital before sending: ${key}=${JSON.stringify(value)}`);
                   finalVitals[key] = null;
                }
              }
            }
            return finalVitals;
         })(),
         // --- End Cline: Apply Refined Vitals Merging Logic ---
        patient: patientId,
        provider: user._id,
      };
      const notesPayload = {};
      const noteFields = ['subjective', 'objective', 'assessment', 'plan', 'intakeHistoryUpdates', 'hpiRosCombined'];
      noteFields.forEach(field => {
        const fieldValue = addForm.getFieldValue(field);
        if (fieldValue !== undefined && fieldValue !== null && fieldValue !== '' && fieldValue !== '<p></p>') {
          notesPayload[field] = fieldValue;
        }
      });
      const finalPayload = {
        ...basePayload,
        notes: notesPayload,
        // Add SBAR fields directly if the type is Nurse SBAR Summary
        ...(values.encounterType === 'Nurse SBAR Summary' && {
          sbarSituation: values.sbarSituation,
          sbarAssessmentOutcome: values.sbarAssessmentOutcome,
          sbarPcpFeedback: values.sbarPcpFeedback,
          sbarComment: values.sbarComment,
        }),
        // Add IDT fields directly if the type is IDT Note
        ...(values.encounterType === 'IDT Note' && {
          idtActiveConcerns: values.idtActiveConcerns,
          idtCurrentStatus: values.idtCurrentStatus,
          idtActiveNotes: values.idtActiveNotes,
          idtSmartGoalsProgress: values.idtSmartGoalsProgress,
          idtNutritionStatusClassification: values.idtNutritionStatusClassification,
          idtNutritionStatus: values.idtNutritionStatus,
          idtPhysicalActivityStatusClassification: values.idtPhysicalActivityStatusClassification,
          idtPhysicalActivityStatus: values.idtPhysicalActivityStatus,
          idtRecoveryStatusClassification: values.idtRecoveryStatusClassification,
          idtRecoveryStatus: values.idtRecoveryStatus,
          idtEmotionStatusClassification: values.idtEmotionStatusClassification,
          idtEmotionStatus: values.idtEmotionStatus,
          idtEnvironmentalStatusClassification: values.idtEnvironmentalStatusClassification,
          idtEnvironmentalStatus: values.idtEnvironmentalStatus,
          idtPharmacyRecommendations: values.idtPharmacyRecommendations,
          idtMdRecommendations: values.idtMdRecommendations,
          idtAdditionalNotes: values.idtAdditionalNotes,
        }),
      };

      // Save
      const response = await axios.post(`/patients/${patientId}/encounters`, finalPayload);
      const newEncounter = response.data.data.encounter;
      console.log('handleSaveAndSignClick: Encounter saved, new encounter object:', newEncounter);
      message.success('Encounter saved successfully!');

      // 3. Prompt for password using the ID from the newly created encounter
      if (newEncounter?._id) {
        console.log('handleSaveAndSignClick: Encounter saved, now prompting for password for ID:', newEncounter._id);
        // Close the add modal *before* showing the password modal
        setAddModalVisible(false);
        setStagedVitals(null); // Clear staged vitals after successful save
        // Call the function to show the password modal
        promptForPasswordAndSign(newEncounter._id);
        // Note: Refreshing the encounter list will happen *after* successful password confirmation
      } else {
        console.error('handleSaveAndSignClick: New encounter ID is missing after save.');
        message.error('Failed to get new encounter ID for signing.');
        throw new Error('Missing encounter ID after save');
      }
      // Removed closing modal and refreshing list here - it happens after sign

    } catch (err) {
      // Handle validation errors or errors from save
      console.error("handleSaveAndSignClick: Error during save workflow:", err);
      // If error is from validateFields, Ant Design handles message display
      // If error is from save, that function handles message display
    } finally {
      // Loading is turned off inside promptForPasswordAndSign or if save fails
      setAddSubmitLoading(false); // Ensure loading is turned off if save fails before prompt
    }
  };


  // --- Edit Modal Logic ---
  const showEditModal = (encounter) => {
    try {
      if (!encounter || !encounter._id) {
        message.error('Invalid encounter data. Cannot open edit modal.');
        return;
      }
      
      console.log('✏️ PatientEncounters: Opening edit modal for encounter:', {
        id: encounter._id,
        type: encounter.encounterType,
        date: encounter.encounterDate,
        isSigned: encounter.isSigned
      });
      setEditingEncounter(encounter);

    // Set current encounter ID for auto-save (especially for IDT Notes)
    if (encounter.encounterType === 'IDT Note') {
      setCurrentIdtEncounterId(encounter._id);
      // Load the encounter version for conflict detection
      loadEncounterVersion(encounter._id);
    }
    // Determine initial objective value for edit modal - Removed template logic
    let initialEditObjective = encounter.notes?.objective || '';
    setLatestVitalsData(null); // Reset latest vitals
    setStagedVitals(null); // Reset staged vitals
    // Reset IDT data states
    setIdtPatientData(null);
    setIdtAllergies([]);
    setIdtMedications([]);
    setIdtOrders([]);
    setIdtAbnormalLabs([]);
    setIdtAllLabEntries([]);
    setIdtLoading(false);

    // --- Vitals Logic for Edit Modal ---
    const encounterHasVitals = encounter.vitals && Object.keys(encounter.vitals).length > 0;
    const isEncounterSigned = encounter.isSigned;

    if (isEncounterSigned) {
      // For signed encounters, ALWAYS use the encounter's saved vitals (permanently locked)
      if (encounterHasVitals) {
        console.log("showEditModal: Using permanently locked vitals from signed encounter:", encounter.vitals);
        setLatestVitalsData({
          source: 'Signed Encounter (Permanently Locked)',
          date: encounter.encounterDate,
          vitals: cleanVitalsObject(encounter.vitals)
        });
      } else {
        console.log("showEditModal: Signed encounter has no vitals");
        setLatestVitalsData(null);
      }
    } else {
      // For unsigned encounters, use encounter vitals if available, otherwise fetch intake/latest vitals
      if (encounterHasVitals) {
        console.log("showEditModal: Using saved vitals from unsigned encounter:", encounter.vitals);
        setLatestVitalsData({
          source: 'This Encounter (Saved)',
          date: encounter.encounterDate,
          vitals: cleanVitalsObject(encounter.vitals)
        });
      } else {
        // Fetch intake vitals first, fallback to latest vitals
        console.log("showEditModal: No saved vitals, fetching intake/latest vitals.");
        const fetchVitalsForEdit = async () => {
          try {
            setEditSubmitLoading(true);

            // Try intake vitals first
            const intakeResponse = await axios.get(`/patients/${patientId}/intakes`);
            const recentIntake = intakeResponse.data.data.intakeRecord;

            let vitalsToDisplay = null;

            if (recentIntake && recentIntake.vitals && Object.keys(recentIntake.vitals).length > 0) {
              console.log("Using intake vitals for edit:", recentIntake.vitals);
              vitalsToDisplay = {
                source: 'Recent Intake',
                date: recentIntake.createdAt || recentIntake.updatedAt,
                vitals: recentIntake.vitals
              };
            } else {
              // Fallback to latest vitals
              const vitalsResponse = await axios.get(`/patients/${patientId}/latest-vitals-trend`);
              console.log("Latest vitals API response for edit:", vitalsResponse.data);

              // Transform the API response to the expected format
              vitalsToDisplay = transformVitalsData(vitalsResponse.data.data);
              console.log("Transformed vitals for edit:", vitalsToDisplay);
            }

            setLatestVitalsData(vitalsToDisplay);
          } catch (err) {
            console.error("Error fetching vitals for edit modal:", err);
            message.error('Failed to load vitals data.');
          } finally {
            setEditSubmitLoading(false);
          }
        };
        fetchVitalsForEdit();
      }
    }
    // --- End Vitals Logic ---

    // Fetch IDT data if editing an IDT Note
    const fetchIdtDataForEdit = async () => {
      if (encounter.encounterType === 'IDT Note') {
        setIdtLoading(true); // Use separate loading state for IDT data
        try {


          // Fetch patient, allergies, meds, problems, orders, vitals, flowsheet data, encounters for vitals history, and SMART Goals
          const [patientRes, allergiesRes, medsRes, problemsRes, ordersRes, vitalsRes, flowsheetRes, encountersRes, smartGoalsRes] = await Promise.all([
            axios.get(`/patients/${patientId}?populate=careTeam`),
            axios.get(`/patients/${patientId}/allergies`),
            axios.get(`/patients/${patientId}/medications?status=Active`),
            axios.get(`/patients/${patientId}/problems?status=Active`),
            axios.get(`/patients/${patientId}/orders?status=Active&status=Pending`),
            axios.get(`/patients/${patientId}/latest-vitals-trend`),
            axios.get(`/patients/${patientId}/flowsheet?limit=500`),
            axios.get(`/patients/${patientId}/encounters?limit=100`), // Get encounters for vitals history
            axios.get(`/patients/${patientId}/smart-goals`) // Get SMART Goals
          ]);
          setIdtPatientData(patientRes.data.data.patient);
          setIdtAllergies(allergiesRes.data.data.allergies || []);
          setIdtMedications(medsRes.data.data.medications || []);
          setIdtProblems(problemsRes.data.data.problems || []);
          // Filter orders to show only Active/Pending Laboratory and Radiology orders for diagnostics
          const filteredOrders = (ordersRes.data.data.orders || []).filter(order =>
            (order.status === 'Active' || order.status === 'Pending') &&
            (order.orderType === 'Laboratory' || order.orderType === 'Radiology')
          );
          setIdtOrders(filteredOrders);

          // Process SMART Goals data
          const smartGoals = smartGoalsRes.data.data.smartGoals || [];
          const activeGoals = smartGoals
            .filter(goal => goal.status !== 'Cancelled')
            .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
          setIdtSmartGoals(activeGoals);

          // Transform and set vitals data for IDT Note display (only if not already set from encounter vitals or snapshot)
          if (!latestVitalsData || !latestVitalsData.isSnapshot) {
            const vitalsToDisplay = transformVitalsData(vitalsRes.data.data);
            if (vitalsToDisplay) {
              setLatestVitalsData({
                ...vitalsToDisplay,
                source: 'Most Recent Vitals'
              });
            }
          }

          // Transform and set abnormal labs data for IDT Note display
          // Use the labs object but get actual entry dates from raw flowsheet entries
          const labsData = flowsheetRes.data.data.labs || {};

          // Create a map of lab names to their most recent entry dates from raw flowsheet entries
          const labDateMap = {};
          if (flowsheetRes.data.data.flowsheetEntries) {
            flowsheetRes.data.data.flowsheetEntries
              .filter(entry => entry.type === 'Lab' || entry.type === 'LabResult')
              .forEach(entry => {
                if (entry.name && entry.entryDate) {
                  // Keep the most recent date for each lab parameter
                  if (!labDateMap[entry.name] || new Date(entry.entryDate) > new Date(labDateMap[entry.name])) {
                    labDateMap[entry.name] = entry.entryDate;
                  }
                }
              });
          }

          const flowsheetEntries = Object.entries(labsData).map(([paramName, value]) => ({
            name: paramName,
            value: value,
            units: getLabUnits(paramName),
            referenceRange: getLabReferenceRange(paramName),
            type: 'Lab',
            category: 'Lab Results',
            entryDate: labDateMap[paramName] || new Date(), // Use actual entry date or fallback to current date
            source: 'Most Recent Flowsheet'
          }));

          const abnormalLabsData = transformLabsForAbnormalDisplay(flowsheetEntries, 30);
          setIdtAbnormalLabs(abnormalLabsData);

          // Store all lab entries for history modal
          const allLabEntries = flowsheetEntries.filter(entry =>
            entry.type === 'Lab' ||
            (entry.name && entry.name.startsWith('labs_')) ||
            entry.category === 'Lab Results'
          );
          setIdtAllLabEntries(allLabEntries);

          // Store all vitals entries for history modal (from encounters)
          const allVitalsEntries = encountersRes.data.data.encounters
            .filter(encounter => encounter.vitals && Object.keys(encounter.vitals).length > 0)
            .map(encounter => ({
              _id: encounter._id,
              entryDate: encounter.encounterDate,
              vitals: encounter.vitals,
              source: encounter.encounterType || 'Encounter'
            }));
          setIdtAllVitalsEntries(allVitalsEntries);

           console.log("IDT Data Fetched for Edit:", { // Updated log message
              patient: patientRes.data.data.patient,
              allergies: allergiesRes.data.data.allergies,
              medications: medsRes.data.data.medications,
              problems: problemsRes.data.data.problems,
              orders: ordersRes.data.data.orders,
              vitals: latestVitalsData,
              abnormalLabs: abnormalLabsData
          });
        } catch (err) {
          console.error("Error fetching data for editing IDT Note:", err);
          message.error('Failed to load required data for IDT Note.');
        } finally {
          setIdtLoading(false); // Stop IDT loading
        }
      }
    };
    fetchIdtDataForEdit();


    // Pre-fill form, handling nested notes and vitals
    // Prepare other fields first
    const otherFields = {
        // Spread encounter fields, but explicitly handle notes and vitals separately
        encounterType: encounter.encounterType,
        reasonForVisit: encounter.reasonForVisit,
        encounterDate: encounter.encounterDate ? moment(encounter.encounterDate) : null,
        subjective: encounter.notes?.subjective || '',
        objective: initialEditObjective, // Use existing value or empty string
        assessment: encounter.encounterType === 'MD Encounter'
          ? combineAssessmentAndPlan(encounter.notes?.assessment || '', encounter.notes?.plan || '')
          : encounter.notes?.assessment || '',
        plan: encounter.notes?.plan || '',
        intakeHistoryUpdates: encounter.notes?.intakeHistoryUpdates || '',
        hpiRosCombined: encounter.notes?.hpiRosCombined || '', // Use new combined field name
        // Pre-fill SBAR fields if they exist
        sbarSituation: encounter.sbarSituation || '',
        sbarAssessmentOutcome: encounter.sbarAssessmentOutcome || '',
        sbarPcpFeedback: encounter.sbarPcpFeedback || '',
        sbarComment: encounter.sbarComment || '',
        // Pre-fill IDT fields if they exist
        idtActiveConcerns: encounter.idtActiveConcerns || '',
        idtCurrentStatus: encounter.idtCurrentStatus || '',
        idtActiveNotes: encounter.idtActiveNotes || '',
        idtSmartGoalsProgress: encounter.idtSmartGoalsProgress || '',
        idtNutritionStatusClassification: encounter.idtNutritionStatusClassification || '',
        idtNutritionStatus: encounter.idtNutritionStatus || '',
        idtPhysicalActivityStatusClassification: encounter.idtPhysicalActivityStatusClassification || '',
        idtPhysicalActivityStatus: encounter.idtPhysicalActivityStatus || '',
        idtRecoveryStatusClassification: encounter.idtRecoveryStatusClassification || '',
        idtRecoveryStatus: encounter.idtRecoveryStatus || '',
        idtEmotionStatusClassification: encounter.idtEmotionStatusClassification || '',
        idtEmotionStatus: encounter.idtEmotionStatus || '',
        idtEnvironmentalStatusClassification: encounter.idtEnvironmentalStatusClassification || '',
        idtEnvironmentalStatus: encounter.idtEnvironmentalStatus || '',
        idtPharmacyRecommendations: encounter.idtPharmacyRecommendations || '',
        idtMdRecommendations: encounter.idtMdRecommendations || '',
        idtAdditionalNotes: encounter.idtAdditionalNotes || '',
    };

    // Prepare vitals fields with array path keys for setFieldsValue
    const vitalsToSet = {};
    if (encounter.vitals) {
      for (const key in encounter.vitals) {
        // Ensure the key is directly on the vitals object
        if (Object.hasOwnProperty.call(encounter.vitals, key)) {
          let vitalValue = encounter.vitals[key];
          // Handle object values with {value, unit} structure (e.g., temperature)
          if (typeof vitalValue === 'object' && vitalValue !== null && 'value' in vitalValue) {
            vitalValue = vitalValue.value;
          }
          // Use the array path format ['vitals', 'fieldName']
          vitalsToSet[['vitals', key]] = vitalValue;
        }
      }
    }
    console.log("Pre-filling editForm with vitals:", vitalsToSet); // Debug log

    // Set all fields together
    editForm.setFieldsValue({
      ...otherFields,
      ...vitalsToSet
    });

    // If the encounter is signed, show the view modal instead of the edit modal
    if (encounter.isSigned) {
      showViewModal(encounter);
    } else {
      setEditModalVisible(true);
    }
    } catch (error) {
      console.error('Error opening edit modal:', error);
      message.error('Failed to open encounter for editing. Please try again.');
    }
  };

  const handleEditCancel = () => {
    // Check if we're closing an IDT Note that may have been auto-saved
    const wasIdtNote = editingEncounter?.encounterType === 'IDT Note';
    const hadEncounterId = currentIdtEncounterId !== null;

    setEditModalVisible(false);
    setEditingEncounter(null);
    setStagedVitals(null); // Reset staged vitals on cancel

    // Reset IDT state
    setCurrentIdtEncounterId(null);

    // Refresh encounters list if IDT Note was auto-saved
    if (wasIdtNote && hadEncounterId) {
      console.log('🔄 Refreshing encounters list after IDT Note auto-save');
      fetchEncounters();
    }
  };

  const handleUpdateEncounter = async (values) => {
     if (!editingEncounter?._id) {
        message.error('No encounter selected for editing.');
        return;
    }
    console.log('Updating encounter:', editingEncounter._id, 'with form values:', values); // Log form values
    console.log('Updating encounter: Staged vitals before submission:', stagedVitals); // Log staged vitals
    setEditSubmitLoading(true);
    try {
       // Start with base payload excluding notes fields
       const basePayload = {
         encounterType: values.encounterType,
         reasonForVisit: values.reasonForVisit,
         encounterDate: values.encounterDate ? values.encounterDate.toISOString() : null,
         // --- Cline: Refined Vitals Merging Logic ---
         vitals: (() => {
            const formVitals = values.vitals || {};
            const finalVitals = { ...formVitals }; // Start with form values

            if (stagedVitals) {
              // Overwrite with staged values
              for (const key in stagedVitals) {
                if (Object.hasOwnProperty.call(stagedVitals, key)) {
                  // Ensure we only stage valid numbers or null/undefined
                  let stagedValue = stagedVitals[key];

                  // --- Cline: Extract numeric value for specific keys if object ---
                  if (['temperature', 'weight', 'height'].includes(key) && typeof stagedValue === 'object' && stagedValue !== null && stagedValue.hasOwnProperty('value')) {
                    stagedValue = stagedValue.value; // Extract the numeric value
                  }
                  // --- End Cline ---

                  if (typeof stagedValue === 'number' || stagedValue === null || stagedValue === undefined) {
                     finalVitals[key] = stagedValue;
                  } else {
                     console.warn(`handleUpdateEncounter: Ignoring non-numeric/invalid staged vital: ${key}=${JSON.stringify(stagedValue)}`); // Log full value
                     if (finalVitals.hasOwnProperty(key)) {
                         finalVitals[key] = null;
                      }
                   }
                }
              }
            }

            // Clean the final object: Ensure all values sent are numbers or null/undefined
            for (const key in finalVitals) {
              if (Object.hasOwnProperty.call(finalVitals, key)) {
                const value = finalVitals[key];
                if ((typeof value === 'object' && value !== null) || value === '') {
                   console.warn(`handleUpdateEncounter: Removing invalid/object vital before sending: ${key}=${JSON.stringify(value)}`);
                   finalVitals[key] = null;
                }
              }
            }
            return finalVitals;
         })(),
         // --- End Cline: Refined Vitals Merging Logic ---
       };

       // Build the notes object based on the form values that are present
       const notesPayload = {};
       // Define fields relevant to encounters (excluding removed history)
       // Use new combined field name, remove reviewOfSystems
       const noteFields = [
         'subjective', 'objective', 'assessment', 'plan',
         'intakeHistoryUpdates', 'hpiRosCombined'
       ];

       noteFields.forEach(field => {
         // Use getFieldValue for potentially unsynced editor values
         const fieldValue = editForm.getFieldValue(field);
         // Include field if it exists in values, allowing empty strings/HTML to clear fields
         if (fieldValue !== undefined && fieldValue !== null) {
             // Treat empty paragraph tag as empty for saving purposes
             notesPayload[field] = (fieldValue === '<p></p>') ? '' : fieldValue;
         }
       });

       // Combine base payload and notes
       const finalPayload = {
         ...basePayload,
        notes: notesPayload,
        // Add SBAR fields directly if the type is Nurse SBAR Summary
        ...(values.encounterType === 'Nurse SBAR Summary' && {
          sbarSituation: values.sbarSituation,
          sbarAssessmentOutcome: values.sbarAssessmentOutcome,
          sbarPcpFeedback: values.sbarPcpFeedback,
          sbarComment: values.sbarComment,
        }),
        // Add IDT fields directly if the type is IDT Note
        ...(values.encounterType === 'IDT Note' && {
          idtActiveConcerns: values.idtActiveConcerns,
          idtCurrentStatus: values.idtCurrentStatus,
          idtActiveNotes: values.idtActiveNotes,
          idtSmartGoalsProgress: values.idtSmartGoalsProgress,
          idtNutritionStatusClassification: values.idtNutritionStatusClassification,
          idtNutritionStatus: values.idtNutritionStatus,
          idtPhysicalActivityStatusClassification: values.idtPhysicalActivityStatusClassification,
          idtPhysicalActivityStatus: values.idtPhysicalActivityStatus,
          idtRecoveryStatusClassification: values.idtRecoveryStatusClassification,
          idtRecoveryStatus: values.idtRecoveryStatus,
          idtEmotionStatusClassification: values.idtEmotionStatusClassification,
          idtEmotionStatus: values.idtEmotionStatus,
          idtEnvironmentalStatusClassification: values.idtEnvironmentalStatusClassification,
          idtEnvironmentalStatus: values.idtEnvironmentalStatus,
          idtPharmacyRecommendations: values.idtPharmacyRecommendations,
          idtMdRecommendations: values.idtMdRecommendations,
          idtAdditionalNotes: values.idtAdditionalNotes,
        }),
      };

      console.log('handleUpdateEncounter: Final payload being sent:', finalPayload); // Log final payload

      // Use conflict resolution endpoint for IDT Notes
      if (values.encounterType === 'IDT Note') {
        const payloadWithVersion = {
          ...finalPayload,
          version: currentEncounterVersion
        };

        try {
          const response = await axios.patch(`/encounters/${editingEncounter._id}/update-with-conflict-resolution`, payloadWithVersion);

          // Update the current version from the response
          if (response.data.data.encounter.version) {
            setCurrentEncounterVersion(response.data.data.encounter.version);
          }

          message.success('IDT Note updated successfully!');
          setEditModalVisible(false);
          setEditingEncounter(null);
          setStagedVitals(null);
          fetchEncounters();
        } catch (conflictError) {
          if (conflictError.response?.status === 409) {
            // Handle conflict
            const conflictDetails = conflictError.response.data.data.conflictDetails;
            setPendingSaveData(finalPayload);
            setConflictData(conflictDetails);
            setConflictModalVisible(true);
            message.warning('Another user has modified this IDT note. Please resolve conflicts to continue.', 5);
          } else {
            throw conflictError; // Re-throw other errors
          }
        }
      } else {
        // Use regular endpoint for non-IDT encounters
        await axios.patch(`/encounters/${editingEncounter._id}`, finalPayload);
        message.success('Encounter updated successfully!');
        setEditModalVisible(false);
        setEditingEncounter(null);
        setStagedVitals(null);
        fetchEncounters();
      }
    } catch (err) {
      console.error("Error updating encounter:", err);
      message.error(err.response?.data?.message || 'Failed to update encounter.');
    } finally {
      setEditSubmitLoading(false);
    }
  };

  // Define table columns for encounters
  const columns = [
    {
      title: 'Date',
      dataIndex: 'encounterDate',
      key: 'encounterDate',
      render: (date, record) => (
        <span style={{ textDecoration: record.isStricken ? 'line-through' : 'none' }}>
          {date ? new Date(date).toLocaleString() : 'N/A'}
        </span>
      ),
      sorter: (a, b) => new Date(a.encounterDate) - new Date(b.encounterDate),
      defaultSortOrder: 'descend',
    },
    {
      title: 'Type',
      dataIndex: 'encounterType',
      key: 'encounterType',
      render: (text, record) => (
        <span style={{ textDecoration: record.isStricken ? 'line-through' : 'none' }}>
          {text}
          {!record.isSigned ? (
            <span style={{ 
              marginLeft: 8, 
              padding: '2px 8px', 
              backgroundColor: '#faad14', 
              color: 'white', 
              borderRadius: '4px', 
              fontSize: '12px',
              fontWeight: 'bold'
            }}>
              DRAFT
            </span>
          ) : (
            <span style={{ 
              marginLeft: 8, 
              padding: '2px 8px', 
              backgroundColor: '#52c41a', 
              color: 'white', 
              borderRadius: '4px', 
              fontSize: '12px',
              fontWeight: 'bold'
            }}>
              SIGNED
            </span>
          )}
        </span>
      ),
      filters: [
        { text: 'MD Encounter', value: 'MD Encounter' },
        { text: 'Progress Note', value: 'Progress Note' },
        { text: 'Nurse Encounter', value: 'Nurse Encounter' },
        { text: 'Nurse SBAR Summary', value: 'Nurse SBAR Summary' }, // Added filter
        { text: 'IDT Note', value: 'IDT Note' },
        { text: 'Pharmacy Note', value: 'Pharmacy Note' },
        { text: 'Lab Review', value: 'Lab Review' },
        { text: 'Radiology Review', value: 'Radiology Review' },
        { text: 'Procedure', value: 'Procedure' },
        { text: 'Telehealth', value: 'Telehealth' },
        { text: 'Comprehensive', value: 'Comprehensive' },
        { text: 'Other', value: 'Other' },
      ],
      onFilter: (value, record) => record.encounterType.includes(value),
    },
    {
      title: 'Provider',
      dataIndex: 'provider',
      key: 'provider',
      render: (provider, record) => (
        <span style={{ textDecoration: record.isStricken ? 'line-through' : 'none' }}>
          {provider ? `${provider.name} (${provider.role})` : 'N/A'}
        </span>
      ),
    },
    {
      title: 'Reason for Visit',
      dataIndex: 'reasonForVisit',
      key: 'reasonForVisit',
      render: (text, record) => (
         <span style={{ textDecoration: record.isStricken ? 'line-through' : 'none' }}>
           {text || 'N/A'}
         </span>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => {
        // Add more robust null/undefined checks for record and its properties
        if (!record || !record.provider) { // Removed encounterType check here as it's checked below
          // Return null or an empty fragment if essential data is missing
          return null;
        }

        // Check if the current user is the provider or an Admin
        const isProvider = user && record.provider && record.provider._id === user._id;
        const isAdmin = user && user.role === 'Admin';
        const canDelete = isProvider || isAdmin;

        // Determine if the current user can sign this encounter type
        let canSign = false;
        // Ensure user and record.encounterType exist before calculating canSign
        if (user && record.encounterType) { // Check record.encounterType here
          const userRole = user.role;
          const encounterType = record.encounterType; // Now guaranteed to exist here

          if (userRole === 'Admin') {
            canSign = true; // Admin can sign any note
          } else if (encounterType === 'Progress Note') {
            canSign = true; // All authenticated users can sign Progress Notes
          } else if (userRole === 'MD' && (encounterType === 'MD Encounter' || encounterType === 'IDT Note')) {
            canSign = true;
          } else if (userRole === 'Nursing' && (encounterType === 'Nurse Encounter' || encounterType === 'Nurse SBAR Summary')) {
            canSign = true;
          } else if (userRole === 'Pharmacy' && encounterType === 'Pharmacy Note') {
            canSign = true; // Pharmacy can sign Pharmacy Notes
          } else if (userRole === 'Laboratory' && encounterType === 'Lab Review') {
            canSign = true; // Laboratory can sign Lab Review notes
          } else if (userRole === 'Radiology' && encounterType === 'Radiology Review') {
            canSign = true; // Radiology can sign Radiology Review notes
          }
          // Add other role/type combinations if needed
        }

        return (
          <Space size="small">
            {/* Sign Button (moved to View Modal) */}

            {/* View Button - available for all encounters (signed and unsigned) */}
            <Button
              type="link"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => showViewModal(record)}
            >
              View
            </Button>

            {/* Edit Button - available for all encounters (signed and unsigned) */}
            <Button
              type="link"
              size="small"
              icon={<EditOutlined />}
              onClick={() => showEditModal(record)}
            >
              Edit
            </Button>
            {/* Add View details button later if needed */}

            {/* Delete Button with Conditional Rendering and Confirmation */}
            {((canDelete && !record.isSigned) || (isAdmin && record.isSigned)) ? ( // record is guaranteed to exist here
              <Popconfirm
                title="Are you sure you want to delete this encounter?"
                onConfirm={() => handleDeleteEncounter(record._id)}
                okText="Yes"
                cancelText="No"
              >
                <Button
                  type="link"
                  size="small"
                  danger // Use danger style for delete
                  icon={<DeleteOutlined />} // Add DeleteOutlined icon
                >
                  Delete
                </Button>
              </Popconfirm>
            ) : null} {/* Render null if button should not be shown */}
          </Space>
        );
      },
    },
  ];

  // --- View Modal Logic ---
  const [isViewModalVisible, setIsViewModalVisible] = useState(false);
  const [viewingEncounter, setViewingEncounter] = useState(null);

  // Function to fetch IDT-specific data for the view modal
  const fetchIdtDataForView = useCallback(async (encounter = null) => { // Accept encounter parameter
    setIdtLoading(true);
    try {
      const [patientRes, allergiesRes, medsRes, problemsRes, ordersRes, vitalsRes, flowsheetRes, encountersRes, smartGoalsRes] = await Promise.all([
        axios.get(`/patients/${patientId}?populate=careTeam`),
        axios.get(`/patients/${patientId}/allergies`),
        axios.get(`/patients/${patientId}/medications?status=Active`),
        axios.get(`/patients/${patientId}/problems?status=Active`),
        axios.get(`/patients/${patientId}/orders?status=Active&status=Pending`),
        axios.get(`/patients/${patientId}/latest-vitals-trend`),
        axios.get(`/patients/${patientId}/flowsheet?limit=500`),
        axios.get(`/patients/${patientId}/encounters?limit=100`), // Get encounters for vitals history
        axios.get(`/patients/${patientId}/smart-goals`) // Get SMART Goals
      ]);
      setIdtPatientData(patientRes.data.data.patient);
      setIdtAllergies(allergiesRes.data.data.allergies || []);
      setIdtMedications(medsRes.data.data.medications || []);
      setIdtProblems(problemsRes.data.data.problems || []);
      // Filter orders to show only Active/Pending Laboratory and Radiology orders for diagnostics
      const filteredOrders = (ordersRes.data.data.orders || []).filter(order =>
        (order.status === 'Active' || order.status === 'Pending') &&
        (order.orderType === 'Laboratory' || order.orderType === 'Radiology')
      );
      setIdtOrders(filteredOrders);

      // Process SMART Goals data
      const smartGoals = smartGoalsRes.data.data.smartGoals || [];
      const activeGoals = smartGoals
        .filter(goal => goal.status !== 'Cancelled')
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      setIdtSmartGoals(activeGoals);

      // Transform and set vitals data for IDT Note display (only if not already set from encounter vitals or snapshot)
      // Check if encounter has snapshot data to avoid overwriting it
      const hasVitalsSnapshot = encounter && encounter.vitalsSnapshot && Object.keys(encounter.vitalsSnapshot).length > 0;
      if (!hasVitalsSnapshot) {
        const vitalsToDisplay = transformVitalsData(vitalsRes.data.data);
        if (vitalsToDisplay) {
          setLatestVitalsData({
            ...vitalsToDisplay,
            source: 'Most Recent Vitals'
          });
        }
      }

      // Transform and set abnormal labs data for IDT Note display
      // Use the labs object but get actual entry dates from raw flowsheet entries
      const labsData = flowsheetRes.data.data.labs || {};

      // Create a map of lab names to their most recent entry dates from raw flowsheet entries
      const labDateMap = {};
      if (flowsheetRes.data.data.flowsheetEntries) {
        flowsheetRes.data.data.flowsheetEntries
          .filter(entry => entry.type === 'Lab' || entry.type === 'LabResult')
          .forEach(entry => {
            if (entry.name && entry.entryDate) {
              // Keep the most recent date for each lab parameter
              if (!labDateMap[entry.name] || new Date(entry.entryDate) > new Date(labDateMap[entry.name])) {
                labDateMap[entry.name] = entry.entryDate;
              }
            }
          });
      }

      const flowsheetEntries = Object.entries(labsData).map(([paramName, value]) => ({
        name: paramName,
        value: value,
        units: getLabUnits(paramName),
        referenceRange: getLabReferenceRange(paramName),
        type: 'Lab',
        category: 'Lab Results',
        entryDate: labDateMap[paramName] || new Date(), // Use actual entry date or fallback to current date
        source: 'Most Recent Flowsheet'
      }));

      const abnormalLabsData = transformLabsForAbnormalDisplay(flowsheetEntries, 30);
      setIdtAbnormalLabs(abnormalLabsData);

      // Store all lab entries for history modal
      const allLabEntries = flowsheetEntries.filter(entry =>
        entry.type === 'Lab' ||
        (entry.name && entry.name.startsWith('labs_')) ||
        entry.category === 'Lab Results'
      );
      setIdtAllLabEntries(allLabEntries);

      // Store all vitals entries for history modal (from encounters)
      const allVitalsEntries = encountersRes.data.data.encounters
        .filter(encounter => encounter.vitals && Object.keys(encounter.vitals).length > 0)
        .map(encounter => ({
          _id: encounter._id,
          entryDate: encounter.encounterDate,
          vitals: encounter.vitals,
          source: encounter.encounterType || 'Encounter'
        }));
      setIdtAllVitalsEntries(allVitalsEntries);

      console.log("IDT Data Fetched for View:", {
          patient: patientRes.data.data.patient,
          allergies: allergiesRes.data.data.allergies,
          medications: medsRes.data.data.medications,
          problems: problemsRes.data.data.problems,
          orders: ordersRes.data.data.orders,
          vitals: latestVitalsData,
          abnormalLabs: abnormalLabsData
      });
    } catch (err) {
      console.error("Error fetching data for viewing IDT Note:", err);
      message.error('Failed to load required data for IDT Note.');
      // Optionally reset state or show error state
    } finally {
      setIdtLoading(false);
    }
  }, [patientId]); // Only patientId as dependency, other functions are defined outside component

  // Helper function to determine if a user can sign a specific encounter type
  const canUserSignEncounterType = useCallback((user, encounterType) => {
    if (!user || !encounterType) return false;
    const userRole = user.role;

    if (userRole === 'Admin') {
      return true; // Admin can sign any note
    } else if (encounterType === 'Progress Note') {
      return true; // All authenticated users can sign Progress Notes
    } else if (userRole === 'MD' && (encounterType === 'MD Encounter' || encounterType === 'IDT Note' || encounterType === 'Comprehensive' || encounterType === 'Lab Review' || encounterType === 'Radiology Review' || encounterType === 'Procedure' || encounterType === 'Telehealth' || encounterType === 'Other')) {
      return true;
    } else if (userRole === 'Nursing' && (encounterType === 'Nurse Encounter' || encounterType === 'Nurse SBAR Summary')) {
      return true;
    } else if (userRole === 'Pharmacy' && encounterType === 'Pharmacy Note') {
      return true;
    } else if (userRole === 'Laboratory' && encounterType === 'Lab Review') {
      return true;
    } else if (userRole === 'Radiology' && encounterType === 'Radiology Review') {
      return true;
    }
    // Add other specific role/type combinations if needed
    return false;
  }, []); // No dependencies as user and encounterType are passed as arguments

  // Determine if the current user can sign the currently viewed encounter
  const canSignViewedEncounter = useMemo(() => {
    // This is now primarily used for displaying signed info and addendum button in view modal
    // The core signing permission logic is in canUserSignEncounterType
    if (!user || !viewingEncounter || !viewingEncounter.encounterType) return false;
    return canUserSignEncounterType(user, viewingEncounter.encounterType);
  }, [user, viewingEncounter, canUserSignEncounterType]); // Recalculate if user, viewingEncounter, or the helper function changes


  const showViewModal = (encounter) => {
    try {
      if (!encounter || !encounter._id) {
        message.error('Invalid encounter data. Cannot open view modal.');
        return;
      }
      

      setViewingEncounter(encounter);
      setIsViewModalVisible(true);

    // Set vitals data for display in view modal
    // Priority: vitalsSnapshot (for any encounter that has it) > vitals (for encounters without snapshot)

    if (encounter.vitalsSnapshot && Object.keys(encounter.vitalsSnapshot).length > 0) {
      // Show the vitals snapshot that was captured when the encounter was saved
      setLatestVitalsData({
        source: encounter.isSigned ? 'Vitals Snapshot (Permanent - Captured at Save Time)' : 'Vitals Snapshot (Captured at Save Time)',
        date: encounter.updatedAt || encounter.createdAt || encounter.encounterDate,
        vitals: cleanVitalsObject(encounter.vitalsSnapshot),
        isSnapshot: true
      });
    } else if (encounter.vitals && Object.keys(encounter.vitals).length > 0) {
      // For unsigned encounters or signed encounters without snapshot, show regular vitals
      setLatestVitalsData({
        source: encounter.isSigned ? 'Signed Encounter (Permanently Locked)' : 'This Encounter',
        date: encounter.encounterDate,
        vitals: cleanVitalsObject(encounter.vitals)
      });
    } else {
      setLatestVitalsData(null);
    }

    // Set medications snapshot data for display
    if (encounter.medicationsSnapshot && Object.keys(encounter.medicationsSnapshot).length > 0) {
      setMedicationsSnapshotData({
        source: encounter.isSigned ? 'Medications Snapshot (Permanent - Captured at Save Time)' : 'Medications Snapshot (Captured at Save Time)',
        medications: encounter.medicationsSnapshot
      });
    } else {
      setMedicationsSnapshotData(null);
    }

    // Set problem list snapshot data for display (IDT encounters only)
    if (encounter.encounterType === 'IDT Note' && encounter.problemListSnapshot && Object.keys(encounter.problemListSnapshot).length > 0) {
      setProblemListSnapshotData({
        source: encounter.isSigned ? 'Problem List Snapshot (Permanent - Captured at IDT Signing Time)' : 'Problem List Snapshot (Captured at IDT Signing Time)',
        problemList: encounter.problemListSnapshot
      });
    } else {
      setProblemListSnapshotData(null);
    }

    // Set abnormal labs snapshot data for display (IDT encounters only)
    if (encounter.encounterType === 'IDT Note' && encounter.abnormalLabsSnapshot && Object.keys(encounter.abnormalLabsSnapshot).length > 0) {
      setAbnormalLabsSnapshotData({
        source: encounter.isSigned ? 'Abnormal Labs Snapshot (Permanent - Captured at IDT Signing Time)' : 'Abnormal Labs Snapshot (Captured at IDT Signing Time)',
        abnormalLabs: encounter.abnormalLabsSnapshot
      });
    } else {
      setAbnormalLabsSnapshotData(null);
    }



    // Fetch IDT data if viewing an IDT Note
    if (encounter.encounterType === 'IDT Note') {
      setIdtLoading(true); // Set loading before starting fetch
      fetchIdtDataForView(encounter); // Pass encounter to preserve snapshot data
    }
    } catch (error) {
      console.error('Error opening view modal:', error);
      message.error('Failed to open encounter for viewing. Please try again.');
    }
  };

  const handleViewCancel = () => {
    setIsViewModalVisible(false);
    setViewingEncounter(null);
    setLatestVitalsData(null); // Clear vitals data when closing view modal
    setMedicationsSnapshotData(null); // Clear medications snapshot data when closing view modal
    setProblemListSnapshotData(null); // Clear problem list snapshot data when closing view modal
    setAbnormalLabsSnapshotData(null); // Clear abnormal labs snapshot data when closing view modal
    // Clear IDT data states when closing the view modal
    setIdtPatientData(null);
    setIdtAllergies([]);
    setIdtMedications([]);
    setIdtOrders([]);
    setIdtAbnormalLabs([]);
    setIdtAllLabEntries([]);
    setIdtLoading(false);
  };

  // --- Addendum Modal Logic ---
  // The state variables below were duplicated and are removed here.
  // The correct declarations are near the top of the component.

  // --- Strike Out Modal Logic ---
  const showStrikeModal = (encounterId) => {
    setStrikingEncounterId(encounterId);
    strikeForm.resetFields();
    setIsStrikeModalVisible(true);
  };

  const handleStrikeCancel = () => {
    setIsStrikeModalVisible(false);
    setStrikingEncounterId(null);
  };

  const handleStrikeOutSubmit = async (values) => {
    if (!strikingEncounterId) {
      message.error('No encounter selected for strike out.');
      return;
    }
    setStrikeSubmitLoading(true);
    console.log(`[Frontend] handleStrikeOutSubmit: Attempting to strike encounter ID: ${strikingEncounterId} with reason:`, values.strikeReason); // Frontend Log 1
    try {
      const payload = {
        strikeReason: values.strikeReason,
      };
      // Make the PATCH request to the backend strike endpoint
      console.log(`[Frontend] handleStrikeOutSubmit: Sending PATCH request to /encounters/${strikingEncounterId}/strike`); // Frontend Log 2
      const response = await axios.patch(`/encounters/${strikingEncounterId}/strike`, payload);
      console.log('[Frontend] handleStrikeOutSubmit: PATCH request successful, response:', response); // Frontend Log 3
      message.success('Encounter stricken out successfully!');
      setIsStrikeModalVisible(false); // Close the modal

      // Refresh the encounter list to reflect the change
      fetchEncounters();

      // If the stricken encounter was being viewed, update the view modal state
      if (isViewModalVisible && viewingEncounter?._id === strikingEncounterId) {
        // Fetch the updated encounter data to show stricken status in the view modal
        const updatedEncounterResponse = await axios.get(`/encounters/${strikingEncounterId}`);
        setViewingEncounter(updatedEncounterResponse.data.data.encounter);
      }
       setStrikingEncounterId(null); // Clear the ID after successful operation

    } catch (err) {
      console.error("[Frontend] handleStrikeOutSubmit: Error striking out encounter:", err); // Frontend Log 4 (Error)
      // Log detailed error response if available
      if (err.response) {
        console.error("[Frontend] handleStrikeOutSubmit: Error response data:", err.response.data);
        console.error("[Frontend] handleStrikeOutSubmit: Error response status:", err.response.status);
        console.error("[Frontend] handleStrikeOutSubmit: Error response headers:", err.response.headers);
      } else if (err.request) {
        // The request was made but no response was received
        console.error("[Frontend] handleStrikeOutSubmit: No response received:", err.request);
      } else {
        // Something happened in setting up the request that triggered an Error
        console.error('[Frontend] handleStrikeOutSubmit: Error setting up request:', err.message);
      }
      message.error(err.response?.data?.message || 'Failed to strike out encounter.');
    } finally {
      setStrikeSubmitLoading(false); // Stop loading indicator
    }
  };


  const showAddendumModal = (encounter) => {
    setAddendumEncounter(encounter);
    addendumForm.resetFields();
    setTempAddendumContent(''); // Clear temporary content
    setIsAddendumModalVisible(true);
  };

  const handleAddendumCancel = () => {
    setIsAddendumModalVisible(false);
    setAddendumEncounter(null);
    setTempAddendumContent(''); // Clear temporary content
  };

  // Step 1: Handle submitting the addendum text (from the first modal)
  const handleAddendumTextSubmit = async (values) => {
    console.log("handleAddendumTextSubmit: Storing addendum text and opening password modal.");
    setTempAddendumContent(values.addendumContent); // Store the text
    setIsAddendumModalVisible(false); // Close the text modal
    addendumPasswordForm.resetFields(); // Clear password form
    setIsAddendumPasswordModalVisible(true); // Open the password modal
  };

  // Step 2: Handle submitting the password (from the second modal)
  const handleConfirmAddendumPassword = async (passwordValues) => {
    if (!addendumEncounter?._id) {
      message.error('Error: Encounter ID missing for addendum.');
      return;
    }
    if (!tempAddendumContent) {
      message.error('Error: Addendum content missing.');
      return;
    }
    console.log("handleConfirmAddendumPassword: Submitting addendum with password.");
    setAddendumSubmitLoading(true); // Use the same loading state for the final submission
    setAddendumPasswordError(''); // Clear previous errors
    try {
      const payload = {
        content: tempAddendumContent, // Use stored content
        password: passwordValues.password, // Use password from this modal
      };
      await axios.post(`/encounters/${addendumEncounter._id}/addendum`, payload);
      message.success('Addendum added successfully!');

      setIsAddendumPasswordModalVisible(false); // Close password modal
      setTempAddendumContent(''); // Clear temporary content
      // setAddendumEncounter(null); // Keep addendumEncounter to fetch updated data

      // Fetch the updated encounter to refresh the view modal if it's open
      if (isViewModalVisible && viewingEncounter?._id === addendumEncounter._id) {
        const updatedEncounterResponse = await axios.get(`/encounters/${addendumEncounter._id}`);
        setViewingEncounter(updatedEncounterResponse.data.data.encounter);
      }
      setAddendumEncounter(null); // Clear after potentially updating view

      fetchEncounters(); // Refresh the main list

    } catch (err) {
      // --- Added Detailed Logging ---
      console.error("handleConfirmAddendumPassword: Error adding addendum. Full error object:", err);
      if (err.response) {
        console.error("handleConfirmAddendumPassword: Backend Response Status:", err.response.status);
        console.error("handleConfirmAddendumPassword: Backend Response Data:", err.response.data);
      } else {
        console.error("handleConfirmAddendumPassword: No response received from backend.");
      }
      // --- End Added Logging ---

      // Check for specific error message or status code from backend for incorrect password
      const errorMessage = err.response?.data?.message || 'Failed to add addendum. Please try again.';
      if (err.response?.status === 401 || err.response?.status === 403 || (err.response?.data?.message && err.response.data.message.toLowerCase().includes('password'))) { // Basic check for password related errors
         // message.error('Incorrect password. Please try again.'); // Replaced with state update
         setAddendumPasswordError('Incorrect password. Please try again.');
      } else {
         // message.error(errorMessage); // Replaced with state update
         setAddendumPasswordError(errorMessage);
      }
      // Keep password modal open on error for retry
    } finally {
      setAddendumSubmitLoading(false); // Stop loading
    }
  };

  // Import DeleteOutlined and Popconfirm from antd
  // Need to add DeleteOutlined to the import statement at the top
  // Need to add Popconfirm to the import statement at the top

  // --- Delete Encounter Logic ---
  const handleDeleteEncounter = async (encounterId) => {
    setLoading(true); // Show loading while deleting
    try {
      await axios.delete(`/encounters/${encounterId}`); // Call the backend DELETE endpoint
      message.success('Encounter deleted successfully!');
      fetchEncounters(); // Refresh the list after deletion
    } catch (err) {
      console.error("Error deleting encounter:", err);
      console.error("Backend Error Response:", err.response?.data); // Add this line for detailed backend error
      message.error(err.response?.data?.message || 'Failed to delete encounter.');
      setLoading(false); // Hide loading on error
    }
  };

  // --- Intake Modal Logic ---
  const showIntakeModal = () => {
    setIsIntakeModalVisible(true);
  };

  const handleIntakeCancel = () => {
    setIsIntakeModalVisible(false);
  };

  const handleIntakeSuccess = () => {
    setIsIntakeModalVisible(false);
    // Optionally, you could refetch encounters or other data if intake affects it
    message.success('Intake form submitted successfully!'); // Give feedback
  };

  // --- Problem List Management Modal Logic ---
  const showProblemListModal = () => {
    setIsProblemListModalVisible(true);
  };

  const handleProblemListCancel = () => {
    setIsProblemListModalVisible(false);
  };

  const handleProblemsToggle = (assessmentText) => {
    // Determine which form to update based on current modal state
    const formToUpdate = addModalVisible ? addForm : editForm;
    if (formToUpdate) {
      // Check if we're in an IDT Note context
      const encounterType = formToUpdate.getFieldValue('encounterType');
      if (encounterType === 'IDT Note') {
        // For IDT Notes, update the MD Recommendations field
        formToUpdate.setFieldsValue({ idtMdRecommendations: assessmentText });

        // Refresh IDT problems data when problems are updated
        const fetchIdtProblems = async () => {
          try {
            const problemsRes = await axios.get(`/patients/${patientId}/problems?status=Active`);
            setIdtProblems(problemsRes.data.data.problems || []);
          } catch (err) {
            console.error("Error refreshing IDT problems:", err);
          }
        };
        fetchIdtProblems();
      } else {
        // For regular encounters, update the assessment field
        formToUpdate.setFieldsValue({ assessment: assessmentText });
      }
    }
  };

  // --- Medication Management Modal Logic ---
  const showMedicationModal = () => {
    setIsMedicationModalVisible(true);
  };

  const handleMedicationModalCancel = () => {
    setIsMedicationModalVisible(false);
  };

  const handleMedicationsUpdate = () => {
    // Refresh IDT medications data when medications are updated
    if (addModalVisible || editModalVisible) {
      // Re-fetch IDT data to update the Current Status section
      const fetchIdtData = async () => {
        try {
          const medsRes = await axios.get(`/patients/${patientId}/medications?status=Active`);
          setIdtMedications(medsRes.data.data.medications || []);
        } catch (err) {
          console.error("Error refreshing IDT medications:", err);
        }
      };
      fetchIdtData();
    }
  };

  // --- Care Team Management Modal Logic ---
  const showCareTeamModal = () => {
    setIsCareTeamModalVisible(true);
  };

  const handleCareTeamModalCancel = () => {
    setIsCareTeamModalVisible(false);
  };

  const handleCareTeamUpdate = () => {
    // Refresh IDT patient data when care team is updated
    if (addModalVisible || editModalVisible) {
      // Re-fetch IDT data to update the Patient Information section
      const fetchIdtPatientData = async () => {
        try {
          const patientRes = await axios.get(`/patients/${patientId}?populate=careTeam`);
          setIdtPatientData(patientRes.data.data.patient);
        } catch (err) {
          console.error("Error refreshing IDT patient data:", err);
        }
      };
      fetchIdtPatientData();
    }
  };

  // --- Order Management Modal Logic ---
  const showOrderModal = () => {
    setIsOrderModalVisible(true);
  };

  const handleOrderModalCancel = () => {
    setIsOrderModalVisible(false);
  };

  const handleOrdersUpdate = () => {
    // Refresh IDT orders data when orders are updated
    if (addModalVisible || editModalVisible) {
      // Re-fetch IDT data to update the Upcoming Diagnostics section
      const fetchIdtOrdersData = async () => {
        try {
          const ordersRes = await axios.get(`/patients/${patientId}/orders?status=Active&status=Pending`);
          // Filter orders to show only Active/Pending Laboratory and Radiology orders for diagnostics
          const filteredOrders = (ordersRes.data.data.orders || []).filter(order =>
            (order.status === 'Active' || order.status === 'Pending') &&
            (order.orderType === 'Laboratory' || order.orderType === 'Radiology')
          );
          setIdtOrders(filteredOrders);
        } catch (err) {
          console.error("Error refreshing IDT orders data:", err);
        }
      };
      fetchIdtOrdersData();
    }
  };

  // --- Flowsheet Modal Logic ---
  const showFlowsheetModal = () => {
    setIsFlowsheetModalVisible(true);
  };

  const handleFlowsheetModalCancel = () => {
    setIsFlowsheetModalVisible(false);
  };

  // --- Documents Modal Logic ---
  const showDocumentsModal = () => {
    setIsDocumentsModalVisible(true);
  };

  const handleDocumentsModalCancel = () => {
    setIsDocumentsModalVisible(false);
  };

  // --- Encounters Modal Logic ---
  const showEncountersModal = () => {
    setIsEncountersModalVisible(true);
  };

  const handleEncountersModalCancel = () => {
    setIsEncountersModalVisible(false);
  };



  // --- Conflict Resolution Functions ---
  const handleConflictResolution = useCallback(async (resolvedData, resolutionChoices, forceOverwrite = false) => {
    try {
      const payload = {
        ...resolvedData,
        version: currentEncounterVersion,
        forceOverwrite
      };

      const response = await axios.patch(
        `/encounters/${currentIdtEncounterId}/update-with-conflict-resolution`,
        payload
      );

      // Update the current version
      if (response.data.data.encounter.version) {
        setCurrentEncounterVersion(response.data.data.encounter.version);
      }

      // Update the form with the resolved data
      if (addModalVisible && selectedEncounterType === 'IDT Note') {
        addForm.setFieldsValue(resolvedData);
      } else if (editModalVisible && editingEncounter?.encounterType === 'IDT Note') {
        editForm.setFieldsValue(resolvedData);
      }

      // Close conflict modal and reset state
      setConflictModalVisible(false);
      setConflictData(null);
      setPendingSaveData(null);

      // Refresh encounters list to show updated data
      fetchEncounters();

      message.success('Conflicts resolved successfully!');
    } catch (error) {
      console.error('❌ Error resolving conflict:', error);
      message.error('Failed to resolve conflict. Please try again.');
    }
  }, [currentEncounterVersion, currentIdtEncounterId, addModalVisible, selectedEncounterType, editModalVisible, editingEncounter, addForm, editForm, fetchEncounters]);

  const handleConflictCancel = useCallback(() => {
    setConflictModalVisible(false);
    setConflictData(null);
    setPendingSaveData(null);
  }, []);

  // Function to load encounter version when editing
  const loadEncounterVersion = useCallback(async (encounterId) => {
    try {
      const response = await axios.get(`/encounters/${encounterId}`);
      const encounter = response.data.data.encounter;
      setCurrentEncounterVersion(encounter.version || 1);
    } catch (error) {
      console.error('❌ Error loading encounter version:', error);
      setCurrentEncounterVersion(1); // Default to version 1
    }
  }, []);

  // --- AI Scribe Modal Logic ---
  const showAIScribeModal = () => {
    setIsAIScribeModalVisible(true);
  };

  const handleAIScribeCancel = () => {
    setIsAIScribeModalVisible(false);
  };

  const handleAIScribeApprove = (scribeData) => {
    try {
      setAIScribeLoading(true);

      // Get the current form (add or edit)
      const currentForm = editingEncounter ? editForm : addForm;

      // Insert the formatted content into the plan field
      const { formattedContent } = scribeData;

      // Get existing content and append or replace
      const existingPlan = currentForm.getFieldValue('plan') || '';
      const newPlanContent = existingPlan ? `${existingPlan}<br/><br/>${formattedContent}` : formattedContent;

      currentForm.setFieldsValue({ plan: newPlanContent });

      setIsAIScribeModalVisible(false);
      message.success('AI Scribe content inserted successfully!');

    } catch (error) {
      console.error('Error inserting AI Scribe content:', error);
      message.error('Failed to insert AI Scribe content');
    } finally {
      setAIScribeLoading(false);
    }
  };

  // --- Comprehensive Physical Exam Modal Logic ---
  const showFullExamModal = () => {
    setIsFullExamModalVisible(true);
  };

  const handleFullExamCancel = () => {
    setIsFullExamModalVisible(false);
  };

  const handleSaveFullExam = async (findingsArray, htmlSummary) => {
    if (!patientId) {
      message.error('Patient ID is missing.');
      return;
    }
    // Determine if we are in Add or Edit mode to get the potential encounterId
    const currentEncounterId = editingEncounter?._id || null; // Use null if in Add mode

    setFullExamSubmitLoading(true);
    try {
      const payload = {
        findingsArray,
        htmlSummary,
        encounterId: currentEncounterId // Include encounterId if available (for linking, though saving is independent for now)
      };
      // Use the new endpoint
      await axios.post(`/patients/${patientId}/flowsheet/physical-exam-full`, payload);
      message.success('Comprehensive physical exam saved successfully!');
      setIsFullExamModalVisible(false);

      // Append the HTML summary to the Objective field of the active form
      const formToUpdate = addModalVisible ? addForm : editForm;
      if (formToUpdate) {
        const currentObjective = formToUpdate.getFieldValue('objective') || '';
        const updatedObjective = currentObjective ? `${currentObjective}<br/>${htmlSummary}` : htmlSummary;
        formToUpdate.setFieldsValue({ objective: updatedObjective });
      }

    } catch (err) {
      console.error("Error saving comprehensive physical exam:", err);
      message.error(err.response?.data?.message || 'Failed to save comprehensive exam.');
    } finally {
      setFullExamSubmitLoading(false);
    }
  };

  // --- Copy Previous Notes Logic ---
  const handleCopyPreviousNotes = () => {
    const form = addModalVisible ? addForm : editForm;
    const isEditing = !!editingEncounter;
    const currentEncounterType = form.getFieldValue('encounterType') || (editingEncounter?.encounterType);
    let previousEncounter = null;

    // Ensure encounters are sorted descending by date (newest first)
    const sortedEncounters = [...encounters].sort((a, b) => new Date(b.encounterDate) - new Date(a.encounterDate));

    // For IDT Notes, find the most recent IDT Note specifically
    if (currentEncounterType === 'IDT Note') {
      const idtEncounters = sortedEncounters.filter(enc => enc.encounterType === 'IDT Note');

      if (isEditing) {
        const currentIndex = idtEncounters.findIndex(enc => enc._id === editingEncounter._id);
        if (currentIndex !== -1 && currentIndex < idtEncounters.length - 1) {
          previousEncounter = idtEncounters[currentIndex + 1];
        }
      } else { // Adding new IDT Note
        if (idtEncounters.length > 0) {
          previousEncounter = idtEncounters[0];
        }
      }
    } else {
      // For regular encounters, use existing logic
      if (isEditing) {
        const currentIndex = sortedEncounters.findIndex(enc => enc._id === editingEncounter._id);
        if (currentIndex !== -1 && currentIndex < sortedEncounters.length - 1) {
          previousEncounter = sortedEncounters[currentIndex + 1];
        }
      } else { // Adding new encounter
        if (sortedEncounters.length > 0) {
          previousEncounter = sortedEncounters[0];
        }
      }
    }

    if (previousEncounter) {
      // Show confirmation dialog before copying
      const encounterTypeText = currentEncounterType === 'IDT Note' ? 'IDT Note' : 'encounter';
      const previousDate = moment(previousEncounter.encounterDate).format('MMM D, YYYY [at] h:mm A');
      const confirmMessage = `Copy Previous ${encounterTypeText}?\n\nThis will copy data from the previous ${encounterTypeText.toLowerCase()} dated ${previousDate} and overwrite any existing content in the current form.\n\nWarning: This action cannot be undone. Any unsaved changes will be lost.\n\nDo you want to continue?`;

      if (window.confirm(confirmMessage)) {
        performCopyOperation(previousEncounter, form, currentEncounterType);
      }
    } else {
      const encounterTypeText = currentEncounterType === 'IDT Note' ? 'IDT Note' : 'encounter';
      message.warning(`No previous ${encounterTypeText.toLowerCase()} found to copy data from.`);
    }
  };

  // Separate function to perform the actual copy operation
  const performCopyOperation = (previousEncounter, form, currentEncounterType) => {
    const targetFields = form.getFieldsValue();
    const fieldsToSet = {};

    if (currentEncounterType === 'IDT Note') {
      // Copy IDT-specific fields
      const idtFieldsToCopy = {
        idtActiveConcerns: previousEncounter.idtActiveConcerns || '',
        idtCurrentStatus: previousEncounter.idtCurrentStatus || '',
        idtActiveNotes: previousEncounter.idtActiveNotes || '',
        idtSmartGoalsProgress: previousEncounter.idtSmartGoalsProgress || '',
        idtNutritionStatusClassification: previousEncounter.idtNutritionStatusClassification || '',
        idtNutritionStatus: previousEncounter.idtNutritionStatus || '',
        idtPhysicalActivityStatusClassification: previousEncounter.idtPhysicalActivityStatusClassification || '',
        idtPhysicalActivityStatus: previousEncounter.idtPhysicalActivityStatus || '',
        idtRecoveryStatusClassification: previousEncounter.idtRecoveryStatusClassification || '',
        idtRecoveryStatus: previousEncounter.idtRecoveryStatus || '',
        idtEmotionStatusClassification: previousEncounter.idtEmotionStatusClassification || '',
        idtEmotionStatus: previousEncounter.idtEmotionStatus || '',
        idtEnvironmentalStatusClassification: previousEncounter.idtEnvironmentalStatusClassification || '',
        idtEnvironmentalStatus: previousEncounter.idtEnvironmentalStatus || '',
        idtPharmacyRecommendations: previousEncounter.idtPharmacyRecommendations || '',
        idtMdRecommendations: previousEncounter.idtMdRecommendations || '',
        idtAdditionalNotes: previousEncounter.idtAdditionalNotes || ''
      };

      // Copy IDT fields that exist in the target form
      Object.keys(idtFieldsToCopy).forEach(field => {
        if (targetFields.hasOwnProperty(field)) {
          fieldsToSet[field] = idtFieldsToCopy[field];
        }
      });
    } else {
      // Copy regular encounter fields
      const notesToCopy = {
        reasonForVisit: previousEncounter.reasonForVisit || '',
        subjective: previousEncounter.notes?.subjective || '',
        hpiRosCombined: previousEncounter.notes?.hpiRosCombined || '',
        intakeHistoryUpdates: previousEncounter.notes?.intakeHistoryUpdates || '',
        objective: previousEncounter.notes?.objective || '',
        assessmentPlan: previousEncounter.notes?.assessmentPlan || '',
        assessment: previousEncounter.notes?.assessment || '',
        plan: previousEncounter.notes?.plan || '',
      };

      // Copy regular fields that exist in the target form
      Object.keys(notesToCopy).forEach(field => {
        if (targetFields.hasOwnProperty(field)) {
          fieldsToSet[field] = notesToCopy[field];
        }
      });

      // Handle assessment/plan logic
      if (previousEncounter.notes?.assessmentPlan !== undefined && targetFields.hasOwnProperty('assessmentPlan')) {
        fieldsToSet.assessmentPlan = notesToCopy.assessmentPlan;
      } else {
        if (targetFields.hasOwnProperty('assessment')) {
          fieldsToSet.assessment = notesToCopy.assessment;
        }
        if (targetFields.hasOwnProperty('plan')) {
          fieldsToSet.plan = notesToCopy.plan;
        }
      }
    }

    if (Object.keys(fieldsToSet).length > 0) {
      form.setFieldsValue(fieldsToSet);
      const encounterTypeText = currentEncounterType === 'IDT Note' ? 'IDT Note' : 'encounter';
      message.success(`Previous ${encounterTypeText.toLowerCase()} data copied successfully!`);
    } else {
      message.info('No relevant fields found in the current form to populate.');
    }
  };

  // Determine if the copy button should be disabled
  const isCopyButtonDisabled = useMemo(() => {
    const isEditing = !!editingEncounter;
    const form = addModalVisible ? addForm : editForm;
    const currentEncounterType = form?.getFieldValue('encounterType') || editingEncounter?.encounterType;

    // Ensure encounters are sorted descending by date (newest first)
    const sortedEncounters = [...encounters].sort((a, b) => new Date(b.encounterDate) - new Date(a.encounterDate));

    // For IDT Notes, check specifically for previous IDT Notes
    if (currentEncounterType === 'IDT Note') {
      const idtEncounters = sortedEncounters.filter(enc => enc.encounterType === 'IDT Note');

      if (isEditing) {
        const currentIndex = idtEncounters.findIndex(enc => enc._id === editingEncounter._id);
        return currentIndex === -1 || currentIndex >= idtEncounters.length - 1; // Disabled if not found or is the oldest IDT Note
      } else { // Adding new IDT Note
        return idtEncounters.length === 0; // Disabled if no IDT Notes exist
      }
    } else {
      // For regular encounters, use existing logic
      if (isEditing) {
        const currentIndex = sortedEncounters.findIndex(enc => enc._id === editingEncounter._id);
        return currentIndex === -1 || currentIndex >= sortedEncounters.length - 1; // Disabled if not found or is the oldest
      } else { // Adding new
        return sortedEncounters.length === 0; // Disabled if no encounters exist
      }
    }
  }, [editingEncounter, encounters, addForm, editForm, addModalVisible]);


  return (
    <Card title="Encounters" style={{ marginTop: 24 }}>
      {/* Display fetch error */}
      {error && !addModalVisible && !editModalVisible && !isIntakeModalVisible && <Alert message={error} type="error" showIcon style={{ marginBottom: 16 }} />} {/* Check all modals */}

      <Space style={{ marginBottom: 16 }}>
        <Button type="primary" icon={<FileAddOutlined />} onClick={showAddModal}>
          Add New Encounter
        </Button>
        {canShowIntakeButton && (
          <Button icon={<SolutionOutlined />} onClick={showIntakeModal}>
            Intake
          </Button>
        )}
      </Space>

       {/* Add Encounter Modal */}
      <Modal
        title="Add New Encounter"
        visible={addModalVisible}
        onCancel={handleAddCancel}
        footer={null}
        destroyOnClose
        width={800} // Wider modal for notes
        maskClosable={false} // Prevent closing when clicking outside
        closable={true} // Keep the X button for intentional closing
      >
         {/* Display add error inside modal - TODO: Use specific error state if needed */}
         {/* {addError && <Alert message={addError} type="error" showIcon style={{ marginBottom: 16 }} />} */}
         <Spin spinning={addSubmitLoading}>
            <Form form={addForm} layout="vertical" onFinish={handleAddEncounter}>
              {/* Add Copy Previous Notes Button */}
              <Button
                icon={<CopyOutlined />}
                onClick={handleCopyPreviousNotes}
                disabled={isCopyButtonDisabled}
                style={{ marginBottom: 16 }}
              >
                Copy Previous Notes
              </Button>
               <Row gutter={16}>
                 <Col span={12}>
                    <Form.Item name="encounterDate" label="Encounter Date" rules={[{ required: true, message: 'Please select the encounter date!' }]}>
                      <MomentDatePicker showTime format="YYYY-MM-DD HH:mm" style={{ width: '100%' }}/>
                    </Form.Item>
                 </Col>
                  <Col span={12}>
                    <Form.Item name="encounterType" label="Encounter Type" rules={[{ required: true, message: 'Please select type!' }]}>
                      <Select placeholder="Select type" onChange={handleEncounterTypeChange}>
                         {/* Values from Encounter Model Enum */}
                         <Option value="MD Encounter">MD Encounter</Option>
                         <Option value="Progress Note">Progress Note</Option>
                         <Option value="Nurse Encounter">Nurse Encounter</Option>
                         <Option value="IDT Note">IDT Note</Option>
                         <Option value="Pharmacy Note">Pharmacy Note</Option>
                         <Option value="Lab Review">Lab Review</Option>
                         <Option value="Radiology Review">Radiology Review</Option>
                         <Option value="Procedure">Procedure</Option>
                         <Option value="Telehealth">Telehealth</Option>
                         <Option value="Comprehensive">Comprehensive</Option>
                         <Option value="Nurse SBAR Summary">Nurse SBAR Summary</Option>
                         <Option value="Other">Other</Option>
                      </Select>
                    </Form.Item>
                 </Col>
              </Row>
               {/* Hide Reason for Visit for SBAR and IDT encounters */}
               {selectedEncounterType !== 'Nurse SBAR Summary' && selectedEncounterType !== 'IDT Note' && (
                 <Form.Item name="reasonForVisit" label="Reason for Visit / Chief Complaint" rules={[{ required: true, message: 'Please enter the reason for visit!' }]}>
                   <Input />
                 </Form.Item>
               )}

               {/* Conditionally Render Note Fields based on Template */}
               {selectedEncounterType && (
                <Card size="small" title={selectedEncounterType === 'Progress Note' ? 'Progress Note' : selectedEncounterType === 'Nurse SBAR Summary' ? 'SBAR Summary' : selectedEncounterType === 'IDT Note' ? 'IDT Note' : `${selectedEncounterType} Notes`} style={{marginBottom: 16}}>
                    {/* Conditionally show Intake History Updates field - Also hide for Progress Note, SBAR, and IDT */}
                    {selectedEncounterType !== 'MD Encounter' && selectedEncounterType !== 'Progress Note' && selectedEncounterType !== 'Nurse SBAR Summary' && selectedEncounterType !== 'IDT Note' && (
                      <>
                        <Form.Item
                          name="intakeHistoryUpdates"
                          label="History Updates from Intake"
                        >
                          <RichTextEditor
                            value={addForm.getFieldValue('intakeHistoryUpdates')}
                            onChange={(html) => addForm.setFieldsValue({ intakeHistoryUpdates: html })}
                            placeholder="Updates noted during intake..."
                          />
                        </Form.Item>
                        <hr style={{margin: '16px 0'}}/> {/* Separator */}
                      </>
                    )}

                    {/* Specific Initial Visit Fields */}
                    {selectedEncounterType === 'MD Encounter' && (
                     <>
                       {/* Update label and name prop */}
                       <Form.Item name="hpiRosCombined" label="HPI & Review of Systems">
                         <RichTextEditor onChange={(html) => addForm.setFieldsValue({ hpiRosCombined: html })} />
                       </Form.Item>
                       {/* medicalHistory, surgicalHistory, familyHistory, socialHistory Removed */}
                        {/* Remove Review of Systems Form.Item */}
                        {/* --- Collapsible Read-Only Intake Summary --- */}
                        <Collapse ghost style={{ marginBottom: 16 }}>
                          <Collapse.Panel header="Current Record Summary (Read-Only)" key="1">
                            <ReadOnlyIntakeSummary patientId={patientId} />
                          </Collapse.Panel>
                        </Collapse>
                        {/* --- Render Clickable Vitals Display --- */}
                        <ClickableVitalsDisplay
                          vitalsData={latestVitalsData?.vitals}
                          allVitalsEntries={idtAllVitalsEntries}
                          isEncounterSigned={false} // New encounters are never signed
                        />
                        <Button
                          onClick={() => setIsUpdateVitalsModalVisible(true)}
                          style={{ marginBottom: 16 }}
                          disabled={addSubmitLoading} // Disable while loading latest vitals
                        >
                          Update Vitals for this Encounter
                        </Button>
                        <hr style={{margin: '16px 0'}}/> {/* Separator */}
                        {/* SOAP Note Title Removed */}
                     </>
                   )}

                   {/* Standard SOAP Fields (conditionally shown based on type) */}
                   {/* Hide Subjective for Initial Visit */}
                   {/* Removed 'Follow Up' from Subjective */}
                   {['Telehealth', 'Other'].includes(selectedEncounterType) && (
                     <Form.Item name="subjective" label="Subjective">
                       <RichTextEditor onChange={(html) => addForm.setFieldsValue({ subjective: html })} />
                     </Form.Item>
                   )}
                   {/* Physical Exam Buttons and Modals */}
                   {['MD Encounter'].includes(selectedEncounterType) && (
                     <Form.Item label="Physical Exam"> {/* Add label for clarity */}
                       <Space wrap> {/* Wrap buttons if needed */}
                         <Button
                           type="primary"
                           onClick={() => setShowPhysicalExamModal(true)}
                           // style={{marginBottom: 16}} // Removed margin, use Space gap
                         >
                           Khám Lâm Sàng
                         </Button>
                       </Space>
                       {/* Simple Exam Modal (Existing) */}
                       <PhysicalExamModal
                         visible={showPhysicalExamModal}
                         patientId={patientId}
                         encounterId={null} // No encounterId in Add mode
                         onCancel={() => setShowPhysicalExamModal(false)}
                         onSave={(examData) => {
                           const currentObjective = addForm.getFieldValue('objective') || '';
                           addForm.setFieldsValue({ objective: currentObjective + examData });
                           setShowPhysicalExamModal(false); // Close modal on save
                         }}
                       />
                       {/* Comprehensive Exam Modal (New) - Rendered outside Form.Item */}
                     </Form.Item>
                   )}

                   {/* Objective is shown for MD Encounter */}
                   {/* Removed 'Follow Up' from Objective */}
                   {['MD Encounter', 'Telehealth', 'Other', 'Nurse Encounter', 'Procedure'].includes(selectedEncounterType) && (
                     <Form.Item name="objective" label="Objective">
                       <RichTextEditor
                         value={editForm.getFieldValue('objective')}
                         onChange={(html) => editForm.setFieldsValue({ objective: html })} />
                     </Form.Item>
                   )}
                   {/* Removed 'Follow Up' from Assessment - Also hide for IDT Note */}
                   {['MD Encounter', 'Telehealth', 'Other', 'Pharmacy Note', 'Lab Review', 'Radiology Review'].includes(selectedEncounterType) && (
                     <>
                       <Form.Item label={selectedEncounterType === 'MD Encounter' ? 'Assessment & Plan' : 'Assessment'}>
                         <Space direction="vertical" style={{ width: '100%' }}>
                           <Space style={{ marginBottom: 8 }}>
                             <Button
                               type="default"
                               icon={<SolutionOutlined />}
                               onClick={showProblemListModal}
                             >
                               Manage Problem List
                             </Button>
                             <Button
                               type="default"
                               icon={<MedicineBoxOutlined />}
                               onClick={showMedicationModal}
                             >
                               Manage Medications
                             </Button>
                           </Space>
                           <Form.Item name="assessment" noStyle>
                             <RichTextEditor
                               onChange={(html) => addForm.setFieldsValue({ assessment: html })} />
                           </Form.Item>
                         </Space>
                       </Form.Item>
                     </>
                   )}
                    {/* AI Scribe Button for Progress Notes */}
                    {selectedEncounterType === 'Progress Note' && (
                      <div style={{ marginBottom: 16 }}>
                        <Button
                          type="primary"
                          icon={<AudioOutlined />}
                          onClick={showAIScribeModal}
                          style={{ marginBottom: 8 }}
                        >
                          AI Scribe
                        </Button>
                        <Text type="secondary" style={{ marginLeft: 8, fontSize: '12px' }}>
                          Record voice notes and convert to SOAP format
                        </Text>
                      </div>
                    )}

                    {/* Keep 'Progress Note' for Plan, remove label for Progress Note - Also hide for IDT Note and MD Encounter */}
                    {['Progress Note', 'Telehealth', 'Other', 'Nurse Encounter', 'Procedure', 'Pharmacy Note', 'Lab Review', 'Radiology Review'].includes(selectedEncounterType) && (
                     <Form.Item name="plan" label={selectedEncounterType === 'Progress Note' ? null : 'Plan'}>
                       <RichTextEditor
                        onChange={(html) => addForm.setFieldsValue({ plan: html })} />
                     </Form.Item>
                   )}

                   {/* SBAR Fields */}
                   {selectedEncounterType === 'Nurse SBAR Summary' && (
                     <>
                       <Form.Item name="sbarSituation" label="Situation">
                         <RichTextEditor
                           value={addForm.getFieldValue('sbarSituation')}
                           onChange={(html) => addForm.setFieldsValue({ sbarSituation: html })}
                         />
                       </Form.Item>
                       <Form.Item name="sbarAssessmentOutcome" label="Outcome of Assessment">
                         <RichTextEditor
                           value={addForm.getFieldValue('sbarAssessmentOutcome')}
                           onChange={(html) => addForm.setFieldsValue({ sbarAssessmentOutcome: html })}
                         />
                       </Form.Item>
                       <Form.Item name="sbarPcpFeedback" label="Primary Care Physician Feedback">
                         <RichTextEditor
                           value={addForm.getFieldValue('sbarPcpFeedback')}
                           onChange={(html) => addForm.setFieldsValue({ sbarPcpFeedback: html })}
                         />
                       </Form.Item>
                       <Form.Item name="sbarComment" label="Comment">
                         <RichTextEditor
                           value={addForm.getFieldValue('sbarComment')}
                           onChange={(html) => addForm.setFieldsValue({ sbarComment: html })}
                         />
                       </Form.Item>
                     </>
                   )}

                   {/* IDT Note Sections */}
                   {selectedEncounterType === 'IDT Note' && (
                     <Spin spinning={idtLoading}>
                       {/* Auto-save and collaboration status indicator */}
                       <div style={{
                         marginBottom: '16px',
                         display: 'flex',
                         justifyContent: 'space-between',
                         alignItems: 'center',
                         padding: '8px 12px',
                         backgroundColor: '#f8f9fa',
                         border: '1px solid #e9ecef',
                         borderRadius: '4px'
                       }}>
                         {/* Collaboration status */}
                         <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                           {collaboration.isConnected ? (
                             <>
                               <div style={{
                                 width: '8px',
                                 height: '8px',
                                 borderRadius: '50%',
                                 backgroundColor: '#52c41a',
                                 animation: 'pulse 2s infinite'
                               }} />
                               <Text type="success" style={{ fontSize: '12px', fontWeight: 'bold' }}>
                                 Live Collaboration Active
                               </Text>
                               {collaboration.activeUsers.length > 0 && (
                                 <Text type="secondary" style={{ fontSize: '11px' }}>
                                   ({collaboration.activeUsers.length} other user{collaboration.activeUsers.length !== 1 ? 's' : ''} online)
                                 </Text>
                               )}
                             </>
                           ) : (
                             <>
                               <div style={{
                                 width: '8px',
                                 height: '8px',
                                 borderRadius: '50%',
                                 backgroundColor: '#faad14'
                               }} />
                               <Text type="warning" style={{ fontSize: '12px', fontWeight: 'bold' }}>
                                 Collaboration Offline
                               </Text>
                             </>
                           )}
                         </div>


                       </div>
                       {idtPatientData ? (
                         <>
                           {/* Section 1: Patient Info */}
                           <Descriptions title="Patient Information" bordered size="small" column={2} style={{ marginBottom: 16 }}>
                             <Descriptions.Item label="Name">{`${idtPatientData.lastName}${idtPatientData.middleName ? ' ' + idtPatientData.middleName : ''} ${idtPatientData.firstName}`}</Descriptions.Item>
                             <Descriptions.Item label="Gender">{idtPatientData.sex}</Descriptions.Item>
                             <Descriptions.Item label="DOB">{moment(idtPatientData.dateOfBirth).format('DD/MM/YYYY')}</Descriptions.Item>
                             <Descriptions.Item label="Care 360 Start Date">{idtPatientData.care360StartDate ? moment(idtPatientData.care360StartDate).format('YYYY-MM-DD') : 'N/A'}</Descriptions.Item>
                             <Descriptions.Item label="Care Team" span={2}>
                               <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                 <span>
                                   {idtPatientData.careTeam?.length > 0 ? idtPatientData.careTeam.map(m => `${m.name} (${m.role})`).join(', ') : 'N/A'}
                                 </span>
                                 <Button
                                   type="default"
                                   size="small"
                                   icon={<TeamOutlined />}
                                   onClick={showCareTeamModal}
                                   style={{ marginLeft: 8 }}
                                 >
                                   Edit Care Team
                                 </Button>
                               </div>
                              </Descriptions.Item>
                             <Descriptions.Item label="Medical History" span={2}>
                               {formatMedicalHistoryForIDT(idtPatientData)}
                             </Descriptions.Item>
                             {/* Removed read-only Active Concerns display */}
                             <Descriptions.Item label="Problem List" span={2}>
                               {formatProblemsForIDT(idtProblems)}
                             </Descriptions.Item>
                            </Descriptions>



                            {/* View Flowsheet, Encounters, and Documents Buttons */}
                            <div style={{ marginTop: 16, marginBottom: 16, textAlign: 'center' }}>
                              <Space size="middle">
                                <Button
                                  type="default"
                                  icon={<TableOutlined />}
                                  onClick={showFlowsheetModal}
                                  size="default"
                                >
                                  View Flowsheet
                                </Button>
                                <Button
                                  type="default"
                                  icon={<FileTextOutlined />}
                                  onClick={showEncountersModal}
                                  size="default"
                                  title="View patient's encounter history"
                                  aria-label="View patient encounters"
                                >
                                  View Encounters
                                </Button>
                                <Button
                                  type="default"
                                  icon={<FolderOutlined />}
                                  onClick={showDocumentsModal}
                                  size="default"
                                >
                                  View Documents
                                </Button>
                              </Space>
                            </div>

                            {/* Added Active Concerns Rich Text Editor (Moved outside Descriptions) */}
                            <CollaborativeIdtField
                              name="idtActiveConcerns"
                              label="Active Concerns"
                              form={addForm}
                              collaboration={collaboration}
                              style={{ marginTop: 16 }}
                            />
                            {/* Moved Changes Since Last Review below Active Concerns */}
                            <Form.Item name="idtActiveNotes" label="Changes Since Last Review">
                              <RichTextEditor
                                value={addForm.getFieldValue('idtActiveNotes')}
                                onChange={(html) => addForm.setFieldsValue({ idtActiveNotes: html })}
                              />
                            </Form.Item>
                            {/* Removed extra closing </Descriptions.Item> and </Descriptions> */}

                            {/* Section 2: Current Status */}
                            <Typography.Title level={5} style={{ marginTop: 16 }}>Current Status</Typography.Title>

                            {/* Most Recent Vitals Section */}
                            <div style={{ marginTop: 16, marginBottom: 16 }}>
                              <Typography.Title level={5} style={{ marginBottom: 8 }}>Most Recent Vitals</Typography.Title>
                              {latestVitalsData ? (
                                <div style={{
                                  border: '1px solid #d9d9d9',
                                  borderRadius: '6px',
                                  padding: '12px',
                                  backgroundColor: '#fafafa'
                                }}>
                                  <ClickableVitalsDisplay
                                    vitalsData={latestVitalsData?.vitals}
                                    allVitalsEntries={idtAllVitalsEntries}
                                    isEncounterSigned={false}
                                  />
                                </div>
                              ) : (
                                <div style={{
                                  border: '1px solid #d9d9d9',
                                  borderRadius: '6px',
                                  padding: '12px',
                                  backgroundColor: '#fafafa',
                                  color: '#8c8c8c',
                                  fontStyle: 'italic'
                                }}>
                                  No recent vitals recorded
                                </div>
                              )}
                            </div>

                            {/* Abnormal Labs Section */}
                            <div style={{ marginTop: 16, marginBottom: 16 }}>
                              <Typography.Title level={5} style={{ marginBottom: 8 }}>Abnormal Labs</Typography.Title>
                              {idtAbnormalLabs && idtAbnormalLabs.length > 0 ? (
                                <div style={{
                                  border: '1px solid #d9d9d9',
                                  borderRadius: '6px',
                                  padding: '12px',
                                  backgroundColor: '#fafafa'
                                }}>
                                  <AbnormalLabsDisplay
                                    abnormalLabsData={idtAbnormalLabs}
                                    isEncounterSigned={false}
                                    allLabEntries={idtAllLabEntries}
                                  />
                                </div>
                              ) : (
                                <div style={{
                                  border: '1px solid #d9d9d9',
                                  borderRadius: '6px',
                                  padding: '12px',
                                  backgroundColor: '#fafafa',
                                  color: '#8c8c8c',
                                  fontStyle: 'italic'
                                }}>
                                  No recent abnormal lab values
                                </div>
                              )}
                            </div>

                            <Descriptions bordered size="small" column={1} style={{ marginBottom: 16 }}>
                              <Descriptions.Item label="Active Medications">
                                {formatMedicationsForIDT(idtMedications)}
                              </Descriptions.Item>
                              <Descriptions.Item label="Allergies">
                                {idtAllergies.length > 0 ? idtAllergies.map(a => a.allergen).join(', ') : 'None Recorded'}
                              </Descriptions.Item>
                              <Descriptions.Item label="Upcoming Diagnostics">
                                {idtOrders.length > 0 ? idtOrders.map(o => `${getOrderDisplayName(o)} (${o.status || 'Status Unknown'})`).join('; ') : 'None Recorded'}
                              </Descriptions.Item>
                            </Descriptions>
                            <hr style={{ margin: '16px 0' }} />

                            {/* Section 3: Progress */}
                           <Typography.Title level={5}>Progress</Typography.Title>

                           {/* Current SMART Goals Display */}
                           <div style={{ marginBottom: '16px' }}>
                             <Typography.Text strong style={{ fontSize: '14px', color: '#1890ff' }}>
                               Current SMART Goals:
                             </Typography.Text>
                             <div style={{
                               marginTop: '8px',
                               padding: '12px',
                               backgroundColor: '#fafafa',
                               border: '1px solid #d9d9d9',
                               borderRadius: '6px'
                             }}>
                               {formatSmartGoalsForIDT(idtSmartGoals, handleSmartGoalClick)}
                             </div>
                           </div>

                           <CollaborativeIdtField
                             name="idtSmartGoalsProgress"
                             label="SMART Goals Progress & Barriers (Free Text)"
                             form={addForm}
                             collaboration={collaboration}
                           />
                           <CollaborativeIdtField
                             name="idtNutritionStatus"
                             label={
                               <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
                                 <span>Nutrition Status</span>
                                 <Form.Item name="idtNutritionStatusClassification" style={{ margin: 0 }}>
                                   <Radio.Group
                                     size="small"
                                     value={addForm.getFieldValue('idtNutritionStatusClassification')}
                                     onChange={(e) => addForm.setFieldsValue({ idtNutritionStatusClassification: e.target.value })}
                                   >
                                     {NUTRITION_STATUS_OPTIONS.map(option => (
                                       <Radio.Button
                                         key={option.value}
                                         value={option.value}
                                         style={{
                                           borderColor: option.color,
                                           backgroundColor: 'transparent',
                                           color: option.color,
                                           fontWeight: 'bold',
                                           fontSize: '12px',
                                           transition: 'all 0.3s ease'
                                         }}
                                         className={`nutrition-status-radio-${option.value}`}
                                       >
                                         {option.label}
                                       </Radio.Button>
                                     ))}
                                   </Radio.Group>
                                 </Form.Item>
                               </div>
                             }
                             form={addForm}
                             collaboration={collaboration}
                           />
                           <CollaborativeIdtField
                             name="idtPhysicalActivityStatus"
                             label={
                               <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
                                 <span>Physical Activity Status</span>
                                 <Form.Item name="idtPhysicalActivityStatusClassification" style={{ margin: 0 }}>
                                   <Radio.Group
                                     size="small"
                                     value={addForm.getFieldValue('idtPhysicalActivityStatusClassification')}
                                     onChange={(e) => addForm.setFieldsValue({ idtPhysicalActivityStatusClassification: e.target.value })}
                                   >
                                     {STATUS_CLASSIFICATION_OPTIONS.map(option => (
                                       <Radio.Button
                                         key={option.value}
                                         value={option.value}
                                         style={{
                                           borderColor: option.color,
                                           backgroundColor: 'transparent',
                                           color: option.color,
                                           fontWeight: 'bold',
                                           fontSize: '12px',
                                           transition: 'all 0.3s ease'
                                         }}
                                         className={`physical-activity-status-radio-${option.value}`}
                                       >
                                         {option.label}
                                       </Radio.Button>
                                     ))}
                                   </Radio.Group>
                                 </Form.Item>
                               </div>
                             }
                             form={addForm}
                             collaboration={collaboration}
                           />
                           <CollaborativeIdtField
                             name="idtRecoveryStatus"
                             label={
                               <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
                                 <span>Recovery Status</span>
                                 <Form.Item name="idtRecoveryStatusClassification" style={{ margin: 0 }}>
                                   <Radio.Group
                                     size="small"
                                     value={addForm.getFieldValue('idtRecoveryStatusClassification')}
                                     onChange={(e) => addForm.setFieldsValue({ idtRecoveryStatusClassification: e.target.value })}
                                   >
                                     {STATUS_CLASSIFICATION_OPTIONS.map(option => (
                                       <Radio.Button
                                         key={option.value}
                                         value={option.value}
                                         style={{
                                           borderColor: option.color,
                                           backgroundColor: 'transparent',
                                           color: option.color,
                                           fontWeight: 'bold',
                                           fontSize: '12px',
                                           transition: 'all 0.3s ease'
                                         }}
                                         className={`recovery-status-radio-${option.value}`}
                                       >
                                         {option.label}
                                       </Radio.Button>
                                     ))}
                                   </Radio.Group>
                                 </Form.Item>
                               </div>
                             }
                             form={addForm}
                             collaboration={collaboration}
                           />
                          <CollaborativeIdtField
                            name="idtEmotionStatus"
                            label={
                              <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
                                <span>Emotion Status</span>
                                <Form.Item name="idtEmotionStatusClassification" style={{ margin: 0 }}>
                                  <Radio.Group
                                    size="small"
                                    value={addForm.getFieldValue('idtEmotionStatusClassification')}
                                    onChange={(e) => addForm.setFieldsValue({ idtEmotionStatusClassification: e.target.value })}
                                  >
                                    {STATUS_CLASSIFICATION_OPTIONS.map(option => (
                                      <Radio.Button
                                        key={option.value}
                                        value={option.value}
                                        style={{
                                          borderColor: option.color,
                                          backgroundColor: 'transparent',
                                          color: option.color,
                                          fontWeight: 'bold',
                                          fontSize: '12px',
                                          transition: 'all 0.3s ease'
                                        }}
                                        className={`emotion-status-radio-${option.value}`}
                                      >
                                        {option.label}
                                      </Radio.Button>
                                    ))}
                                  </Radio.Group>
                                </Form.Item>
                              </div>
                            }
                            form={addForm}
                            collaboration={collaboration}
                          />

                          <CollaborativeIdtField
                            name="idtEnvironmentalStatus"
                            label={
                              <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
                                <span>Environmental Status</span>
                                <Form.Item name="idtEnvironmentalStatusClassification" style={{ margin: 0 }}>
                                  <Radio.Group
                                    size="small"
                                    value={addForm.getFieldValue('idtEnvironmentalStatusClassification')}
                                    onChange={(e) => addForm.setFieldsValue({ idtEnvironmentalStatusClassification: e.target.value })}
                                  >
                                    {STATUS_CLASSIFICATION_OPTIONS.map(option => (
                                      <Radio.Button
                                        key={option.value}
                                        value={option.value}
                                        style={{
                                          borderColor: option.color,
                                          color: option.color,
                                          fontWeight: 'bold',
                                          fontSize: '12px',
                                          transition: 'all 0.3s ease'
                                        }}
                                        className={`environmental-status-radio-${option.value}`}
                                      >
                                        {option.label}
                                      </Radio.Button>
                                    ))}
                                  </Radio.Group>
                                </Form.Item>
                              </div>
                            }
                            form={addForm}
                            collaboration={collaboration}
                          />
                           <hr style={{ margin: '16px 0' }} />

                           {/* Section 4: Recommendations */}
                           <Typography.Title level={5}>Recommendations</Typography.Title>
                           <CollaborativeIdtField
                             name="idtPharmacyRecommendations"
                             label="Pharmacy Recommendations"
                             form={addForm}
                             collaboration={collaboration}
                           />
                           <Form.Item label="MD Recommendations">
                             <Space style={{ marginBottom: 8 }}>
                               <Button
                                 type="default"
                                 icon={<SolutionOutlined />}
                                 onClick={showProblemListModal}
                               >
                                 Manage Problem List
                               </Button>
                               <Button
                                 type="default"
                                 icon={<MedicineBoxOutlined />}
                                 onClick={showMedicationModal}
                               >
                                 Manage Medications
                               </Button>
                               <Button
                                 type="default"
                                 icon={<FileTextOutlined />}
                                 onClick={showOrderModal}
                               >
                                 Manage Orders
                               </Button>
                             </Space>
                             <CollaborativeIdtField
                               name="idtMdRecommendations"
                               label=""
                               form={addForm}
                               collaboration={collaboration}
                               style={{ marginBottom: 0 }}
                             />
                           </Form.Item>
                           <CollaborativeIdtField
                             name="idtAdditionalNotes"
                             label="Additional Notes"
                             form={addForm}
                             collaboration={collaboration}
                           />
                         </>
                       ) : (
                         <Alert message="Loading patient data..." type="info" />
                       )}
                     </Spin>
                   )}
                 </Card>
               )}

               {/* Vitals Input Card Removed */}

              <Form.Item>
                <Space> {/* Use Space to add gap between buttons */}
                  <Button
                    type="primary"
                    onClick={handleSaveForLater}
                    loading={addSubmitLoading}
                    disabled={!selectedEncounterType}
                  >
                    Save for Later
                  </Button>
                  {/* Add Sign and Save Button */}
                  {canUserSignEncounterType(user, selectedEncounterType) && ( // Conditionally render based on signing permission
                    <Button
                      type="primary"
                      loading={addSubmitLoading} // Use addSubmitLoading for consistency
                      onClick={handleSaveAndSignClick} // Call the new save and sign handler
                    >
                      Sign and Save
                    </Button>
                  )}
                  <Button style={{ marginLeft: 8 }} onClick={handleAddCancel}>
                    Cancel
                  </Button>
                </Space>
              </Form.Item>
            </Form>
         </Spin>
      </Modal>

      {/* Edit Encounter Modal */}
       <Modal
        title="Edit Encounter"
        visible={editModalVisible}
        onCancel={handleEditCancel}
        footer={null}
        destroyOnClose
        width={800}
        maskClosable={false} // Prevent closing when clicking outside
        closable={true} // Keep the X button for intentional closing
      >
         {/* TODO: Add specific error display for edit modal if needed */}
         <Spin spinning={editSubmitLoading}>
            {/* Disable the form if the encounter is signed */}
            <Form form={editForm} layout="vertical" onFinish={handleUpdateEncounter} disabled={editingEncounter?.isSigned}>
              {/* Add Copy Previous Notes Button */}
              <Button
                icon={<CopyOutlined />}
                onClick={handleCopyPreviousNotes}
                disabled={isCopyButtonDisabled}
                style={{ marginBottom: 16 }}
              >
                Copy Previous Notes
              </Button>
              {/* Fields mirror the Add modal, pre-filled by showEditModal */}
               <Row gutter={16}>
                <Col span={12}>
                    <Form.Item name="encounterDate" label="Encounter Date">
                      <MomentDatePicker showTime format="YYYY-MM-DD HH:mm" style={{ width: '100%' }}/>
                    </Form.Item>
                 </Col>
                  <Col span={12}>
                    <Form.Item name="encounterType" label="Encounter Type" rules={[{ required: true, message: 'Please select type!' }]}>
                      <Select placeholder="Select type" disabled> {/* Disable type change on edit? */}
                         <Option value="MD Encounter">MD Encounter</Option>
                         <Option value="Progress Note">Progress Note</Option>
                         <Option value="Nurse Encounter">Nurse Encounter</Option>
                         <Option value="IDT Note">IDT Note</Option>
                         <Option value="Pharmacy Note">Pharmacy Note</Option>
                         <Option value="Lab Review">Lab Review</Option>
                         <Option value="Radiology Review">Radiology Review</Option>
                         <Option value="Procedure">Procedure</Option>
                         <Option value="Telehealth">Telehealth</Option>
                         <Option value="Comprehensive">Comprehensive</Option>
                         <Option value="Nurse SBAR Summary">Nurse SBAR Summary</Option>
                         <Option value="Other">Other</Option>
                      </Select>
                    </Form.Item>
                 </Col>
              </Row>
               {/* Hide Reason for Visit for SBAR and IDT encounters */}
               {editingEncounter?.encounterType !== 'Nurse SBAR Summary' && editingEncounter?.encounterType !== 'IDT Note' && (
                 <Form.Item name="reasonForVisit" label="Reason for Visit / Chief Complaint">
                   <Input />
                 </Form.Item>
               )}
               {/* Conditionally Render Note Fields based on Encounter Type */}
               {editingEncounter?.encounterType && ( // Check if editing encounter and type exists
                 <Card size="small" title={editingEncounter.encounterType === 'Progress Note' ? 'Progress Note' : editingEncounter.encounterType === 'Nurse SBAR Summary' ? 'SBAR Summary' : editingEncounter.encounterType === 'IDT Note' ? 'IDT Note' : `${editingEncounter.encounterType} Notes`} style={{marginBottom: 16}}>
                    {/* Conditionally show Intake History Updates field - Also hide for Progress Note, SBAR, and IDT */}
                    {editingEncounter.encounterType !== 'MD Encounter' && editingEncounter.encounterType !== 'Progress Note' && editingEncounter.encounterType !== 'Nurse SBAR Summary' && editingEncounter.encounterType !== 'IDT Note' && (
                      <>
                        <Form.Item
                          name="intakeHistoryUpdates"
                          label="History Updates from Intake"
                        >
                          <RichTextEditor
                            value={editForm.getFieldValue('intakeHistoryUpdates')}
                            onChange={(html) => editForm.setFieldsValue({ intakeHistoryUpdates: html })}
                          />
                        </Form.Item>
                        <hr style={{margin: '16px 0'}}/> {/* Separator */}
                      </>
                    )}

                    {/* Specific Initial Visit Fields */}
                    {editingEncounter.encounterType === 'MD Encounter' && (
                     <>
                       {/* Update label and name prop */}
                       <Form.Item name="hpiRosCombined" label="HPI & Review of Systems">
                         <RichTextEditor
                            value={editForm.getFieldValue('hpiRosCombined')}
                            onChange={(html) => editForm.setFieldsValue({ hpiRosCombined: html })} />
                       </Form.Item>
                       {/* medicalHistory, surgicalHistory, familyHistory, socialHistory Removed */}
                        {/* Remove Review of Systems Form.Item */}
                        {/* --- Collapsible Read-Only Intake Summary --- */}
                        <Collapse ghost style={{ marginBottom: 16 }}>
                          <Collapse.Panel header="Current Record Summary (Read-Only)" key="1">
                            <ReadOnlyIntakeSummary patientId={patientId} />
                          </Collapse.Panel>
                        </Collapse>
                        {/* --- Render Clickable Vitals Display --- */}
                        <ClickableVitalsDisplay
                          vitalsData={latestVitalsData?.vitals}
                          allVitalsEntries={idtAllVitalsEntries}
                          isEncounterSigned={editingEncounter?.isSigned || false}
                        />
                        {!editingEncounter?.isSigned && (
                          <Button
                            onClick={() => setIsUpdateVitalsModalVisible(true)}
                            style={{ marginBottom: 16 }}
                            disabled={editSubmitLoading} // Disable while loading latest vitals
                          >
                            Update Vitals for this Encounter
                          </Button>
                        )}
                        {editingEncounter?.isSigned && (
                          <div style={{
                            marginBottom: 16,
                            padding: '8px 12px',
                            backgroundColor: '#f6ffed',
                            border: '1px solid #b7eb8f',
                            borderRadius: '6px'
                          }}>
                            <Text type="secondary" style={{ fontStyle: 'italic' }}>
                              Vitals are locked because this encounter is signed. These vitals are permanently associated with this encounter.
                            </Text>
                          </div>
                        )}
                        <hr style={{margin: '16px 0'}}/> {/* Separator */}
                        {/* SOAP Note Title Removed */}
                     </>
                   )}

                   {/* Standard SOAP Fields (conditionally shown based on type) */}
                   {/* Hide Subjective for Initial Visit */}
                   {/* Removed 'Follow Up' from Subjective */}
                   {['Telehealth', 'Other'].includes(editingEncounter.encounterType) && (
                     <Form.Item name="subjective" label="Subjective">
                       <RichTextEditor
                         value={editForm.getFieldValue('subjective')}
                         onChange={(html) => editForm.setFieldsValue({ subjective: html })} />
                     </Form.Item>
                   )}

                   {/* Physical Exam Buttons and Modals */}
                   {['MD Encounter'].includes(editingEncounter.encounterType) && (
                     <Form.Item label="Physical Exam"> {/* Add label for clarity */}
                       <Space wrap> {/* Wrap buttons */}
                         <Button
                           type="primary"
                           onClick={() => setShowPhysicalExamModal(true)}
                           // style={{marginBottom: 16}}
                         >
                           Khám Lâm Sàng
                         </Button>
                       </Space>
                       {/* Simple Exam Modal (Existing) */}
                       <PhysicalExamModal
                         visible={showPhysicalExamModal}
                         patientId={patientId}
                         encounterId={editingEncounter?._id} // Pass encounterId in Edit mode
                         onCancel={() => setShowPhysicalExamModal(false)}
                         onSave={(examData) => {
                           const currentObjective = editForm.getFieldValue('objective') || '';
                           editForm.setFieldsValue({ objective: currentObjective + examData });
                           setShowPhysicalExamModal(false); // Close modal on save
                         }}
                       />
                       {/* Comprehensive Exam Modal (New) - Rendered outside Form.Item */}
                     </Form.Item>
                   )}

                   {/* Objective is shown for MD Encounter */}
                   {/* Removed 'Follow Up' from Objective */}
                   {['MD Encounter', 'Telehealth', 'Other', 'Nurse Encounter', 'Procedure'].includes(editingEncounter.encounterType) && (
                     <Form.Item name="objective" label="Objective">
                       <RichTextEditor
                         value={editForm.getFieldValue('objective')}
                         onChange={(html) => editForm.setFieldsValue({ objective: html })} />
                     </Form.Item>
                   )}
                   {/* Removed 'Follow Up' from Assessment - Also hide for IDT Note */}
                   {['MD Encounter', 'Telehealth', 'Other', 'Pharmacy Note', 'Lab Review', 'Radiology Review'].includes(editingEncounter.encounterType) && (
                     <>
                       <Form.Item label={editingEncounter.encounterType === 'MD Encounter' ? 'Assessment & Plan' : 'Assessment'}>
                         <Space direction="vertical" style={{ width: '100%' }}>
                           <Space style={{ marginBottom: 8 }}>
                             <Button
                               type="default"
                               icon={<SolutionOutlined />}
                               onClick={showProblemListModal}
                             >
                               Manage Problem List
                             </Button>
                             <Button
                               type="default"
                               icon={<MedicineBoxOutlined />}
                               onClick={showMedicationModal}
                             >
                               Manage Medications
                             </Button>
                           </Space>
                           <Form.Item name="assessment" noStyle>
                             <RichTextEditor
                               value={editForm.getFieldValue('assessment')}
                               onChange={(html) => editForm.setFieldsValue({ assessment: html })} />
                           </Form.Item>
                         </Space>
                       </Form.Item>
                     </>
                   )}
                    {/* AI Scribe Button for Progress Notes in Edit Mode */}
                    {editingEncounter.encounterType === 'Progress Note' && (
                      <div style={{ marginBottom: 16 }}>
                        <Button
                          type="primary"
                          icon={<AudioOutlined />}
                          onClick={showAIScribeModal}
                          style={{ marginBottom: 8 }}
                        >
                          AI Scribe
                        </Button>
                        <Text type="secondary" style={{ marginLeft: 8, fontSize: '12px' }}>
                          Record voice notes and convert to SOAP format
                        </Text>
                      </div>
                    )}

                    {/* Keep 'Progress Note' for Plan, remove label for Progress Note - Also hide for IDT Note and MD Encounter */}
                    {['Progress Note', 'Telehealth', 'Other', 'Nurse Encounter', 'Procedure', 'Pharmacy Note', 'Lab Review', 'Radiology Review'].includes(editingEncounter.encounterType) && (
                     <Form.Item name="plan" label={editingEncounter.encounterType === 'Progress Note' ? null : 'Plan'}>
                       <RichTextEditor
                         value={editForm.getFieldValue('plan')}
                        onChange={(html) => editForm.setFieldsValue({ plan: html })} />
                     </Form.Item>
                   )}

                   {/* SBAR Fields */}
                   {editingEncounter?.encounterType === 'Nurse SBAR Summary' && (
                     <>
                       <Form.Item name="sbarSituation" label="Situation">
                         <RichTextEditor
                           value={editForm.getFieldValue('sbarSituation')}
                           onChange={(html) => editForm.setFieldsValue({ sbarSituation: html })}
                         />
                       </Form.Item>
                       <Form.Item name="sbarAssessmentOutcome" label="Outcome of Assessment">
                         <RichTextEditor
                           value={editForm.getFieldValue('sbarAssessmentOutcome')}
                           onChange={(html) => editForm.setFieldsValue({ sbarAssessmentOutcome: html })}
                         />
                       </Form.Item>
                       <Form.Item name="sbarPcpFeedback" label="Primary Care Physician Feedback">
                         <RichTextEditor
                           value={editForm.getFieldValue('sbarPcpFeedback')}
                           onChange={(html) => editForm.setFieldsValue({ sbarPcpFeedback: html })}
                         />
                       </Form.Item>
                       <Form.Item name="sbarComment" label="Comment">
                         <RichTextEditor
                           value={editForm.getFieldValue('sbarComment')}
                           onChange={(html) => editForm.setFieldsValue({ sbarComment: html })}
                         />
                       </Form.Item>
                     </>
                   )}

                   {/* IDT Note Sections */}
                   {editingEncounter?.encounterType === 'IDT Note' && (
                     <Spin spinning={idtLoading}>
                       {/* Auto-save and collaboration status indicator */}
                       <div style={{
                         marginBottom: '16px',
                         display: 'flex',
                         justifyContent: 'space-between',
                         alignItems: 'center',
                         padding: '8px 12px',
                         backgroundColor: '#f8f9fa',
                         border: '1px solid #e9ecef',
                         borderRadius: '4px'
                       }}>
                         {/* Collaboration status */}
                         <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                           {collaboration.isConnected ? (
                             <>
                               <div style={{
                                 width: '8px',
                                 height: '8px',
                                 borderRadius: '50%',
                                 backgroundColor: '#52c41a',
                                 animation: 'pulse 2s infinite'
                               }} />
                               <Text type="success" style={{ fontSize: '12px', fontWeight: 'bold' }}>
                                 Live Collaboration Active
                               </Text>
                               {collaboration.activeUsers.length > 0 && (
                                 <Text type="secondary" style={{ fontSize: '11px' }}>
                                   ({collaboration.activeUsers.length} other user{collaboration.activeUsers.length !== 1 ? 's' : ''} online)
                                 </Text>
                               )}
                             </>
                           ) : (
                             <>
                               <div style={{
                                 width: '8px',
                                 height: '8px',
                                 borderRadius: '50%',
                                 backgroundColor: '#faad14'
                               }} />
                               <Text type="warning" style={{ fontSize: '12px', fontWeight: 'bold' }}>
                                 Collaboration Offline
                               </Text>
                             </>
                           )}
                         </div>


                       </div>
                       {idtPatientData ? (
                         <>
                           {/* Section 1: Patient Info */}
                           <Descriptions title="Patient Information" bordered size="small" column={2} style={{ marginBottom: 16 }}>
                             <Descriptions.Item label="Name">{`${idtPatientData.lastName}${idtPatientData.middleName ? ' ' + idtPatientData.middleName : ''} ${idtPatientData.firstName}`}</Descriptions.Item>
                             <Descriptions.Item label="Gender">{idtPatientData.sex}</Descriptions.Item>
                             <Descriptions.Item label="DOB">{moment(idtPatientData.dateOfBirth).format('DD/MM/YYYY')}</Descriptions.Item>
                             <Descriptions.Item label="Care 360 Start Date">{idtPatientData.care360StartDate ? moment(idtPatientData.care360StartDate).format('YYYY-MM-DD') : 'N/A'}</Descriptions.Item>
                             <Descriptions.Item label="Care Team" span={2}>
                               <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                 <span>
                                   {idtPatientData.careTeam?.length > 0 ? idtPatientData.careTeam.map(m => `${m.name} (${m.role})`).join(', ') : 'N/A'}
                                 </span>
                                 <Button
                                   type="default"
                                   size="small"
                                   icon={<TeamOutlined />}
                                   onClick={showCareTeamModal}
                                   style={{ marginLeft: 8 }}
                                 >
                                   Edit Care Team
                                 </Button>
                               </div>
                              </Descriptions.Item>
                             <Descriptions.Item label="Medical History" span={2}>
                               {formatMedicalHistoryForIDT(idtPatientData)}
                             </Descriptions.Item>
                             {/* Removed read-only Active Concerns display */}
                             <Descriptions.Item label="Problem List" span={2}>
                               {formatProblemsForIDT(idtProblems)}
                             </Descriptions.Item>
                            </Descriptions>



                            {/* View Flowsheet, Encounters, and Documents Buttons */}
                            <div style={{ marginTop: 16, marginBottom: 16, textAlign: 'center' }}>
                              <Space size="middle">
                                <Button
                                  type="default"
                                  icon={<TableOutlined />}
                                  onClick={showFlowsheetModal}
                                  size="default"
                                >
                                  View Flowsheet
                                </Button>
                                <Button
                                  type="default"
                                  icon={<FileTextOutlined />}
                                  onClick={showEncountersModal}
                                  size="default"
                                  title="View patient's encounter history"
                                  aria-label="View patient encounters"
                                >
                                  View Encounters
                                </Button>
                                <Button
                                  type="default"
                                  icon={<FolderOutlined />}
                                  onClick={showDocumentsModal}
                                  size="default"
                                >
                                  View Documents
                                </Button>
                              </Space>
                            </div>

                            {/* Added Active Concerns Rich Text Editor (Moved outside Descriptions) */}
                            <CollaborativeIdtField
                              name="idtActiveConcerns"
                              label="Active Concerns"
                              form={editForm}
                              collaboration={collaboration}
                              style={{ marginTop: 16 }}
                            />
                            {/* Moved Changes Since Last Review below Active Concerns */}
                            <CollaborativeIdtField
                              name="idtActiveNotes"
                              label="Changes Since Last Review"
                              form={editForm}
                              collaboration={collaboration}
                            />
                            {/* Removed extra closing </Descriptions.Item> and </Descriptions> */}

                            {/* Section 2: Current Status */}
                            <Typography.Title level={5} style={{ marginTop: 16 }}>Current Status</Typography.Title>

                            {/* Most Recent Vitals Section */}
                            <div style={{ marginTop: 16, marginBottom: 16 }}>
                              <Typography.Title level={5} style={{ marginBottom: 8 }}>Most Recent Vitals</Typography.Title>
                              {latestVitalsData ? (
                                <div style={{
                                  border: '1px solid #d9d9d9',
                                  borderRadius: '6px',
                                  padding: '12px',
                                  backgroundColor: '#fafafa'
                                }}>
                                  <ClickableVitalsDisplay
                                    vitalsData={latestVitalsData?.vitals}
                                    allVitalsEntries={idtAllVitalsEntries}
                                    isEncounterSigned={editingEncounter?.isSigned || false}
                                  />
                                </div>
                              ) : (
                                <div style={{
                                  border: '1px solid #d9d9d9',
                                  borderRadius: '6px',
                                  padding: '12px',
                                  backgroundColor: '#fafafa',
                                  color: '#8c8c8c',
                                  fontStyle: 'italic'
                                }}>
                                  No recent vitals recorded
                                </div>
                              )}
                            </div>

                            {/* Abnormal Labs Section */}
                            <div style={{ marginTop: 16, marginBottom: 16 }}>
                              <Typography.Title level={5} style={{ marginBottom: 8 }}>Abnormal Labs</Typography.Title>
                              {idtAbnormalLabs && idtAbnormalLabs.length > 0 ? (
                                <div style={{
                                  border: '1px solid #d9d9d9',
                                  borderRadius: '6px',
                                  padding: '12px',
                                  backgroundColor: '#fafafa'
                                }}>
                                  <AbnormalLabsDisplay
                                    abnormalLabsData={idtAbnormalLabs}
                                    isEncounterSigned={editingEncounter?.isSigned || false}
                                    allLabEntries={idtAllLabEntries}
                                  />
                                </div>
                              ) : (
                                <div style={{
                                  border: '1px solid #d9d9d9',
                                  borderRadius: '6px',
                                  padding: '12px',
                                  backgroundColor: '#fafafa',
                                  color: '#8c8c8c',
                                  fontStyle: 'italic'
                                }}>
                                  No recent abnormal lab values
                                </div>
                              )}
                            </div>

                            <Descriptions bordered size="small" column={1} style={{ marginBottom: 16 }}>
                              <Descriptions.Item label="Active Medications">
                                {formatMedicationsForIDT(idtMedications)}
                              </Descriptions.Item>
                              <Descriptions.Item label="Allergies">
                                {idtAllergies.length > 0 ? idtAllergies.map(a => a.allergen).join(', ') : 'None Recorded'}
                              </Descriptions.Item>
                              <Descriptions.Item label="Upcoming Diagnostics">
                                {idtOrders.length > 0 ? idtOrders.map(o => `${getOrderDisplayName(o)} (${o.status || 'Status Unknown'})`).join('; ') : 'None Recorded'}
                              </Descriptions.Item>
                            </Descriptions>
                            <hr style={{ margin: '16px 0' }} />

                            {/* Section 3: Progress */}
                           <Typography.Title level={5}>Progress</Typography.Title>

                           {/* Current SMART Goals Display */}
                           <div style={{ marginBottom: '16px' }}>
                             <Typography.Text strong style={{ fontSize: '14px', color: '#1890ff' }}>
                               Current SMART Goals:
                             </Typography.Text>
                             <div style={{
                               marginTop: '8px',
                               padding: '12px',
                               backgroundColor: '#fafafa',
                               border: '1px solid #d9d9d9',
                               borderRadius: '6px'
                             }}>
                               {formatSmartGoalsForIDT(idtSmartGoals, handleSmartGoalClick)}
                             </div>
                           </div>

                           <CollaborativeIdtField
                             name="idtSmartGoalsProgress"
                             label="SMART Goals Progress & Barriers (Free Text)"
                             form={editForm}
                             collaboration={collaboration}
                           />
                           <Form.Item
                             name="idtNutritionStatus"
                             label={
                               <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
                                 <span>Nutrition Status</span>
                                 <Form.Item name="idtNutritionStatusClassification" style={{ margin: 0 }}>
                                   <Radio.Group
                                     size="small"
                                     value={editForm.getFieldValue('idtNutritionStatusClassification')}
                                     onChange={(e) => editForm.setFieldsValue({ idtNutritionStatusClassification: e.target.value })}
                                   >
                                     {NUTRITION_STATUS_OPTIONS.map(option => (
                                       <Radio.Button
                                         key={option.value}
                                         value={option.value}
                                         style={{
                                           borderColor: option.color,
                                           backgroundColor: 'transparent',
                                           color: option.color,
                                           fontWeight: 'bold',
                                           fontSize: '12px',
                                           transition: 'all 0.3s ease'
                                         }}
                                         className={`nutrition-status-radio-${option.value}`}
                                       >
                                         {option.label}
                                       </Radio.Button>
                                     ))}
                                   </Radio.Group>
                                 </Form.Item>
                               </div>
                             }
                           >
                             <CollaborativeIdtField
                               name="idtNutritionStatus"
                               form={editForm}
                               collaboration={collaboration}
                               hideLabel={true}
                             />
                           </Form.Item>
                           <Form.Item
                             name="idtPhysicalActivityStatus"
                             label={
                               <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
                                 <span>Physical Activity Status</span>
                                 <Form.Item name="idtPhysicalActivityStatusClassification" style={{ margin: 0 }}>
                                   <Radio.Group
                                     size="small"
                                     value={editForm.getFieldValue('idtPhysicalActivityStatusClassification')}
                                     onChange={(e) => editForm.setFieldsValue({ idtPhysicalActivityStatusClassification: e.target.value })}
                                   >
                                     {STATUS_CLASSIFICATION_OPTIONS.map(option => (
                                       <Radio.Button
                                         key={option.value}
                                         value={option.value}
                                         style={{
                                           borderColor: option.color,
                                           backgroundColor: 'transparent',
                                           color: option.color,
                                           fontWeight: 'bold',
                                           fontSize: '12px',
                                           transition: 'all 0.3s ease'
                                         }}
                                         className={`physical-activity-status-radio-${option.value}`}
                                       >
                                         {option.label}
                                       </Radio.Button>
                                     ))}
                                   </Radio.Group>
                                 </Form.Item>
                               </div>
                             }
                           >
                             <CollaborativeIdtField
                               name="idtPhysicalActivityStatus"
                               form={editForm}
                               collaboration={collaboration}
                               hideLabel={true}
                             />
                           </Form.Item>
                           <Form.Item
                             name="idtRecoveryStatus"
                             label={
                               <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
                                 <span>Recovery Status</span>
                                 <Form.Item name="idtRecoveryStatusClassification" style={{ margin: 0 }}>
                                   <Radio.Group
                                     size="small"
                                     value={editForm.getFieldValue('idtRecoveryStatusClassification')}
                                     onChange={(e) => editForm.setFieldsValue({ idtRecoveryStatusClassification: e.target.value })}
                                   >
                                     {STATUS_CLASSIFICATION_OPTIONS.map(option => (
                                       <Radio.Button
                                         key={option.value}
                                         value={option.value}
                                         style={{
                                           borderColor: option.color,
                                           backgroundColor: 'transparent',
                                           color: option.color,
                                           fontWeight: 'bold',
                                           fontSize: '12px',
                                           transition: 'all 0.3s ease'
                                         }}
                                         className={`recovery-status-radio-${option.value}`}
                                       >
                                         {option.label}
                                       </Radio.Button>
                                     ))}
                                   </Radio.Group>
                                 </Form.Item>
                               </div>
                             }
                           >
                             <CollaborativeIdtField
                               name="idtRecoveryStatus"
                               form={editForm}
                               collaboration={collaboration}
                               hideLabel={true}
                             />
                           </Form.Item>
                           <Form.Item
                             name="idtEmotionStatus"
                             label={
                               <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
                                 <span>Emotion Status</span>
                                 <Form.Item name="idtEmotionStatusClassification" style={{ margin: 0 }}>
                                   <Radio.Group
                                     size="small"
                                     value={editForm.getFieldValue('idtEmotionStatusClassification')}
                                     onChange={(e) => editForm.setFieldsValue({ idtEmotionStatusClassification: e.target.value })}
                                   >
                                     {STATUS_CLASSIFICATION_OPTIONS.map(option => (
                                       <Radio.Button
                                         key={option.value}
                                         value={option.value}
                                         style={{
                                           borderColor: option.color,
                                           backgroundColor: 'transparent',
                                           color: option.color,
                                           fontWeight: 'bold',
                                           fontSize: '12px',
                                           transition: 'all 0.3s ease'
                                         }}
                                         className={`emotion-status-radio-${option.value}`}
                                       >
                                         {option.label}
                                       </Radio.Button>
                                     ))}
                                   </Radio.Group>
                                 </Form.Item>
                               </div>
                             }
                           >
                             <CollaborativeIdtField
                               name="idtEmotionStatus"
                               form={editForm}
                               collaboration={collaboration}
                               hideLabel={true}
                             />
                           </Form.Item>
                           <Form.Item
                             name="idtEnvironmentalStatus"
                             label={
                               <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
                                 <span>Environmental Status</span>
                                 <Form.Item name="idtEnvironmentalStatusClassification" style={{ margin: 0 }}>
                                   <Radio.Group
                                     size="small"
                                     value={editForm.getFieldValue('idtEnvironmentalStatusClassification')}
                                     onChange={(e) => editForm.setFieldsValue({ idtEnvironmentalStatusClassification: e.target.value })}
                                   >
                                     {STATUS_CLASSIFICATION_OPTIONS.map(option => (
                                       <Radio.Button
                                         key={option.value}
                                         value={option.value}
                                         style={{
                                           borderColor: option.color,
                                           backgroundColor: 'transparent',
                                           color: option.color,
                                           fontWeight: 'bold',
                                           fontSize: '12px',
                                           transition: 'all 0.3s ease'
                                         }}
                                         className={`environmental-status-radio-${option.value}`}
                                       >
                                         {option.label}
                                       </Radio.Button>
                                     ))}
                                   </Radio.Group>
                                 </Form.Item>
                               </div>
                             }
                           >
                             <CollaborativeIdtField
                               name="idtEnvironmentalStatus"
                               form={editForm}
                               collaboration={collaboration}
                               hideLabel={true}
                             />
                           </Form.Item>
                           <hr style={{ margin: '16px 0' }} />

                           {/* Section 4: Recommendations */}
                           <Typography.Title level={5}>Recommendations</Typography.Title>
                           <CollaborativeIdtField
                             name="idtPharmacyRecommendations"
                             label="Pharmacy Recommendations"
                             form={editForm}
                             collaboration={collaboration}
                           />
                           <Form.Item label="MD Recommendations">
                             <Space style={{ marginBottom: 8 }}>
                               <Button
                                 type="default"
                                 icon={<SolutionOutlined />}
                                 onClick={showProblemListModal}
                               >
                                 Manage Problem List
                               </Button>
                               <Button
                                 type="default"
                                 icon={<MedicineBoxOutlined />}
                                 onClick={showMedicationModal}
                               >
                                 Manage Medications
                               </Button>
                               <Button
                                 type="default"
                                 icon={<FileTextOutlined />}
                                 onClick={showOrderModal}
                               >
                                 Manage Orders
                               </Button>
                             </Space>
                             <CollaborativeIdtField
                               name="idtMdRecommendations"
                               form={editForm}
                               collaboration={collaboration}
                               hideLabel={true}
                             />
                           </Form.Item>
                           <CollaborativeIdtField
                             name="idtAdditionalNotes"
                             label="Additional Notes"
                             form={editForm}
                             collaboration={collaboration}
                           />
                         </>
                       ) : (
                         <Alert message="Loading patient data..." type="info" />
                       )}
                     </Spin>
                   )}
                 </Card>
               )}
               {/* Display Addendums if the encounter is signed and has addendums */}
               {editingEncounter?.isSigned && editingEncounter?.addendums?.length > 0 && (
                 <Card size="small" title="Addendums" style={{marginBottom: 16}}>
                   {editingEncounter.addendums.map((addendum, index) => (
                     <div key={index} style={{ marginBottom: 12, paddingBottom: 12, borderBottom: '1px solid #f0f0f0' }}>
                       <Typography.Text strong>{addendum.createdBy?.name || 'Unknown User'} on {new Date(addendum.createdAt).toLocaleString()}:</Typography.Text>
                       <div dangerouslySetInnerHTML={{ __html: addendum.content }} />
                     </div>
                   ))}
                 </Card>
               )}
               {/* Vitals Input Card Removed */}
              <Form.Item>
                <Space> {/* Use Space to add gap between buttons */}
                  {/* Disable the submit button if the encounter is signed */}
                  <Button type="primary" htmlType="submit" loading={editSubmitLoading} disabled={editingEncounter?.isSigned}>
                    Save Changes
                  </Button>
                  {/* Add Sign Button */}
                  {!editingEncounter?.isSigned && canUserSignEncounterType(user, editingEncounter?.encounterType) && ( // Conditionally render based on signed status and signing permission
                     <Button
                       type="primary"
                       loading={editSubmitLoading}
                       onClick={() => promptForPasswordAndSign(editingEncounter._id)} // Call the password prompt handler
                     >
                       Sign Encounter
                     </Button>
                   )}
                  <Button style={{ marginLeft: 8 }} onClick={handleEditCancel}>
                    Cancel
                  </Button>
                </Space>
              </Form.Item>
            </Form>
         </Spin>
      </Modal>

      {/* View Encounter Modal */}
      <Modal
        title={`Encounter Details: ${viewingEncounter?.encounterType || ''} on ${viewingEncounter?.encounterDate ? new Date(viewingEncounter.encounterDate).toLocaleString() : ''}`}
        visible={isViewModalVisible}
        onCancel={handleViewCancel}
        footer={null}
        destroyOnClose
        width={800}
        maskClosable={false} // Prevent closing when clicking outside
        closable={true} // Keep the X button for intentional closing
      >
         <Spin spinning={loading}> {/* Use general loading state or add a specific one if needed */}
          {viewingEncounter && (
            <> {/* Use Fragment to wrap Alert and Card */}
              {/* Display Strike Out Information if applicable */}
              {viewingEncounter.isStricken && (
                <Alert
                  message="Encounter Stricken Out"
                  description={
                    <>
                      <p><strong>Reason:</strong> {viewingEncounter.strikeReason || 'N/A'}</p>
                      <p>
                        <strong>By:</strong> {viewingEncounter.strickenBy?.name || 'Unknown User'}
                        {' on '}
                        {viewingEncounter.strickenAt ? new Date(viewingEncounter.strickenAt).toLocaleString() : 'N/A'}
                      </p>
                    </>
                  }
                  type="error"
                  showIcon
                  style={{ marginBottom: 16 }} // Removed line-through style from Alert itself
                />
              )}
              {/* Wrap content that should be stricken in a div */}
              <div style={{ textDecoration: viewingEncounter.isStricken ? 'line-through' : 'none' }}>
                <Card size="small" title="Details" style={{ marginBottom: 16 }}>
                  {/* Removed style from Card */}
                  <p><strong>Type:</strong> {viewingEncounter.encounterType}</p>
                  <p><strong>Date:</strong> {viewingEncounter.encounterDate ? new Date(viewingEncounter.encounterDate).toLocaleString() : 'N/A'}</p>
                  <p><strong>Provider:</strong> {viewingEncounter.provider ? `${viewingEncounter.provider.name} (${viewingEncounter.provider.role})` : 'N/A'}</p>
                  {viewingEncounter.reasonForVisit && <p><strong>Reason for Visit:</strong> {viewingEncounter.reasonForVisit}</p>}

                  {/* --- Cline: Add ReadOnlyIntakeSummary --- */}
                  {viewingEncounter.encounterType === 'MD Encounter' && ( // Only show for MD Encounters for now, adjust if needed
                    <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid #f0f0f0' }}>
                       <Typography.Title level={5}>Current Record Summary:</Typography.Title>
                       <ReadOnlyIntakeSummary patientId={patientId} />
                    </div>
                  )}
                  {/* --- End Cline: Add ReadOnlyIntakeSummary --- */}







                  {/* Display Notes */}
                  {viewingEncounter.notes && (
                    <div style={{ marginTop: 16 }}>
                      <Typography.Title level={5}>Notes:</Typography.Title>
                      {viewingEncounter.notes.hpiRosCombined && (
                        <>
                          <Typography.Text strong>HPI & Review of Systems:</Typography.Text>
                          <RichTextDisplay content={viewingEncounter.notes.hpiRosCombined} />
                        </>
                      )}
                      {viewingEncounter.notes.intakeHistoryUpdates && (
                        <>
                          <Typography.Text strong>History Updates from Intake:</Typography.Text>
                          <RichTextDisplay content={viewingEncounter.notes.intakeHistoryUpdates} />
                        </>
                      )}
                      {viewingEncounter.notes.subjective && (
                        <>
                          <Typography.Text strong>Subjective:</Typography.Text>
                          <RichTextDisplay content={viewingEncounter.notes.subjective} />
                        </>
                      )}
                      {viewingEncounter.notes.objective && (
                        <>
                          <Typography.Text strong>Objective:</Typography.Text>
                          <RichTextDisplay content={viewingEncounter.notes.objective} />
                        </>
                      )}
                      {viewingEncounter.notes.assessment && (
                        <>
                          <Typography.Text strong>Assessment:</Typography.Text>
                          <RichTextDisplay content={viewingEncounter.notes.assessment} />
                        </>
                      )}
                      {viewingEncounter.notes.plan && (
                        <>
                          <Typography.Text strong>Plan:</Typography.Text>
                          <RichTextDisplay content={viewingEncounter.notes.plan} />
                        </>
                      )}
                    </div>
                  )}

                  {/* Display SBAR Fields */}
                  {viewingEncounter.encounterType === 'Nurse SBAR Summary' && (
                    <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid #f0f0f0' }}>
                      <Typography.Title level={5}>SBAR Summary:</Typography.Title>
                      {viewingEncounter.sbarSituation && (
                        <>
                          <Typography.Text strong>Situation:</Typography.Text>
                          <RichTextDisplay content={viewingEncounter.sbarSituation} />
                        </>
                      )}
                      {viewingEncounter.sbarAssessmentOutcome && (
                        <>
                          <Typography.Text strong>Outcome of Assessment:</Typography.Text>
                          <RichTextDisplay content={viewingEncounter.sbarAssessmentOutcome} />
                        </>
                      )}
                      {viewingEncounter.sbarPcpFeedback && (
                        <>
                          <Typography.Text strong>Primary Care Physician Feedback:</Typography.Text>
                          <RichTextDisplay content={viewingEncounter.sbarPcpFeedback} />
                        </>
                      )}
                      {viewingEncounter.sbarComment && (
                        <>
                          <Typography.Text strong>Comment:</Typography.Text>
                          <RichTextDisplay content={viewingEncounter.sbarComment} />
                        </>
                      )}
                    </div>
                  )}

                  {/* Display IDT Note Fields */}
                  {viewingEncounter.encounterType === 'IDT Note' && (
                    <Spin spinning={idtLoading}> {/* Reuse idtLoading state for view modal fetch */}
                      {idtPatientData ? ( // Check if data is loaded
                        <>
                          {/* Section 1: Patient Info */}
                          <Descriptions title="Patient Information" bordered size="small" column={2} style={{ marginBottom: 16, marginTop: 16 }}>
                            <Descriptions.Item label="Name">{`${idtPatientData.lastName}${idtPatientData.middleName ? ' ' + idtPatientData.middleName : ''} ${idtPatientData.firstName}`}</Descriptions.Item>
                            <Descriptions.Item label="Gender">{idtPatientData.sex}</Descriptions.Item>
                            <Descriptions.Item label="DOB">{moment(idtPatientData.dateOfBirth).format('DD/MM/YYYY')}</Descriptions.Item>
                            <Descriptions.Item label="Care 360 Start Date">{idtPatientData.care360StartDate ? moment(idtPatientData.care360StartDate).format('YYYY-MM-DD') : 'N/A'}</Descriptions.Item>
                            <Descriptions.Item label="Care Team" span={2}>
                              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <span>
                                  {idtPatientData.careTeam?.length > 0 ? idtPatientData.careTeam.map(m => `${m.name} (${m.role})`).join(', ') : 'N/A'}
                                </span>
                                <Button
                                  type="default"
                                  size="small"
                                  icon={<TeamOutlined />}
                                  onClick={showCareTeamModal}
                                  style={{ marginLeft: 8 }}
                                >
                                  Edit Care Team
                                </Button>
                              </div>
                            </Descriptions.Item>
                            <Descriptions.Item label="Medical History" span={2}>
                              {formatMedicalHistoryForIDT(idtPatientData)}
                            </Descriptions.Item>
                            {/* Removed read-only Active Concerns display */}
                            <Descriptions.Item label="Problem List" span={2}>
                              {viewingEncounter.isSigned && problemListSnapshotData
                                ? formatProblemsForIDT(problemListSnapshotData.problemList, true)
                                : formatProblemsForIDT(idtProblems)}
                            </Descriptions.Item>
                          </Descriptions>



                          {/* Display saved Active Concerns */}
                          {viewingEncounter.idtActiveConcerns && (
                            <div style={{ marginTop: 16 }}>
                              <Typography.Text strong>Active Concerns:</Typography.Text>
                              <RichTextDisplay content={viewingEncounter.idtActiveConcerns} style={{ marginTop: 8 }} />
                            </div>
                          )}
                          {/* Moved Changes Since Last Review below Active Concerns */}
                          {viewingEncounter.idtActiveNotes && (
                            <div style={{ marginTop: 16 }}>
                              <Typography.Text strong>Changes Since Last Review:</Typography.Text>
                              <RichTextDisplay content={viewingEncounter.idtActiveNotes} style={{ marginTop: 8 }} />
                            </div>
                          )}
                          {/* Removed extra closing </Descriptions.Item> and </Descriptions> */}

                          {/* Section 2: Current Status */}
                          <Typography.Title level={5} style={{ marginTop: 16 }}>Current Status</Typography.Title>

                          {/* Most Recent Vitals Section */}
                          <div style={{ marginTop: 16, marginBottom: 16 }}>
                            <Typography.Title level={5} style={{ marginBottom: 8 }}>Most Recent Vitals</Typography.Title>
                            {latestVitalsData ? (
                              <div style={{
                                border: latestVitalsData.isSnapshot ? '2px solid #722ed1' : '1px solid #d9d9d9',
                                borderRadius: '6px',
                                padding: '12px',
                                backgroundColor: latestVitalsData.isSnapshot ? '#f9f0ff' : '#fafafa'
                              }}>
                                {latestVitalsData.isSnapshot ? (
                                  <VitalsSnapshotDisplay
                                    vitalsSnapshot={latestVitalsData.vitals}
                                    signedAt={viewingEncounter.signedAt}
                                    source={latestVitalsData.source}
                                  />
                                ) : (
                                  <ClickableVitalsDisplay
                                    vitalsData={latestVitalsData?.vitals}
                                    allVitalsEntries={idtAllVitalsEntries}
                                    isEncounterSigned={viewingEncounter.isSigned || false}
                                  />
                                )}
                              </div>
                            ) : (
                              <div style={{
                                border: '1px solid #d9d9d9',
                                borderRadius: '6px',
                                padding: '12px',
                                backgroundColor: '#fafafa',
                                color: '#8c8c8c',
                                fontStyle: 'italic'
                              }}>
                                {viewingEncounter.isSigned ? 'No vitals data in snapshot' : 'No recent vitals recorded'}
                              </div>
                            )}
                          </div>

                          {/* Abnormal Labs Section */}
                          <div style={{ marginTop: 16, marginBottom: 16 }}>
                            <Typography.Title level={5} style={{ marginBottom: 8 }}>Abnormal Labs</Typography.Title>
                            {viewingEncounter.isSigned && abnormalLabsSnapshotData ? (
                              <div style={{
                                border: '2px solid #722ed1',
                                borderRadius: '6px',
                                padding: '12px',
                                backgroundColor: '#f9f0ff'
                              }}>
                                {formatAbnormalLabsSnapshotForIDT(abnormalLabsSnapshotData.abnormalLabs)}
                              </div>
                            ) : idtAbnormalLabs && idtAbnormalLabs.length > 0 ? (
                              <div style={{
                                border: '1px solid #d9d9d9',
                                borderRadius: '6px',
                                padding: '12px',
                                backgroundColor: '#fafafa'
                              }}>
                                <AbnormalLabsDisplay
                                  abnormalLabsData={idtAbnormalLabs}
                                  isEncounterSigned={viewingEncounter.isSigned || false}
                                  allLabEntries={idtAllLabEntries}
                                />
                              </div>
                            ) : (
                              <div style={{
                                border: '1px solid #d9d9d9',
                                borderRadius: '6px',
                                padding: '12px',
                                backgroundColor: '#fafafa',
                                color: '#8c8c8c',
                                fontStyle: 'italic'
                              }}>
                                {viewingEncounter.isSigned ? 'No abnormal lab values in snapshot' : 'No recent abnormal lab values'}
                              </div>
                            )}
                          </div>

                          <Descriptions bordered size="small" column={1} style={{ marginBottom: 16 }}>
                            <Descriptions.Item label="Active Medications">
                              {viewingEncounter.isSigned && medicationsSnapshotData
                                ? formatMedicationsForIDT(medicationsSnapshotData.medications, true)
                                : formatMedicationsForIDT(idtMedications)}
                            </Descriptions.Item>
                            <Descriptions.Item label="Allergies">
                              {idtAllergies.length > 0 ? idtAllergies.map(a => a.allergen).join(', ') : 'None Recorded'}
                            </Descriptions.Item>
                            <Descriptions.Item label="Upcoming Diagnostics">
                                {idtOrders.length > 0 ? idtOrders.map(o => `${getOrderDisplayName(o)} (${o.status || 'Status Unknown'})`).join('; ') : 'None Recorded'}
                              </Descriptions.Item>
                            </Descriptions>
                            <hr style={{ margin: '16px 0' }} />

                            {/* Section 3: Progress */}
                          <Typography.Title level={5}>Progress</Typography.Title>

                          {/* Current SMART Goals Display */}
                          <div style={{ marginBottom: '16px' }}>
                            <Typography.Text strong style={{ fontSize: '14px', color: '#1890ff' }}>
                              Current SMART Goals:
                            </Typography.Text>
                            <div style={{
                              marginTop: '8px',
                              padding: '12px',
                              backgroundColor: '#fafafa',
                              border: '1px solid #d9d9d9',
                              borderRadius: '6px'
                            }}>
                              {formatSmartGoalsForIDT(idtSmartGoals, handleSmartGoalClick)}
                            </div>
                          </div>

                          {viewingEncounter.idtSmartGoalsProgress && <> <Typography.Text strong>SMART Goals Progress & Barriers:</Typography.Text> <div dangerouslySetInnerHTML={{ __html: viewingEncounter.idtSmartGoalsProgress }} /> </>}
                          {(viewingEncounter.idtNutritionStatusClassification || viewingEncounter.idtNutritionStatus) && (
                            <>
                              <Typography.Text strong>Nutrition Status:</Typography.Text>
                              {viewingEncounter.idtNutritionStatusClassification && (
                                <div style={{ marginTop: '4px', marginBottom: '8px' }}>
                                  {formatNutritionStatusClassification(viewingEncounter.idtNutritionStatusClassification)}
                                </div>
                              )}
                              {viewingEncounter.idtNutritionStatus && (
                                <div dangerouslySetInnerHTML={{ __html: viewingEncounter.idtNutritionStatus }} />
                              )}
                            </>
                          )}
                          {(viewingEncounter.idtPhysicalActivityStatusClassification || viewingEncounter.idtPhysicalActivityStatus) && (
                            <>
                              <Typography.Text strong>Physical Activity Status:</Typography.Text>
                              {viewingEncounter.idtPhysicalActivityStatusClassification && (
                                <div style={{ marginTop: '4px', marginBottom: '8px' }}>
                                  {formatPhysicalActivityStatusClassification(viewingEncounter.idtPhysicalActivityStatusClassification)}
                                </div>
                              )}
                              {viewingEncounter.idtPhysicalActivityStatus && (
                                <div dangerouslySetInnerHTML={{ __html: viewingEncounter.idtPhysicalActivityStatus }} />
                              )}
                            </>
                          )}
                          {(viewingEncounter.idtRecoveryStatusClassification || viewingEncounter.idtRecoveryStatus) && (
                            <>
                              <Typography.Text strong>Recovery Status:</Typography.Text>
                              {viewingEncounter.idtRecoveryStatusClassification && (
                                <div style={{ marginTop: '4px', marginBottom: '8px' }}>
                                  {formatRecoveryStatusClassification(viewingEncounter.idtRecoveryStatusClassification)}
                                </div>
                              )}
                              {viewingEncounter.idtRecoveryStatus && (
                                <div dangerouslySetInnerHTML={{ __html: viewingEncounter.idtRecoveryStatus }} />
                              )}
                            </>
                          )}
                          {(viewingEncounter.idtEmotionStatusClassification || viewingEncounter.idtEmotionStatus) && (
                            <>
                              <Typography.Text strong>Emotion Status:</Typography.Text>
                              {viewingEncounter.idtEmotionStatusClassification && (
                                <div style={{ marginTop: '4px', marginBottom: '8px' }}>
                                  {formatEmotionStatusClassification(viewingEncounter.idtEmotionStatusClassification)}
                                </div>
                              )}
                              {viewingEncounter.idtEmotionStatus && (
                                <div dangerouslySetInnerHTML={{ __html: viewingEncounter.idtEmotionStatus }} />
                              )}
                            </>
                          )}
                          {(viewingEncounter.idtEnvironmentalStatusClassification || viewingEncounter.idtEnvironmentalStatus) && (
                            <>
                              <Typography.Text strong>Environmental Status:</Typography.Text>
                              {viewingEncounter.idtEnvironmentalStatusClassification && (
                                <div style={{ marginTop: '4px', marginBottom: '8px' }}>
                                  {formatEnvironmentalStatusClassification(viewingEncounter.idtEnvironmentalStatusClassification)}
                                </div>
                              )}
                              {viewingEncounter.idtEnvironmentalStatus && (
                                <div dangerouslySetInnerHTML={{ __html: viewingEncounter.idtEnvironmentalStatus }} />
                              )}
                            </>
                          )}
                          <hr style={{ margin: '16px 0' }} />

                          {/* Section 4: Recommendations */}
                          <Typography.Title level={5}>Recommendations</Typography.Title>
                          {viewingEncounter.idtPharmacyRecommendations && (
                            <>
                              <Typography.Text strong>Pharmacy Recommendations:</Typography.Text>
                              <RichTextDisplay content={viewingEncounter.idtPharmacyRecommendations} style={{ marginTop: 8 }} />
                            </>
                          )}
                          {viewingEncounter.idtMdRecommendations && (
                            <>
                              <Typography.Text strong>MD Recommendations:</Typography.Text>
                              <Space style={{ marginBottom: 8, marginTop: 8 }}>
                                <Button
                                  type="default"
                                  icon={<SolutionOutlined />}
                                  onClick={showProblemListModal}
                                >
                                  Manage Problem List
                                </Button>
                                <Button
                                  type="default"
                                  icon={<MedicineBoxOutlined />}
                                  onClick={showMedicationModal}
                                >
                                  Manage Medications
                                </Button>
                                <Button
                                  type="default"
                                  icon={<FileTextOutlined />}
                                  onClick={showOrderModal}
                                >
                                  Manage Orders
                                </Button>
                              </Space>
                              <RichTextDisplay content={viewingEncounter.idtMdRecommendations} style={{ marginTop: 8 }} />
                            </>
                          )}
                          {viewingEncounter.idtAdditionalNotes && (
                            <>
                              <Typography.Text strong>Additional Notes:</Typography.Text>
                              <RichTextDisplay content={viewingEncounter.idtAdditionalNotes} style={{ marginTop: 8 }} />
                            </>
                          )}
                        </>
                      ) : (
                        <Alert message="Loading IDT data..." type="info" />
                      )}
                    </Spin>
                  )}
                </Card>



                {/* Display Signing Information - Apply style conditionally */}
                {viewingEncounter.isSigned && (
                  <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid #f0f0f0', textDecoration: viewingEncounter.isStricken ? 'line-through' : 'none' }}>
                    <Typography.Text strong>Signed By:</Typography.Text> {viewingEncounter.signedBy?.name || 'Unknown User'} ({viewingEncounter.signedBy?.role || 'N/A'})
                    <br />
                    <Typography.Text strong>Signed At:</Typography.Text> {viewingEncounter.signedAt ? new Date(viewingEncounter.signedAt).toLocaleString() : 'N/A'}
                  </div>
                )}

                {/* Display Addendums - Apply style conditionally */}
                {viewingEncounter.addendums?.length > 0 && (
                  <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid #f0f0f0', textDecoration: viewingEncounter.isStricken ? 'line-through' : 'none' }}>
                    <Typography.Title level={5}>Addendums:</Typography.Title>
                    {viewingEncounter.addendums.map((addendum, index) => (
                      <div key={index} style={{ marginBottom: 12, paddingBottom: 12, borderBottom: '1px solid #f0f0f0' }}>
                        <Typography.Text strong>{addendum.createdBy?.name || 'Unknown User'} on {new Date(addendum.createdAt).toLocaleString()}:</Typography.Text>
                        <div dangerouslySetInnerHTML={{ __html: addendum.content }} />
                      </div>
                    ))}
                  </div>
                )}
              </div> {/* Close the main content wrapper div */}

              {/* Buttons remain outside the styled div */}
              {/* Add Addendum Button (within view modal for signed, non-stricken notes) */}
              {viewingEncounter.isSigned && !viewingEncounter.isStricken && (
                 <Button
                   type="primary"
                   style={{ marginTop: 16 }}
                   onClick={() => showAddendumModal(viewingEncounter)} // Pass the current encounter to the addendum modal
                 >
                   Add Addendum
                 </Button>
              )}

              {/* Strike Out Button (within view modal for signed, not-stricken notes) */}
              {viewingEncounter.isSigned && !viewingEncounter.isStricken && (user?.role === 'Admin' || user?._id === viewingEncounter.signedBy?._id) && (
                 <Button
                   danger // Use danger style for strike out
                   style={{ marginLeft: 8, marginTop: 16 }} // Add margin
                   onClick={() => showStrikeModal(viewingEncounter._id)}
                 >
                   Strike Out
                 </Button>
              )}
            </> /* Close Fragment */
          )}
         </Spin>
       </Modal>

      {/* Strike Out Modal */}
      <Modal
         title="Strike Out Encounter" // Add a title for clarity
         visible={isStrikeModalVisible} // Add the missing visible prop
         onCancel={handleStrikeCancel}
         footer={null}
         destroyOnClose
       >
         <Spin spinning={strikeSubmitLoading}>
           <Form form={strikeForm} layout="vertical" onFinish={handleStrikeOutSubmit}>
             <Form.Item
               name="strikeReason"
               label="Reason"
               rules={[{ required: true, message: 'Please enter the reason for striking out this encounter!' }]}
             >
               <Input.TextArea rows={4} />
             </Form.Item>
             <Form.Item>
               <Button type="primary" danger htmlType="submit" loading={strikeSubmitLoading}>
                 Confirm Strike Out
               </Button>
               <Button style={{ marginLeft: 8 }} onClick={handleStrikeCancel}>
                 Cancel
               </Button>
             </Form.Item>
           </Form>
         </Spin>
       </Modal>

      {/* Addendum Modal */}
      <Modal
        title={`Add Addendum to Encounter ${addendumEncounter?.encounterDate ? new Date(addendumEncounter.encounterDate).toLocaleString() : ''}`}
        visible={isAddendumModalVisible}
        onCancel={handleAddendumCancel}
        footer={null}
        destroyOnClose
        width={800}
      >
        <Spin spinning={addendumSubmitLoading}>
          {/* Use handleAddendumTextSubmit for this form's onFinish */}
          <Form form={addendumForm} layout="vertical" onFinish={handleAddendumTextSubmit}>
            <Form.Item
              name="addendumContent"
              label="Addendum Content"
              rules={[{ required: true, message: 'Please enter addendum content!' }]}
            >
              <RichTextEditor />
            </Form.Item>
            {/* Removed Password Field from this modal */}
            <Form.Item>
              <Button type="primary" htmlType="submit" loading={addendumSubmitLoading}>
                Save
              </Button>
              <Button style={{ marginLeft: 8 }} onClick={handleAddendumCancel}>
                Cancel
              </Button>
            </Form.Item>
          </Form>
        </Spin>
      </Modal>

      {/* Addendum Password Confirmation Modal (New) */}
      <Modal
        title="Confirm Addendum Password"
        visible={isAddendumPasswordModalVisible}
        onCancel={() => {
          setIsAddendumPasswordModalVisible(false);
          setTempAddendumContent(''); // Clear temp content on cancel
          setAddendumPasswordError(''); // Clear error on cancel
        }}
        footer={null}
        destroyOnClose
        maskClosable={false}
      >
        <Spin spinning={addendumSubmitLoading}>
          <Form form={addendumPasswordForm} layout="vertical" onFinish={handleConfirmAddendumPassword}>
            {/* Conditionally display error Alert */}
            {addendumPasswordError && (
              <Alert message={addendumPasswordError} type="error" showIcon style={{ marginBottom: 16 }} />
            )}
            <Form.Item
              name="password"
              label="Please enter your password to confirm addendum"
              rules={[{ required: true, message: 'Password is required!' }]}
            >
              <Input.Password />
            </Form.Item>
            <Form.Item>
              <Space>
                <Button type="primary" htmlType="submit" loading={addendumSubmitLoading}>
                  Confirm Add Addendum
                </Button>
                <Button onClick={() => {
                  setIsAddendumPasswordModalVisible(false);
                  setTempAddendumContent(''); // Clear temp content on cancel
                  setAddendumPasswordError(''); // Clear error on cancel
                }} disabled={addendumSubmitLoading}>
                  Cancel
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Spin>
      </Modal>

      {/* Encounter List Table */}
      <Spin spinning={loading && !addModalVisible && !editModalVisible && !isAddendumModalVisible}> {/* Check all modals */}
        <Table
          className="no-wrap-table-header" // Add this class name
          columns={columns}
          dataSource={encounters}
          rowKey="_id"
          pagination={{ pageSize: 5 }} // Smaller page size for embedded list
          scroll={{ x: 'max-content' }} // Allow horizontal scrolling
        />
      </Spin>

      {/* Intake Form Modal */}
      <Modal
        title="Patient Intake Form"
        visible={isIntakeModalVisible}
        onCancel={handleIntakeCancel}
        footer={null} // Assuming PatientIntakeForm has its own controls
        destroyOnClose
        width={800} // Adjust width as needed
      >
        {/* Pass patientId and a success handler to close modal */}
        <PatientIntakeForm patientId={patientId} onSuccess={handleIntakeSuccess} />
      </Modal>

      {/* Update Vitals Modal */}
      <UpdateVitalsModal
        visible={isUpdateVitalsModalVisible}
        onCancel={() => setIsUpdateVitalsModalVisible(false)}
        // Always pre-populate with the latest fetched vitals (prioritizes intake)
        initialVitals={latestVitalsData?.vitals || {}}
        onSave={(newVitals) => {
          // Store the updated vitals in the state variable
          console.log("UpdateVitalsModal onSave: Staging new vitals:", newVitals);
          setStagedVitals(newVitals);

          // Update the display component immediately
          setLatestVitalsData({
            source: 'Current Entry', // Indicate these are unsaved, current values
            date: new Date(), // Use current time for display timestamp
            vitals: cleanVitalsObject(newVitals)
          });
          // Removed the attempt to update the main form state here

          setIsUpdateVitalsModalVisible(false);
          message.info('Vitals staged. Save encounter to persist changes.'); // Update message
         }}
       />

      {/* Comprehensive Physical Exam Modal (New) - Placed outside Add/Edit Modals for clarity */}
      <Modal
        title="Comprehensive Physical Exam Input"
        visible={isFullExamModalVisible}
        onCancel={handleFullExamCancel}
        footer={null} // Footer handled by the form component
        destroyOnClose
        width={900} // Make wider for the detailed form
        maskClosable={false} // Prevent closing by clicking outside
      >
        <Spin spinning={fullExamSubmitLoading}>
          {/* Render the form only when the modal is visible */}
          {isFullExamModalVisible && (
            <StructuredPhysicalExamFormFull
              // Pass necessary props
              patientId={patientId}
              encounterId={editingEncounter?._id || null} // Pass current encounter ID if editing, else null
              onSave={handleSaveFullExam} // Use the new save handler
              onCancel={handleFullExamCancel}
              // TODO: Pass initialValues if pre-filling is implemented later
            />
          )}
         </Spin>
       </Modal>

      {/* Password Confirmation Modal (for Signing) */}
      <Modal
        title="Confirm Sign Encounter"
        visible={isPasswordModalVisible}
        onCancel={() => setIsPasswordModalVisible(false)}
        footer={null} // Footer handled by Form buttons
        destroyOnClose
        maskClosable={false} // Prevent closing by clicking outside
      >
        <Spin spinning={passwordModalLoading}>
          <Form form={passwordForm} layout="vertical" onFinish={handlePasswordConfirmSubmit}>
            <Form.Item
              name="password"
              label="Please enter your password to confirm signing"
              rules={[{ required: true, message: 'Password is required!' }]}
              validateStatus={passwordError ? 'error' : ''}
              help={passwordError}
            >
              <Input.Password />
            </Form.Item>
            <Form.Item>
              <Space>
                <Button type="primary" htmlType="submit" loading={passwordModalLoading}>
                  Confirm Sign
                </Button>
                <Button onClick={() => setIsPasswordModalVisible(false)} disabled={passwordModalLoading}>
                  Cancel
                </Button>
              </Space>
            </Form.Item>
          </Form>
         </Spin>
       </Modal>

      {/* Problem List Management Modal */}
      <ProblemListManagementModal
        visible={isProblemListModalVisible}
        onCancel={handleProblemListCancel}
        patientId={patientId}
        onProblemsToggle={handleProblemsToggle}
        currentAssessmentText={
          addModalVisible
            ? (addForm.getFieldValue('encounterType') === 'IDT Note'
                ? addForm.getFieldValue('idtMdRecommendations') || ''
                : addForm.getFieldValue('assessment') || '')
            : editModalVisible
              ? (editForm.getFieldValue('encounterType') === 'IDT Note'
                  ? editForm.getFieldValue('idtMdRecommendations') || ''
                  : editForm.getFieldValue('assessment') || '')
              : ''
        }
      />

      {/* Medication Management Modal */}
      <MedicationManagementModal
        visible={isMedicationModalVisible}
        onCancel={handleMedicationModalCancel}
        patientId={patientId}
        onMedicationsUpdate={handleMedicationsUpdate}
      />

      {/* Care Team Management Modal */}
      <CareTeamManagementModal
        visible={isCareTeamModalVisible}
        onCancel={handleCareTeamModalCancel}
        patientId={patientId}
        onCareTeamUpdate={handleCareTeamUpdate}
      />

      {/* Order Management Modal */}
      <OrderManagementModal
        visible={isOrderModalVisible}
        onCancel={handleOrderModalCancel}
        patientId={patientId}
        onOrdersUpdate={handleOrdersUpdate}
      />

      {/* Flowsheet Modal */}
      <Modal
        title="Patient Flowsheet"
        open={isFlowsheetModalVisible}
        onCancel={handleFlowsheetModalCancel}
        footer={null}
        width="95vw"
        style={{ top: 20 }}
        destroyOnClose={true}
      >
        <PatientFlowsheet patientId={patientId} />
      </Modal>

      {/* Documents Modal */}
      <Modal
        title="Patient Documents & Health Library"
        open={isDocumentsModalVisible}
        onCancel={handleDocumentsModalCancel}
        footer={null}
        width="90vw"
        style={{ top: 20 }}
        destroyOnClose={true}
      >
        <PatientDocumentsAndHealthLibrary patientId={patientId} />
      </Modal>

      {/* Encounters Modal */}
      <Modal
        title="Patient Encounters History"
        open={isEncountersModalVisible}
        onCancel={handleEncountersModalCancel}
        footer={null}
        width="95vw"
        style={{ top: 20 }}
        destroyOnClose={true}
        maskClosable={false} // Prevent closing when clicking outside
        closable={true} // Keep the X button for intentional closing
      >
        <div style={{ height: '70vh', overflow: 'auto' }}>
          <Table
            columns={[
              {
                title: 'Date',
                dataIndex: 'encounterDate',
                key: 'encounterDate',
                render: (date) => new Date(date).toLocaleDateString(),
                sorter: (a, b) => new Date(a.encounterDate) - new Date(b.encounterDate),
                defaultSortOrder: 'descend',
              },
              {
                title: 'Type',
                dataIndex: 'encounterType',
                key: 'encounterType',
                filters: [
                  { text: 'MD Encounter', value: 'MD Encounter' },
                  { text: 'IDT Note', value: 'IDT Note' },
                  { text: 'Nurse Encounter', value: 'Nurse Encounter' },
                  { text: 'Progress Note', value: 'Progress Note' },
                ],
                onFilter: (value, record) => record.encounterType === value,
              },
              {
                title: 'Provider',
                dataIndex: 'provider',
                key: 'provider',
                render: (provider) => typeof provider === 'object' ? provider?.name || 'N/A' : provider || 'N/A',
              },
              {
                title: 'Status',
                key: 'status',
                render: (_, record) => (
                  <span>
                    {record.isStricken ? (
                      <Tag color="red">Stricken</Tag>
                    ) : record.isSigned ? (
                      <Tag color="green">Signed</Tag>
                    ) : (
                      <Tag color="orange">Draft</Tag>
                    )}
                  </span>
                ),
                filters: [
                  { text: 'Draft', value: 'draft' },
                  { text: 'Signed', value: 'signed' },
                  { text: 'Stricken', value: 'stricken' },
                ],
                onFilter: (value, record) => {
                  if (value === 'draft') return !record.isSigned && !record.isStricken;
                  if (value === 'signed') return record.isSigned && !record.isStricken;
                  if (value === 'stricken') return record.isStricken;
                  return false;
                },
              },
              {
                title: 'Chief Complaint / Reason',
                key: 'reason',
                render: (_, record) => {
                  const reason = record.reasonForVisit || record.chiefComplaint || 'N/A';
                  return (
                    <div style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {reason}
                    </div>
                  );
                },
              },
              {
                title: 'Actions',
                key: 'actions',
                render: (_, record) => (
                  <Button
                    type="link"
                    size="small"
                    icon={<EyeOutlined />}
                    title="View encounter details"
                  >
                    View Details
                  </Button>
                ),
              },
            ]}
            dataSource={encounters}
            rowKey="_id"
            pagination={{ pageSize: 20, showSizeChanger: true, showQuickJumper: true }}
            size="small"
            scroll={{ x: 'max-content' }}
            expandable={{
              expandedRowRender: (record) => (
                <div style={{ margin: 0, padding: '16px', backgroundColor: '#fafafa', border: '1px solid #f0f0f0', borderRadius: '6px' }}>
                  <div style={{ marginBottom: '16px' }}>
                    <Text strong style={{ fontSize: '16px', color: '#1890ff' }}>
                      {record.encounterType} - {new Date(record.encounterDate).toLocaleString()}
                    </Text>
                    {record.isSigned && (
                      <Tag color="green" style={{ marginLeft: '8px' }}>
                        Signed by {typeof record.signedBy === 'object' ? record.signedBy?.name || 'Unknown' : record.signedBy || 'Unknown'} on {record.signedAt ? new Date(record.signedAt).toLocaleString() : 'Unknown date'}
                      </Tag>
                    )}
                    {record.isStricken && (
                      <Tag color="red" style={{ marginLeft: '8px' }}>
                        Stricken Out
                      </Tag>
                    )}
                  </div>

                  <Row gutter={[16, 16]}>
                    <Col span={12}>
                      <div>
                        <Text strong>Provider:</Text> {typeof record.provider === 'object' ? record.provider?.name || 'N/A' : record.provider || 'N/A'}
                      </div>
                      <div style={{ marginTop: '8px' }}>
                        <Text strong>Reason for Visit:</Text> {record.reasonForVisit || record.chiefComplaint || 'N/A'}
                      </div>
                    </Col>
                    <Col span={12}>
                      <div>
                        <Text strong>Created:</Text> {new Date(record.createdAt).toLocaleString()}
                      </div>
                      <div style={{ marginTop: '8px' }}>
                        <Text strong>Last Updated:</Text> {new Date(record.updatedAt).toLocaleString()}
                      </div>
                    </Col>
                  </Row>

                  {/* Vitals Section */}
                  {record.vitals && Object.keys(record.vitals).length > 0 && (
                    <div style={{ marginTop: '16px' }}>
                      <Text strong style={{ fontSize: '14px', color: '#1890ff' }}>Vital Signs:</Text>
                      <div style={{ marginTop: '8px' }}>
                        <ClickableVitalsDisplay
                          vitalsData={record.vitals}
                          allVitalsEntries={idtAllVitalsEntries}
                          isEncounterSigned={record.isSigned || false}
                        />
                      </div>
                    </div>
                  )}

                  {/* Notes Section */}
                  {record.notes && (
                    <div style={{ marginTop: '16px' }}>
                      <Text strong style={{ fontSize: '14px', color: '#1890ff' }}>Clinical Notes:</Text>
                      <Row gutter={[16, 8]} style={{ marginTop: '8px' }}>
                        {/* HPI & Review of Systems (MD Encounters) */}
                        {record.notes?.hpiRosCombined && (
                          <Col span={24}>
                            <div style={{ marginBottom: '8px' }}>
                              <Text strong>HPI & Review of Systems:</Text>
                              <div style={{ marginTop: '4px', padding: '8px', backgroundColor: '#fff', border: '1px solid #d9d9d9', borderRadius: '4px' }}>
                                <RichTextDisplay content={record.notes.hpiRosCombined} />
                              </div>
                            </div>
                          </Col>
                        )}

                        {/* History Updates from Intake (Nurse Encounters) */}
                        {record.notes?.intakeHistoryUpdates && (
                          <Col span={24}>
                            <div style={{ marginBottom: '8px' }}>
                              <Text strong>History Updates from Intake:</Text>
                              <div style={{ marginTop: '4px', padding: '8px', backgroundColor: '#fff', border: '1px solid #d9d9d9', borderRadius: '4px' }}>
                                <RichTextDisplay content={record.notes.intakeHistoryUpdates} />
                              </div>
                            </div>
                          </Col>
                        )}

                        {/* Standard SOAP Notes */}
                        {record.notes?.subjective && (
                          <Col span={24}>
                            <div style={{ marginBottom: '8px' }}>
                              <Text strong>Subjective:</Text>
                              <div style={{ marginTop: '4px', padding: '8px', backgroundColor: '#fff', border: '1px solid #d9d9d9', borderRadius: '4px' }}>
                                <RichTextDisplay content={record.notes.subjective} />
                              </div>
                            </div>
                          </Col>
                        )}
                        {record.notes?.objective && (
                          <Col span={24}>
                            <div style={{ marginBottom: '8px' }}>
                              <Text strong>Objective:</Text>
                              <div style={{ marginTop: '4px', padding: '8px', backgroundColor: '#fff', border: '1px solid #d9d9d9', borderRadius: '4px' }}>
                                <RichTextDisplay content={record.notes.objective} />
                              </div>
                            </div>
                          </Col>
                        )}
                        {record.notes?.assessment && (
                          <Col span={24}>
                            <div style={{ marginBottom: '8px' }}>
                              <Text strong>Assessment:</Text>
                              <div style={{ marginTop: '4px', padding: '8px', backgroundColor: '#fff', border: '1px solid #d9d9d9', borderRadius: '4px' }}>
                                <RichTextDisplay content={record.notes.assessment} />
                              </div>
                            </div>
                          </Col>
                        )}
                        {record.notes?.plan && (
                          <Col span={24}>
                            <div style={{ marginBottom: '8px' }}>
                              <Text strong>Plan:</Text>
                              <div style={{ marginTop: '4px', padding: '8px', backgroundColor: '#fff', border: '1px solid #d9d9d9', borderRadius: '4px' }}>
                                <RichTextDisplay content={record.notes.plan} />
                              </div>
                            </div>
                          </Col>
                        )}
                      </Row>
                    </div>
                  )}

                  {/* SBAR Notes Section (for Nurse SBAR Summary) */}
                  {record.encounterType === 'Nurse SBAR Summary' && (record.sbarSituation || record.sbarAssessmentOutcome || record.sbarPcpFeedback || record.sbarComment) && (
                    <div style={{ marginTop: '16px' }}>
                      <Text strong style={{ fontSize: '14px', color: '#1890ff' }}>SBAR Summary:</Text>
                      <Row gutter={[16, 8]} style={{ marginTop: '8px' }}>
                        {record.sbarSituation && (
                          <Col span={24}>
                            <div style={{ marginBottom: '8px' }}>
                              <Text strong>Situation:</Text>
                              <div style={{ marginTop: '4px', padding: '8px', backgroundColor: '#fff', border: '1px solid #d9d9d9', borderRadius: '4px' }}>
                                <RichTextDisplay content={record.sbarSituation} />
                              </div>
                            </div>
                          </Col>
                        )}
                        {record.sbarAssessmentOutcome && (
                          <Col span={24}>
                            <div style={{ marginBottom: '8px' }}>
                              <Text strong>Assessment & Outcome:</Text>
                              <div style={{ marginTop: '4px', padding: '8px', backgroundColor: '#fff', border: '1px solid #d9d9d9', borderRadius: '4px' }}>
                                <RichTextDisplay content={record.sbarAssessmentOutcome} />
                              </div>
                            </div>
                          </Col>
                        )}
                        {record.sbarPcpFeedback && (
                          <Col span={24}>
                            <div style={{ marginBottom: '8px' }}>
                              <Text strong>PCP Feedback:</Text>
                              <div style={{ marginTop: '4px', padding: '8px', backgroundColor: '#fff', border: '1px solid #d9d9d9', borderRadius: '4px' }}>
                                <RichTextDisplay content={record.sbarPcpFeedback} />
                              </div>
                            </div>
                          </Col>
                        )}
                        {record.sbarComment && (
                          <Col span={24}>
                            <div style={{ marginBottom: '8px' }}>
                              <Text strong>Comment:</Text>
                              <div style={{ marginTop: '4px', padding: '8px', backgroundColor: '#fff', border: '1px solid #d9d9d9', borderRadius: '4px' }}>
                                <RichTextDisplay content={record.sbarComment} />
                              </div>
                            </div>
                          </Col>
                        )}
                      </Row>
                    </div>
                  )}

                  {/* IDT Notes Section */}
                  {record.encounterType === 'IDT Note' && (
                    <div style={{ marginTop: '16px' }}>
                      <Text strong style={{ fontSize: '14px', color: '#1890ff' }}>IDT Note Details:</Text>
                      <Row gutter={[16, 8]} style={{ marginTop: '8px' }}>
                        {record.idtActiveConcerns && (
                          <Col span={24}>
                            <div style={{ marginBottom: '8px' }}>
                              <Text strong>Active Concerns:</Text>
                              <div style={{ marginTop: '4px', padding: '8px', backgroundColor: '#fff', border: '1px solid #d9d9d9', borderRadius: '4px' }}>
                                <RichTextDisplay content={record.idtActiveConcerns} showEmpty={false} />
                              </div>
                            </div>
                          </Col>
                        )}
                        {record.idtCurrentStatus && (
                          <Col span={24}>
                            <div style={{ marginBottom: '8px' }}>
                              <Text strong>Current Status:</Text>
                              <div style={{ marginTop: '4px', padding: '8px', backgroundColor: '#fff', border: '1px solid #d9d9d9', borderRadius: '4px' }}>
                                <RichTextDisplay content={record.idtCurrentStatus} showEmpty={false} />
                              </div>
                            </div>
                          </Col>
                        )}
                        {record.idtActiveNotes && (
                          <Col span={24}>
                            <div style={{ marginBottom: '8px' }}>
                              <Text strong>Active Notes:</Text>
                              <div style={{ marginTop: '4px', padding: '8px', backgroundColor: '#fff', border: '1px solid #d9d9d9', borderRadius: '4px' }}>
                                <RichTextDisplay content={record.idtActiveNotes} showEmpty={false} />
                              </div>
                            </div>
                          </Col>
                        )}
                        {record.idtSmartGoalsProgress && (
                          <Col span={24}>
                            <div style={{ marginBottom: '8px' }}>
                              <Text strong>SMART Goals Progress:</Text>
                              <div style={{ marginTop: '4px', padding: '8px', backgroundColor: '#fff', border: '1px solid #d9d9d9', borderRadius: '4px' }}>
                                <RichTextDisplay content={record.idtSmartGoalsProgress} showEmpty={false} />
                              </div>
                            </div>
                          </Col>
                        )}
                        {(record.idtNutritionStatusClassification || record.idtNutritionStatus) && (
                          <Col span={24}>
                            <div style={{ marginBottom: '8px' }}>
                              <Text strong>Nutrition Status:</Text>
                              {record.idtNutritionStatusClassification && (
                                <div style={{ marginTop: '4px', marginBottom: '4px' }}>
                                  {formatNutritionStatusClassification(record.idtNutritionStatusClassification)}
                                </div>
                              )}
                              {record.idtNutritionStatus && (
                                <div style={{ marginTop: '4px', padding: '8px', backgroundColor: '#fff', border: '1px solid #d9d9d9', borderRadius: '4px' }}>
                                  <RichTextDisplay content={record.idtNutritionStatus} showEmpty={false} />
                                </div>
                              )}
                            </div>
                          </Col>
                        )}
                        {(record.idtPhysicalActivityStatusClassification || record.idtPhysicalActivityStatus) && (
                          <Col span={24}>
                            <div style={{ marginBottom: '8px' }}>
                              <Text strong>Physical Activity Status:</Text>
                              {record.idtPhysicalActivityStatusClassification && (
                                <div style={{ marginTop: '4px', marginBottom: '4px' }}>
                                  {formatPhysicalActivityStatusClassification(record.idtPhysicalActivityStatusClassification)}
                                </div>
                              )}
                              {record.idtPhysicalActivityStatus && (
                                <div style={{ marginTop: '4px', padding: '8px', backgroundColor: '#fff', border: '1px solid #d9d9d9', borderRadius: '4px' }}>
                                  <RichTextDisplay content={record.idtPhysicalActivityStatus} showEmpty={false} />
                                </div>
                              )}
                            </div>
                          </Col>
                        )}
                        {(record.idtRecoveryStatusClassification || record.idtRecoveryStatus) && (
                          <Col span={24}>
                            <div style={{ marginBottom: '8px' }}>
                              <Text strong>Recovery Status:</Text>
                              {record.idtRecoveryStatusClassification && (
                                <div style={{ marginTop: '4px', marginBottom: '4px' }}>
                                  {formatRecoveryStatusClassification(record.idtRecoveryStatusClassification)}
                                </div>
                              )}
                              {record.idtRecoveryStatus && (
                                <div style={{ marginTop: '4px', padding: '8px', backgroundColor: '#fff', border: '1px solid #d9d9d9', borderRadius: '4px' }}>
                                  <RichTextDisplay content={record.idtRecoveryStatus} showEmpty={false} />
                                </div>
                              )}
                            </div>
                          </Col>
                        )}
                        {(record.idtEmotionStatusClassification || record.idtEmotionStatus) && (
                          <Col span={24}>
                            <div style={{ marginBottom: '8px' }}>
                              <Text strong>Emotion Status:</Text>
                              {record.idtEmotionStatusClassification && (
                                <div style={{ marginTop: '4px', marginBottom: '4px' }}>
                                  {formatEmotionStatusClassification(record.idtEmotionStatusClassification)}
                                </div>
                              )}
                              {record.idtEmotionStatus && (
                                <div style={{ marginTop: '4px', padding: '8px', backgroundColor: '#fff', border: '1px solid #d9d9d9', borderRadius: '4px' }}>
                                  <RichTextDisplay content={record.idtEmotionStatus} showEmpty={false} />
                                </div>
                              )}
                            </div>
                          </Col>
                        )}
                        {(record.idtEnvironmentalStatusClassification || record.idtEnvironmentalStatus) && (
                          <Col span={24}>
                            <div style={{ marginBottom: '8px' }}>
                              <Text strong>Environmental Status:</Text>
                              {record.idtEnvironmentalStatusClassification && (
                                <div style={{ marginTop: '4px', marginBottom: '4px' }}>
                                  {formatEnvironmentalStatusClassification(record.idtEnvironmentalStatusClassification)}
                                </div>
                              )}
                              {record.idtEnvironmentalStatus && (
                                <div style={{ marginTop: '4px', padding: '8px', backgroundColor: '#fff', border: '1px solid #d9d9d9', borderRadius: '4px' }}>
                                  <RichTextDisplay content={record.idtEnvironmentalStatus} showEmpty={false} />
                                </div>
                              )}
                            </div>
                          </Col>
                        )}
                        {record.idtPharmacyRecommendations && (
                          <Col span={24}>
                            <div style={{ marginBottom: '8px' }}>
                              <Text strong>Pharmacy Recommendations:</Text>
                              <div style={{ marginTop: '4px', padding: '8px', backgroundColor: '#fff', border: '1px solid #d9d9d9', borderRadius: '4px' }}>
                                <RichTextDisplay content={record.idtPharmacyRecommendations} showEmpty={false} />
                              </div>
                            </div>
                          </Col>
                        )}
                        {record.idtMdRecommendations && (
                          <Col span={24}>
                            <div style={{ marginBottom: '8px' }}>
                              <Text strong>MD Recommendations:</Text>
                              <div style={{ marginTop: '4px', padding: '8px', backgroundColor: '#fff', border: '1px solid #d9d9d9', borderRadius: '4px' }}>
                                <RichTextDisplay content={record.idtMdRecommendations} showEmpty={false} />
                              </div>
                            </div>
                          </Col>
                        )}
                        {record.idtAdditionalNotes && (
                          <Col span={24}>
                            <div style={{ marginBottom: '8px' }}>
                              <Text strong>Additional Notes:</Text>
                              <div style={{ marginTop: '4px', padding: '8px', backgroundColor: '#fff', border: '1px solid #d9d9d9', borderRadius: '4px' }}>
                                <RichTextDisplay content={record.idtAdditionalNotes} showEmpty={false} />
                              </div>
                            </div>
                          </Col>
                        )}
                      </Row>
                    </div>
                  )}

                  {/* Addenda Section */}
                  {record.addenda && record.addenda.length > 0 && (
                    <div style={{ marginTop: '16px' }}>
                      <Text strong style={{ fontSize: '14px', color: '#1890ff' }}>Addenda:</Text>
                      {record.addenda.map((addendum, index) => (
                        <div key={index} style={{ marginTop: '8px', padding: '8px', backgroundColor: '#fff7e6', border: '1px solid #ffd591', borderRadius: '4px' }}>
                          <div style={{ marginBottom: '4px' }}>
                            <Text strong>Added by:</Text> {typeof addendum.addedBy === 'object' ? addendum.addedBy?.name || 'Unknown' : addendum.addedBy || 'Unknown'} on {new Date(addendum.addedAt).toLocaleString()}
                          </div>
                          <div dangerouslySetInnerHTML={{ __html: addendum.content }} />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ),
              expandIcon: ({ expanded, onExpand, record }) => (
                <Button
                  type="text"
                  size="small"
                  icon={expanded ? <UpOutlined /> : <DownOutlined />}
                  onClick={(e) => onExpand(record, e)}
                  title={expanded ? "Collapse details" : "Expand to view details"}
                />
              ),
            }}
          />
        </div>
      </Modal>

      {/* AI Scribe Modal */}
      <AIScribeModal
        visible={isAIScribeModalVisible}
        onCancel={handleAIScribeCancel}
        onApprove={handleAIScribeApprove}
        loading={aiScribeLoading}
      />

      {/* SMART Goals Details Modal */}
      {selectedSmartGoal && (
        <EnhancedSmartGoalDetailsModal
          visible={isSmartGoalDetailsModalVisible}
          onCancel={() => {
            setIsSmartGoalDetailsModalVisible(false);
            setSelectedSmartGoal(null);
          }}
          goal={selectedSmartGoal}
          initialEditing={false}
          readOnly={true}
          onUpdate={handleSmartGoalUpdate}
          onDelete={null} // Disable delete functionality in IDT context
        />
      )}

      {/* IDT Conflict Resolution Modal */}
      <IdtConflictResolutionModal
        visible={conflictModalVisible}
        onCancel={handleConflictCancel}
        onResolve={handleConflictResolution}
        conflictData={conflictData}
        loading={false}
      />

     </Card>
   );
 };

export default PatientEncounters;
